import React, { useEffect, useState, useRef } from "react";
import { parseReaderFromEmail, callClaudeWithImage } from './anthropic';
import SuperAdminDashboard from './admin/SuperAdminDashboard';
import { functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { getIdTokenResult } from 'firebase/auth';
import Papa from "papaparse";
import MessagesTab from './tabs/MessagesTab';
import RoadmapTestingTab from './tabs/RoadmapTestingTab';
import DuplicateMerger from './DuplicateMerger';
import ReaderInsights from './ReaderInsights';
import OrdersManagement from './OrdersManagement';
// ProductSettings removed — now in Author Dashboard per-book
import BulkBookImport, { seedBookDatabase } from './BulkBookImport';
import BookImportTools from './BookImportTools';
import ReadingHistoryImport from './ReadingHistoryImport';
import ReaderResume from './ReaderResume';
import { updateArcStatus } from './utils/updateArcStatus';
import { normaliseStatus, getArcStatusBadgeClass, getArcStatusBgClass, getArcStatusLabel } from './constants/arcStatus';

import {
  Search,
  Plus,
  Minus,
  Edit2,
  Trash2,
  Download,
  Upload,
  LogOut,
  Mail,
  Star,
  BookOpen,
  Shield,
  Lock,
  Filter,
  CheckCircle,
  XCircle,
  ExternalLink,
  Gift,
  Link as LinkIcon,
  User,
  MessageCircle,
  Heart,
  Pin,
  Package,
  Settings,
  Send,
  Inbox,
  Sparkles,
  Tag,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  KeyRound,
  FlaskConical,
} from "lucide-react";

import { auth, db } from "./firebase";
import PublishingRoadmap from './PublishingChecklist_Product';
import { BOOK_TAXONOMY, cycleTagToggle, resolveBookTags } from './lib/bookTaxonomy';
import { findDuplicate, findAllDuplicateGroups, mergeBookRecords } from './lib/duplicateDetection';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Badge } from './components/ui/badge';
import { Card, CardContent } from './components/ui/card';

import {
  collection,
  collectionGroup,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  getDoc,
  getDocs,
  setDoc,
  doc,
  orderBy,
  limit,
  increment,
} from "firebase/firestore";

import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";

/* ----------------------------------------
   BOOTSTRAP SUPERADMIN — one-time setup
---------------------------------------- */
function BootstrapSuperadmin() {
  const [status, setStatus] = useState('checking'); // checking | show | done | error
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) { setStatus('show'); return; }
        const token = await getIdTokenResult(user);
        const roles = Array.isArray(token.claims.roles) ? token.claims.roles : (token.claims.role ? [token.claims.role] : []);
        if (roles.includes('superadmin')) {
          setStatus('done');
        } else {
          setStatus('show');
        }
      } catch {
        setStatus('show');
      }
    })();
  }, []);

  async function handleBootstrap() {
    setStatus('loading');
    setError('');
    try {
      const fn = httpsCallable(functions, 'bootstrapSuperadmin');
      await fn();
      // Force token refresh to pick up new claims
      const user = auth.currentUser;
      if (user) await user.getIdToken(true);
      setStatus('done');
    } catch (err) {
      if (err.code === 'functions/already-exists') {
        setStatus('done');
      } else {
        setError(err.message || 'Failed to bootstrap');
        setStatus('error');
      }
    }
  }

  if (status === 'checking') return null;
  if (status === 'done') return null;

  return (
    <Card className="mb-6 border-teal-200 bg-teal-50/50">
      <CardContent className="flex items-center justify-between p-4">
        <div>
          <p className="font-medium text-teal-800">Set Up Superadmin</p>
          <p className="text-sm text-teal-700">
            One-time setup — grants your account full platform admin rights.
          </p>
          {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
        <Button
          className="bg-teal-600 hover:bg-teal-700 text-white"
          onClick={handleBootstrap}
          disabled={status === 'loading'}
        >
          {status === 'loading' ? 'Setting up...' : 'Activate Superadmin'}
        </Button>
      </CardContent>
    </Card>
  );
}

/* ----------------------------------------
   STAT CARD
---------------------------------------- */
function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className="rounded-2xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 shadow-sm">
      <CardContent className="flex flex-col gap-2 px-4 py-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${accent}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="text-2xl font-semibold text-slate-900">{value}</p>
      </CardContent>
    </Card>
  );
}

/* ----------------------------------------
   ROADMAP LICENCES — read-only superadmin view of the top-level `licenses`
   collection (Publishing Roadmap / Plot Planner / bundle trials + purchases).
   Lives in the CRM because the CRM is the admin-only surface; the author portal
   (multi-tenant) must never show it. Writes are Cloud-Functions-only.
---------------------------------------- */
function licenceTsToMillis(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return isNaN(d.getTime()) ? null : d.getTime();
}
function fmtLicenceDate(ts) {
  const ms = licenceTsToMillis(ts);
  if (ms == null) return "—";
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function licenceType(lic) {
  return lic.term === "trial" ? "trial" : "paid";
}
// "revoked" is distinct from "expired": a revoked licence carries revokedAt (set by
// adminRevokeLicence). A licence with active:false but no revokedAt reads as expired.
function licenceStatus(lic) {
  if (lic.revokedAt) return "revoked";
  if (lic.active === false) return "expired";
  const ms = licenceTsToMillis(lic.expiresAt);
  if (ms == null) return "active";        // non-expiring licence
  return ms < Date.now() ? "expired" : "active";
}
function licenceStatusBadgeClass(status) {
  if (status === "active") return "bg-green-100 text-green-800";
  if (status === "revoked") return "bg-red-100 text-red-800";
  return "bg-slate-100 text-slate-600";   // expired
}
function licenceStatusLabel(status) {
  return status === "active" ? "Active" : status === "revoked" ? "Revoked" : "Expired";
}
function truncateLicenceKey(key) {
  const k = String(key || "");
  if (!k) return "—";
  return k.length <= 8 ? k : `${k.slice(0, 8)}…`;
}
function msToDateInputValue(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
const LICENCE_SCOPE_LABEL = { roadmap: "Roadmap", planner: "Planner", bundle: "Bundle" };
const LICENCE_TERM_OPTIONS = [
  { value: "trial", label: "Trial" },
  { value: "1m", label: "1 Month" },
  { value: "6m", label: "6 Month" },
  { value: "12m", label: "12 Month" },
];

// Admin-only licence actions — Cloud Functions (Admin SDK) so no client write
// rules are needed. Both verify isSuperadmin() server-side before writing.
const adminUpdateLicenceFn = httpsCallable(functions, "adminUpdateLicence");
const adminRevokeLicenceFn = httpsCallable(functions, "adminRevokeLicence");
const adminUnrevokeLicenceFn = httpsCallable(functions, "adminUnrevokeLicence");
const adminDeleteLicenceFn = httpsCallable(functions, "adminDeleteLicence");
const adminCreateLicenceFn = httpsCallable(functions, "adminCreateLicence");

/* ---- Add licence modal (admin housekeeping — no email sent) ---- */
function AddLicenceModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [scope, setScope] = useState("roadmap");
  const [term, setTerm] = useState("1m");
  const [expiryDate, setExpiryDate] = useState(msToDateInputValue(Date.now() + 30 * 86400000));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const confirm = async () => {
    const em = email.trim().toLowerCase();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setErr("Enter a valid email address."); return; }
    if (!expiryDate) { setErr("Please choose an expiry date."); return; }
    const expiresAtMs = new Date(`${expiryDate}T23:59:59`).getTime();
    if (!Number.isFinite(expiresAtMs)) { setErr("That date isn't valid."); return; }
    setSaving(true); setErr("");
    try {
      await adminCreateLicenceFn({ email: em, scope, term, expiresAtMs });
      onClose();
    } catch (e) {
      setErr(e?.message || "Could not create the licence — please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
        <h3 className="mb-1 text-lg font-bold text-slate-900">Add licence</h3>
        <p className="mb-4 text-xs text-slate-500">Creates a licence directly. No email is sent to the holder.</p>

        <label className="mb-1 block text-xs font-semibold text-slate-600">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          className="mb-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        />

        <label className="mb-1 block text-xs font-semibold text-slate-600">Product</label>
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="mb-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        >
          <option value="roadmap">Roadmap</option>
          <option value="planner">Planner</option>
          <option value="bundle">Bundle</option>
        </select>

        <label className="mb-1 block text-xs font-semibold text-slate-600">Term</label>
        <select
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          className="mb-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        >
          {LICENCE_TERM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <label className="mb-1 block text-xs font-semibold text-slate-600">Expiry date</label>
        <input
          type="date"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          className="mb-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        />

        {err && <p className="mb-3 text-xs text-red-600">{err}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={confirm} disabled={saving}>
            {saving ? "Creating…" : "Create licence"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---- Extend / Upgrade modal ---- */
function ExtendLicenceModal({ licence, onClose }) {
  const currentExpiryMs = licenceTsToMillis(licence.expiresAt);
  const defaultExpiryMs = (currentExpiryMs ?? Date.now()) + 30 * 86400000;
  const [expiryDate, setExpiryDate] = useState(msToDateInputValue(defaultExpiryMs));
  const [term, setTerm] = useState(
    LICENCE_TERM_OPTIONS.some((o) => o.value === licence.term) ? licence.term : "1m"
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const confirm = async () => {
    if (!expiryDate) { setErr("Please choose an expiry date."); return; }
    // End of the chosen day, local time — "access until" includes the whole day.
    const expiresAtMs = new Date(`${expiryDate}T23:59:59`).getTime();
    if (!Number.isFinite(expiresAtMs)) { setErr("That date isn't valid."); return; }
    setSaving(true); setErr("");
    try {
      await adminUpdateLicenceFn({ docId: licence.id, expiresAtMs, term });
      onClose();
    } catch (e) {
      setErr(e?.message || "Could not update the licence — please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
        <h3 className="mb-1 text-lg font-bold text-slate-900">Extend / upgrade licence</h3>
        <p className="mb-4 text-xs text-slate-500 break-all">{licence.email || "—"}</p>

        <label className="mb-1 block text-xs font-semibold text-slate-600">New expiry date</label>
        <input
          type="date"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          className="mb-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        />

        <label className="mb-1 block text-xs font-semibold text-slate-600">Plan label</label>
        <select
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          className="mb-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
        >
          {LICENCE_TERM_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {err && <p className="mb-3 text-xs text-red-600">{err}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={confirm} disabled={saving}>
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---- Revoke confirmation ---- */
function RevokeLicenceConfirm({ licence, onClose }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const confirm = async () => {
    setSaving(true); setErr("");
    try {
      await adminRevokeLicenceFn({ docId: licence.id });
      onClose();
    } catch (e) {
      setErr(e?.message || "Could not revoke the licence — please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
        <h3 className="mb-2 text-lg font-bold text-slate-900">Revoke access</h3>
        <p className="mb-4 text-sm text-slate-600">
          Revoke access for <span className="font-medium text-slate-900 break-all">{licence.email || "this licence"}</span>? This cannot be undone.
        </p>
        {err && <p className="mb-3 text-xs text-red-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={confirm} disabled={saving}>
            {saving ? "Revoking…" : "Revoke"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---- Permanent delete confirmation (housekeeping — no email to holder) ---- */
function DeleteLicenceConfirm({ licence, onClose }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const confirm = async () => {
    setSaving(true); setErr("");
    try {
      await adminDeleteLicenceFn({ docId: licence.id });
      onClose();
    } catch (e) {
      setErr(e?.message || "Could not delete the licence — please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
        <h3 className="mb-2 text-lg font-bold text-slate-900">Permanently delete licence</h3>
        <p className="mb-4 text-sm text-slate-600">
          Permanently delete licence for <span className="font-medium text-slate-900 break-all">{licence.email || "this licence"}</span>? This removes the record entirely and cannot be undone.
        </p>
        {err && <p className="mb-3 text-xs text-red-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={confirm} disabled={saving}>
            {saving ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RoadmapLicencesPanel({ licences }) {
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [extendTarget, setExtendTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [unrevokingId, setUnrevokingId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const unrevoke = async (licence) => {
    setUnrevokingId(licence.id);
    try {
      await adminUnrevokeLicenceFn({ docId: licence.id });
    } catch (e) {
      console.error("Failed to unrevoke licence:", e?.message);
    }
    setUnrevokingId(null);
  };

  const rows = licences
    .map((l) => ({
      ...l,
      _type: licenceType(l),
      _status: licenceStatus(l),
      _created: licenceTsToMillis(l.createdAt) ?? licenceTsToMillis(l.purchasedAt) ?? 0,
    }))
    .sort((a, b) => b._created - a._created);

  const activeTrials = rows.filter((r) => r._type === "trial" && r._status === "active").length;
  const paidCount = rows.filter((r) => r._type === "paid").length;

  const filtered = rows.filter((r) => {
    if (typeFilter !== "all" && r._type !== typeFilter) return false;
    if (statusFilter !== "all" && r._status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-slate-800">{activeTrials}</span> active trial{activeTrials !== 1 ? "s" : ""}
          {" · "}
          <span className="font-semibold text-slate-800">{paidCount}</span> paid licence{paidCount !== 1 ? "s" : ""}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="h-4 w-4" /> Add Licence
          </Button>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700"
          >
            <option value="all">All types</option>
            <option value="trial">Trial</option>
            <option value="paid">Paid</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
          <KeyRound className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">
            {rows.length === 0
              ? "No licences yet. Trial and paid licences appear here as they are issued."
              : "No licences match your filters."}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left">
                  <th className="px-3 py-3 font-medium text-slate-600">Email</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Key</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Product</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Type</th>
                  <th className="px-3 py-3 font-medium text-slate-600">Status</th>
                  <th className="px-3 py-3 font-medium text-slate-600 hidden md:table-cell">Created</th>
                  <th className="px-3 py-3 font-medium text-slate-600 hidden md:table-cell">Expires</th>
                  <th className="px-3 py-3 font-medium text-slate-600 hidden lg:table-cell">Country</th>
                  <th className="px-3 py-3 font-medium text-slate-600 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-slate-50 align-top">
                    <td className="px-3 py-3 text-slate-700">{l.email || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-500" title={l.key || ""}>{truncateLicenceKey(l.key)}</td>
                    <td className="px-3 py-3 text-slate-600">{LICENCE_SCOPE_LABEL[l.scope] || "Roadmap"}</td>
                    <td className="px-3 py-3">
                      <Badge className={`${l._type === "trial" ? "bg-blue-100 text-blue-800" : "bg-teal-100 text-teal-800"} border-0`}>
                        {l._type === "trial" ? "Trial" : "Paid"}
                      </Badge>
                    </td>
                    <td className="px-3 py-3">
                      <Badge className={`${licenceStatusBadgeClass(l._status)} border-0`}>
                        {licenceStatusLabel(l._status)}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-slate-500 hidden md:table-cell whitespace-nowrap">{fmtLicenceDate(l.createdAt || l.purchasedAt)}</td>
                    <td className="px-3 py-3 text-slate-500 hidden md:table-cell whitespace-nowrap">{l.expiresAt ? fmtLicenceDate(l.expiresAt) : "Never"}</td>
                    <td className="px-3 py-3 text-slate-500 hidden lg:table-cell">{l.country || "—"}</td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-1.5 whitespace-nowrap">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs border-teal-200 text-teal-700 hover:bg-teal-50"
                          onClick={() => setExtendTarget(l)}
                        >
                          Extend
                        </Button>
                        {l.revokedAt ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs border-teal-200 text-teal-700 hover:bg-teal-50 disabled:opacity-40"
                            onClick={() => unrevoke(l)}
                            disabled={unrevokingId === l.id}
                          >
                            {unrevokingId === l.id ? "Restoring…" : "Unrevoke"}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => setRevokeTarget(l)}
                          >
                            Revoke
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0 border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteTarget(l)}
                          title="Permanently delete licence"
                          aria-label="Permanently delete licence"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {extendTarget && (
        <ExtendLicenceModal licence={extendTarget} onClose={() => setExtendTarget(null)} />
      )}
      {revokeTarget && (
        <RevokeLicenceConfirm licence={revokeTarget} onClose={() => setRevokeTarget(null)} />
      )}
      {deleteTarget && (
        <DeleteLicenceConfirm licence={deleteTarget} onClose={() => setDeleteTarget(null)} />
      )}
      {addOpen && (
        <AddLicenceModal onClose={() => setAddOpen(false)} />
      )}
    </div>
  );
}

/* ----------------------------------------
   SOURCE BADGE COMPONENT
---------------------------------------- */
function SourceBadge({ source }) {
  if (!source) return null;
  
  const badges = {
    'reader_portal': { label: 'Portal', color: 'bg-purple-100 text-purple-700' },
    'manual': { label: 'Manual', color: 'bg-slate-100 text-slate-700' }
  };
  
  const badgeInfo = badges[source] || { label: 'Manual', color: 'bg-slate-100 text-slate-700' };
  
  return (
    <Badge className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-medium border-0 ${badgeInfo.color}`}>
      <User className="h-2.5 w-2.5" />
      {badgeInfo.label}
    </Badge>
  );
}

/* ----------------------------------------
   EREADER EMAIL STATUS ICON
---------------------------------------- */
function EReaderEmailIcon({ emailCorrespondence, eReaderEmail }) {
  const ereaderEmail = eReaderEmail || '';
  const correspondenceEmail = (emailCorrespondence || '').toLowerCase();
  
  if (!ereaderEmail || ereaderEmail.trim() === '') {
    return (
      <span 
        className="inline-flex items-center justify-center rounded-full bg-red-100 p-1" 
        title="No eReader email - ask reader to provide"
      >
        <XCircle className="h-3 w-3 text-red-600" />
      </span>
    );
  }
  
  if (ereaderEmail.toLowerCase() === correspondenceEmail) {
    return (
      <span 
        className="inline-flex items-center justify-center rounded-full bg-yellow-100 p-1" 
        title="eReader email same as correspondence - verify this is correct"
      >
        <Mail className="h-3 w-3 text-yellow-600" />
      </span>
    );
  }
  
  return (
    <span 
      className="inline-flex items-center justify-center rounded-full bg-green-100 p-1" 
      title="Valid eReader email ✓"
    >
      <CheckCircle className="h-3 w-3 text-green-600" />
    </span>
  );
}

/* ----------------------------------------
   MAIN APP COMPONENT
---------------------------------------- */
function App() {

  /* AUTH */
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  // Admin route guard: the CRM is superadmin-only. We resolve the caller's role
  // claims before rendering anything, and redirect non-superadmins off the app.
  const [isSuperadminUser, setIsSuperadminUser] = useState(false);
  const [authRoles, setAuthRoles] = useState([]);
  const [claimsChecked, setClaimsChecked] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  /* UI */
  const [activeTab, setActiveTab] = useState("readers");
  const [viewingReader, setViewingReader] = useState(null); // NEW
const [showReaderDetailPanel, setShowReaderDetailPanel] = useState(false);
const [readerReviews, setReaderReviews] = useState([]);
const [readerReadingHistory, setReaderReadingHistory] = useState([]);
const [readerImportBatches, setReaderImportBatches] = useState([]);
const [showReaderHistoryImport, setShowReaderHistoryImport] = useState(false);
  const [showReaderDrawer, setShowReaderDrawer] = useState(false);
  const [isSavingReader, setIsSavingReader] = useState(false);

  /* Books UI (read-only platform view) */
  const [books, setBooks] = useState([]);
  const [platformAuthors, setPlatformAuthors] = useState({});
  const [booksSearch, setBooksSearch] = useState('');
  const [booksStatusFilter, setBooksStatusFilter] = useState('all');
  const [booksExpandedReview, setBooksExpandedReview] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);

  /* Legacy book form state (kept for any remaining references) */
  const [showBookForm, setShowBookForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [bookFormData, setBookFormData] = useState({
    title: "",
    series: "",
    releaseDate: "",
    arcAvailable: true,
    status: "upcoming",
    coverUrl: "",
    blurb: "",
    genres: [],
    contentWarnings: "",
    wordCount: "",
    pageCount: "",
    arcSendDate: "",
    goLiveDate: "",
    reviewReleaseDate: "",
    reviewsEnabled: true,
  });

  /* Book Database & Tagging (admin) */
  const [bookDbView, setBookDbView]             = useState('list'); // 'list' | 'search' | 'editor'
  const [bookDbSearch, setBookDbSearch]         = useState('');
  const [bookDbSearchResults, setBookDbSearchResults] = useState([]);
  const [bookDbSearching, setBookDbSearching]   = useState(false);
  const [bookDbList, setBookDbList]             = useState([]);
  const [bookDbFilter, setBookDbFilter]         = useState('');
  const [adminTagBookId, setAdminTagBookId]     = useState(null);
  const [adminTagBookData, setAdminTagBookData] = useState(null);
  const [adminTagForm, setAdminTagForm]         = useState({});
  const [adminTagSections, setAdminTagSections] = useState({});
  const [adminTagSubmissions, setAdminTagSubmissions] = useState([]);
  const [adminTagOverrides, setAdminTagOverrides] = useState({});
  const [adminTagSaving, setAdminTagSaving]     = useState(false);
  const [adminTagMsg, setAdminTagMsg]           = useState('');
  const [csvImportData, setCsvImportData]       = useState(null);
  const [csvImporting, setCsvImporting]         = useState(false);
  const [csvImportResult, setCsvImportResult]   = useState(null);
  const csvFileRef = useRef(null);
  const [bookDbListFilter, setBookDbListFilter] = useState('all'); // 'all' | 'needsTagging'
  const [screenshotImporting, setScreenshotImporting] = useState(false);
  const [screenshotPreview, setScreenshotPreview] = useState(null); // { detectedTags, imageSrc }
  const screenshotFileRef = useRef(null);
  const [seedToast, setSeedToast]               = useState(null);
  const seedRunRef = useRef(false);
  const [dupWarning, setDupWarning]             = useState(null); // { newBook, match, similarity }
  const [dismissedDupPairs, setDismissedDupPairs] = useState(new Set());
  const [dupMergeState, setDupMergeState]       = useState(null); // { primary, secondary, preferCover }

  /* Reviews UI */
  const [reviews, setReviews] = useState([]);
  const [customPoints, setCustomPoints] = useState({});

  /* Review Approval Modal */
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalReview, setApprovalReview] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState('Street Team');

  /* Editable star ratings per review */
  const [editingRatings, setEditingRatings] = useState({});

  /* Collapsible review sections */
  const [reviewSectionOpen, setReviewSectionOpen] = useState({ pending: true, approved: true, rejected: false });

  /* Manual Management */
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [pointsModalReader, setPointsModalReader] = useState(null);
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [rewardModalReader, setRewardModalReader] = useState(null);
  const [selectedRewardId, setSelectedRewardId] = useState('');
  const [pointsHistory, setPointsHistory] = useState([]);
  const [readerSearchTerm, setReaderSearchTerm] = useState('');

  /* ARC Requests */
const [arcRequests, setArcRequests] = useState([]);

  /* Rewards Management */
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [showRewardForm, setShowRewardForm] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [rewardFormData, setRewardFormData] = useState({
    name: '',
    description: '',
    pointCost: 0,
    available: true,
    stock: 'unlimited',
    quantity: 0,
  });

  /* Reader Management */
  const [readers, setReaders] = useState([]);
  const [editingReader, setEditingReader] = useState(null);
  const [isLoadingReaders, setIsLoadingReaders] = useState(true);

  /* Roadmap Licences (superadmin-only, read-only view of `licenses`) */
  const [licences, setLicences] = useState([]);

  /* Roadmap Testing (superadmin-only: feedback reports + tester roster) */
  const [roadmapFeedback, setRoadmapFeedback] = useState([]);
  const [roadmapTesters, setRoadmapTesters] = useState([]);

  /* Marketing Compass feedback (superadmin-only — sits alongside roadmap feedback
     in the Testing tab; written only by submitMarketingFeedback via Admin SDK) */
  const [marketingFeedback, setMarketingFeedback] = useState([]);

  /* Signup notifications (superadmin-only: trial starts + plan purchases,
     written by issueTrial / handleRoadmapPurchase via the Admin SDK) */
  const [signupNotifications, setSignupNotifications] = useState([]);

  /* Genres members typed under q1 "Other" — grows the taxonomy from real answers */
  const [genreSuggestions, setGenreSuggestions] = useState([]);

 /* Filters */
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterSource, setFilterSource] = useState("All");
  const [filterArc, setFilterArc] = useState("All");
  const [filterEReader, setFilterEReader] = useState("All");
  const [filterRating, setFilterRating] = useState("All");

  /* CSV */
  const fileInputRef = useRef(null);

/* Collapsed Books State */
const [collapsedBooks, setCollapsedBooks] = useState({});

  /* Quick Add Modal */
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [pastedEmailText, setPastedEmailText] = useState("");
  const [isParsingEmail, setIsParsingEmail] = useState(false);

  /* Reader Template */
const blankReader = {
  name: "",
  emailCorrespondence: "",
  eReaderEmail: "",
  phoneNumber: "",
  acceptsMarketing: true,
  interestedIn: "eBook ARC request",
  reviewPlatforms: [],
  socialUsernames: "",
  favoriteBooks: "",
  location: "",
  signupDate: new Date().toISOString().split("T")[0],
  status: "Noted ARC interest",
  arcSent: "",
  reviewDate: "",
  rating: "",
  notes: "",
  totalPoints: 0,
  totalReviews: 0,
  booksReceived: [],  
  primaryPlatform: "Instagram",        
  followerCount: 0,                    
};

const [readerForm, setReaderForm] = useState(blankReader);

  /* Announcements */
  const [announcements, setAnnouncements] = useState([]);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    arcSendDate: '',
    releaseDate: '',
    reviewDeadline: '',
    bookId: ''
  });

  /* ✨ Community Feed */
  const [posts, setPosts] = useState([]);
  const [postComments, setPostComments] = useState({});
  const [postLikes, setPostLikes] = useState({});
  const [showCreateAnnouncement, setShowCreateAnnouncement] = useState(false);
  const [announcementPostForm, setAnnouncementPostForm] = useState({
    content: '',
  });

  /* DM Inbox — unread count updated by MessagesTab callback */
  const [dmUnreadTotal, setDmUnreadTotal] = useState(0);
  const handleDmUnreadCount = React.useCallback((count) => setDmUnreadTotal(count), []);
  // Community reply state
  const [authorCommentInput, setAuthorCommentInput] = useState({});

  const getStatusColor = (status) => {
  const colors = {
    'Noted ARC interest': 'bg-blue-100 text-blue-700',
    'Engaged': 'bg-purple-100 text-purple-700',
    'Follow-up needed': 'bg-yellow-100 text-yellow-700',
    'ARC sent': 'bg-green-100 text-green-700',
    'Reviewed': 'bg-emerald-100 text-emerald-700',
    'Closed': 'bg-slate-100 text-slate-500',
    'Street Team': 'bg-slate-100 text-teal-700',
    'Book 2 ARC interest': 'bg-indigo-100 text-indigo-700',
    'New Buyer': 'bg-orange-100 text-orange-700',
  };
  return colors[status] || 'bg-slate-100 text-slate-600';
};

  /* ----------------------------------------
     AUTH LISTENER
  ---------------------------------------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (!user) {
        setAuthRoles([]);
        setIsSuperadminUser(false);
        setClaimsChecked(true);
        setIsLoadingAuth(false);
        return;
      }
      try {
        const token = await getIdTokenResult(user);
        const roles = Array.isArray(token.claims.roles)
          ? token.claims.roles
          : (token.claims.role ? [token.claims.role] : []);
        setAuthRoles(roles);
        setIsSuperadminUser(roles.includes('superadmin'));
      } catch {
        // Fail closed: if claims can't be read, treat as NOT superadmin.
        setAuthRoles([]);
        setIsSuperadminUser(false);
      } finally {
        setClaimsChecked(true);
        setIsLoadingAuth(false);
      }
    });
    return () => unsub();
  }, []);

  /* ----------------------------------------
     ADMIN ROUTE GUARD
     The CRM is superadmin-only. Once auth + role claims are resolved, any
     authenticated non-superadmin is redirected off the admin app — authors to
     their dashboard, everyone else (readers, guests, unknown) to the reader portal.
     Unauthenticated users fall through to the login screen below.
  ---------------------------------------- */
  useEffect(() => {
    if (isLoadingAuth || !claimsChecked) return;
    if (!currentUser) return;       // → login screen
    if (isSuperadminUser) return;   // → CRM renders
    const dest = authRoles.includes('author') ? '/author' : '/reader';
    window.location.replace(dest);
  }, [isLoadingAuth, claimsChecked, currentUser, isSuperadminUser, authRoles]);

  /* ----------------------------------------
     LOAD READERS
  ---------------------------------------- */
  useEffect(() => {
    // Only the superadmin may load the reader roster (matches the Firestore rules).
    if (!currentUser || !isSuperadminUser) {
      setReaders([]);
      return;
    }

    setIsLoadingReaders(true);
    const q = query(collection(db, "readers"));

    const unsub = onSnapshot(q, (snap) => {
      const items = [];
      snap.forEach((doc) => items.push({ id: doc.id, ...doc.data() }));
      setReaders(items);
      setIsLoadingReaders(false);
    });

    return () => unsub();
  }, [currentUser, isSuperadminUser]);

  /* ----------------------------------------
     LOAD ROADMAP LICENCES (superadmin only — matches the Firestore rule
     `allow read: if isSuperadmin()` on the `licenses` collection). Read-only.
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser || !isSuperadminUser) {
      setLicences([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, "licenses"),
      (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        setLicences(items);
      },
      (err) => console.error("Licences listener error:", err.message)
    );
    return () => unsub();
  }, [currentUser, isSuperadminUser]);

  /* ----------------------------------------
     LOAD ROADMAP TESTING DATA (superadmin only — matches the Firestore rules
     `if isSuperadmin()` on roadmapFeedback + roadmapTesters). Feedback newest
     first; testers are sorted for display in the component.
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser || !isSuperadminUser) {
      setRoadmapFeedback([]);
      setRoadmapTesters([]);
      setMarketingFeedback([]);
      setSignupNotifications([]);
      setGenreSuggestions([]);
      return;
    }
    const unsubF = onSnapshot(
      query(collection(db, "roadmapFeedback"), orderBy("createdAt", "desc")),
      (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        setRoadmapFeedback(items);
      },
      (err) => console.error("roadmapFeedback listener error:", err.message)
    );
    const unsubT = onSnapshot(
      collection(db, "roadmapTesters"),
      (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        setRoadmapTesters(items);
      },
      (err) => console.error("roadmapTesters listener error:", err.message)
    );
    // Marketing Compass feedback — same superadmin-only rule, newest first.
    const unsubM = onSnapshot(
      query(collection(db, "marketingFeedback"), orderBy("answeredAt", "desc")),
      (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        setMarketingFeedback(items);
      },
      (err) => console.error("marketingFeedback listener error:", err.message)
    );
    // Signup notifications — newest first, capped so the feed stays cheap.
    const unsubS = onSnapshot(
      query(collection(db, "signupNotifications"), orderBy("createdAt", "desc"), limit(200)),
      (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        setSignupNotifications(items);
      },
      (err) => console.error("signupNotifications listener error:", err.message)
    );
    const unsubG = onSnapshot(
      query(collection(db, "genreSuggestions"), orderBy("createdAt", "desc"), limit(200)),
      (snap) => {
        const items = [];
        snap.forEach((d) => items.push({ id: d.id, ...d.data() }));
        setGenreSuggestions(items);
      },
      (err) => console.error("genreSuggestions listener error:", err.message)
    );
    return () => { unsubF(); unsubT(); unsubM(); unsubS(); unsubG(); };
  }, [currentUser, isSuperadminUser]);

  /* ----------------------------------------
     LOAD BOOKS
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) {
      setBooks([]);
      return;
    }

    const unsub = onSnapshot(collectionGroup(db, "books"), (snap) => {
      const list = [];
      snap.forEach((d) => {
        const path = d.ref.path;
        // Only include author-scoped books, not reader bookshelves
        if (path.startsWith("authors/") && path.split("/").length === 4) {
          const authorId = path.split("/")[1];
          list.push({ id: d.id, authorId, ...d.data() });
        }
      });
      setBooks(list);
    });
    return () => unsub();
  }, [currentUser]);

  /* ----------------------------------------
     LOAD PLATFORM AUTHORS (for admin books view)
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) { setPlatformAuthors({}); return; }
    const unsub = onSnapshot(collection(db, "authors"), (snap) => {
      const map = {};
      snap.forEach((d) => {
        const data = d.data();
        map[d.id] = data.penName || data.displayName || data.name || data.email || 'Unknown';
      });
      setPlatformAuthors(map);
    });
    return () => unsub();
  }, [currentUser]);

  /* ----------------------------------------
     LOAD REVIEWS
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) {
      setReviews([]);
      return;
    }

    const unsub = onSnapshot(collectionGroup(db, "bookReviews"), (snap) => {
      const list = [];
      snap.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
      setReviews(list);
    });
    return () => unsub();
  }, [currentUser]);

/* ----------------------------------------
   LOAD ARC REQUESTS
---------------------------------------- */
useEffect(() => {
  if (!currentUser) {
    setArcRequests([]);
    return;
  }

  const unsub = onSnapshot(collection(db, "arcRequests"), (snap) => {
    const list = [];
    snap.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));
    setArcRequests(list);
  });
  return () => unsub();
}, [currentUser]);

  /* ----------------------------------------
     LOAD REWARDS
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) {
      setRewards([]);
      return;
    }

    const rewardsRef = collection(db, "rewards");
    const unsub = onSnapshot(rewardsRef, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setRewards(list);
    });
    return () => unsub();
  }, [currentUser]);

  /* ----------------------------------------
     LOAD REDEMPTIONS
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) {
      setRedemptions([]);
      return;
    }

    const redemptionsRef = collection(db, "redemptions");
    const unsub = onSnapshot(redemptionsRef, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setRedemptions(list);
    });
    return () => unsub();
  }, [currentUser]);

  /* ----------------------------------------
     LOAD ANNOUNCEMENTS
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) {
      setAnnouncements([]);
      return;
    }

    const announcementsRef = collection(db, "announcements");
    const q = query(announcementsRef, orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setAnnouncements(list);
    });
    return () => unsub();
  }, [currentUser]);

  /* ----------------------------------------
     LOAD COMMUNITY POSTS
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) {
      setPosts([]);
      return;
    }

    const postsRef = collection(db, "posts");
    const q = query(postsRef, orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      
      // Sort: pinned posts first, then by date
      const sorted = list.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return b.createdAt?.toMillis() - a.createdAt?.toMillis();
      });
      
      setPosts(sorted);
    });
    return () => unsub();
  }, [currentUser]);

  /* ----------------------------------------
     LOAD POST LIKES
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) {
      setPostLikes({});
      return;
    }

    const likesRef = collection(db, "postLikes");
    const unsub = onSnapshot(likesRef, (snap) => {
      const likesData = {};
      
      snap.forEach((d) => {
        const like = d.data();
        const postId = like.postId;
        
        if (!likesData[postId]) {
          likesData[postId] = { count: 0 };
        }
        
        likesData[postId].count++;
      });
      
      setPostLikes(likesData);
    });
    return () => unsub();
  }, [currentUser]);

  /* ----------------------------------------
     LOAD POST COMMENTS
  ---------------------------------------- */
  useEffect(() => {
    if (!currentUser) {
      setPostComments({});
      return;
    }

    const commentsRef = collection(db, "postComments");
    const q = query(commentsRef, orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      const commentsData = {};
      
      snap.forEach((d) => {
        const comment = { id: d.id, ...d.data() };
        const postId = comment.postId;
        
        if (!commentsData[postId]) {
          commentsData[postId] = [];
        }
        
        commentsData[postId].push(comment);
      });
      
      setPostComments(commentsData);
    });
    return () => unsub();
  }, [currentUser]);

  /* ----------------------------------------
     LOGIN
  ---------------------------------------- */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      setLoginPassword("");
    } catch (err) {
      setLoginError("Invalid email or password.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  /* ----------------------------------------
     READER DRAWER HELPERS
  ---------------------------------------- */
  const openNewReaderDrawer = () => {
    setEditingReader(null);
    setReaderForm(blankReader);
    setShowReaderDrawer(true);
  };

  const openEditReaderDrawer = (reader) => {
    setEditingReader(reader);
    setReaderForm({ ...blankReader, ...reader });
    setShowReaderDrawer(true);
  };

  const closeReaderDrawer = () => {
    setShowReaderDrawer(false);
    setEditingReader(null);
    setReaderForm(blankReader);
  };

  /* ----------------------------------------
   READER DETAIL PANEL HANDLERS (NEW)
---------------------------------------- */
const openReaderDetailPanel = (reader) => {
  setViewingReader(reader);
  setShowReaderDetailPanel(true);
  setShowReaderHistoryImport(false);

  // Load reader's reviews
  const readerReviewsList = reviews.filter(
    r => r.readerEmail === reader.emailCorrespondence || r.readerId === reader.id
  );
  setReaderReviews(readerReviewsList);

  // Load points history
  loadPointsHistory(reader.id);

  // Load reading history + import batches
  loadReaderReadingHistory(reader.id);
};

const loadReaderReadingHistory = async (rid) => {
  setReaderReadingHistory([]);
  setReaderImportBatches([]);
  if (!rid) return;
  try {
    const [histSnap, batchSnap] = await Promise.all([
      getDocs(collection(db, 'readers', rid, 'readingHistory')),
      getDocs(collection(db, 'readers', rid, 'importBatches')),
    ]);
    const hist = [];
    histSnap.forEach(d => hist.push({ id: d.id, ...d.data() }));
    setReaderReadingHistory(hist);
    const batches = [];
    batchSnap.forEach(d => batches.push({ id: d.id, ...d.data() }));
    batches.sort((a, b) => (b.importedAt?.seconds || 0) - (a.importedAt?.seconds || 0));
    setReaderImportBatches(batches);
  } catch (e) {
    console.error('[Admin] Failed to load reading history:', e);
  }
};

const closeReaderDetailPanel = () => {
  setShowReaderDetailPanel(false);
  setViewingReader(null);
  setReaderReviews([]);
  setReaderReadingHistory([]);
  setReaderImportBatches([]);
};

const switchToEditFromDetail = () => {
  // Switch from detail view to edit mode
  setShowReaderDetailPanel(false);
  openEditReaderDrawer(viewingReader);
};

  /* ----------------------------------------
     SAVE READER
  ---------------------------------------- */
  const handleSaveReader = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setIsSavingReader(true);

    try {
      const data = {
        ...readerForm,
        lastModified: serverTimestamp(),
        authorId: currentUser.uid,
      };

      if (editingReader) {
        await updateDoc(doc(db, "readers", editingReader.id), data);
 } else {
        await setDoc(
          doc(db, "readers", readerForm.emailCorrespondence),
          {
            ...data,
            source: 'manual', // Track manual adds
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      closeReaderDrawer();
      alert("Reader saved!");
    } catch (err) {
      alert("Error saving reader: " + err.message);
    }

    setIsSavingReader(false);
  };

  /* ----------------------------------------
     DELETE READER
  ---------------------------------------- */
  const handleDeleteReader = async (id) => {
    if (!window.confirm("Delete this reader?")) return;

    try {
      await deleteDoc(doc(db, "readers", id));
      alert("Reader deleted.");
    } catch (err) {
      alert("Error deleting reader: " + err.message);
    }
  };

/* ----------------------------------------
   QUICK ADD FROM EMAIL PASTE
---------------------------------------- */
const handleQuickAddFromEmail = async () => {
  if (!pastedEmailText.trim()) {
    alert("Please paste the email content first");
    return;
  }

  setIsParsingEmail(true);
  try {
    const parsed = await parseReaderFromEmail(pastedEmailText);

    if (!parsed.emailCorrespondence) {
      alert("Could not find an email address in the pasted text. Please check the content and try again.");
      return;
    }

    const readerData = {
      name: parsed.name || "",
      emailCorrespondence: parsed.emailCorrespondence,
      eReaderEmail: parsed.eReaderEmail || parsed.emailCorrespondence,
      acceptsMarketing: parsed.acceptsMarketing !== false,
      interestedIn: parsed.interestedIn || "eBook ARC request",
      reviewPlatforms: Array.isArray(parsed.reviewPlatforms) ? parsed.reviewPlatforms : [],
      socialUsernames: parsed.socialUsernames || "",
      favoriteBooks: parsed.favoriteBooks || "",
      location: parsed.location || "",
      signupDate: new Date().toISOString().split("T")[0],
      status: "Noted ARC interest",
      arcSent: "",
      reviewDate: "",
      rating: "",
      notes: "",
      totalPoints: 0,
      totalReviews: 0,
      booksReceived: [],
      authorId: currentUser.uid,
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),
    };

    await setDoc(
      doc(db, "readers", readerData.emailCorrespondence),
      { ...readerData, source: 'manual' },
      { merge: true }
    );

    alert(`✅ ${readerData.name || readerData.emailCorrespondence} added successfully!`);
    setShowQuickAddModal(false);
    setPastedEmailText("");
  } catch (err) {
    alert("Error adding reader: " + err.message);
  } finally {
    setIsParsingEmail(false);
  }
};

  /* ----------------------------------------
     EXPORT CSV
  ---------------------------------------- */
  const exportToCSV = () => {
    if (readers.length === 0) {
      alert('No readers to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Name',
      'Email (Correspondence)',
      'Email (eReader)',
      'Status',
      'Platforms',
      'Primary Social',
      'Follower Count',
      'Location',
      'Signup Date',
      'ARC Sent Date',
      'Review Date',
      'Rating',
      'Total Points',
      'Total Reviews',
      'Books Received',
      'Notes'
    ];

    // Convert readers to CSV rows
    const rows = readers.map(reader => {
      const platforms = Array.isArray(reader.reviewPlatforms) 
        ? reader.reviewPlatforms.join('; ') 
        : '';
      
      const booksReceived = (reader.booksReceived || [])
        .map(bookId => {
          const book = books.find(b => b.id === bookId);
          return book ? book.title : bookId;
        })
        .join('; ');

      return [
        reader.name || '',
        reader.emailCorrespondence || '',
        reader.eReaderEmail || '',
        reader.status || '',
        platforms,
        reader.primaryPlatform || '',
        reader.followerCount || 0,
        reader.location || '',
        reader.signupDate || '',
        reader.arcSent || '',
        reader.reviewDate || '',
        reader.rating || '',
        reader.totalPoints || 0,
        reader.totalReviews || 0,
        booksReceived,
        (reader.notes || '').replace(/"/g, '""') // Escape quotes in notes
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `arc-readers-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ----------------------------------------
     BOOK HANDLERS
  ---------------------------------------- */
  const handleBookSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBook) {
        await updateDoc(doc(db, "books", editingBook.id), {
          ...bookFormData,
          lastModified: serverTimestamp(),
        });
        alert("Book updated!");
      } else {
        await addDoc(collection(db, "books"), {
          ...bookFormData,
          createdAt: serverTimestamp(),
        });
        alert("Book added!");
      }
      resetBookForm();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const resetBookForm = () => {
    setEditingBook(null);
    setBookFormData({
      title: "",
      series: "",
      releaseDate: "",
      arcAvailable: true,
      status: "upcoming",
      coverUrl: "",
      blurb: "",
      genres: [],
      contentWarnings: "",
      wordCount: "",
      pageCount: "",
      arcSendDate: "",
      goLiveDate: "",
      reviewReleaseDate: "",
      reviewsEnabled: true,
    });
    setShowBookForm(false);
  };

  const handleEditBook = (book) => {
    setEditingBook(book);
    setBookFormData(book);
    setShowBookForm(true);
  };

  const handleDeleteBook = async (id) => {
    if (!window.confirm("Delete this book?")) return;
    try {
      await deleteDoc(doc(db, "books", id));
      alert("Book removed.");
    } catch (err) {
      alert(err.message);
    }
  };

  /* ----------------------------------------
     BOOK DATABASE & ADMIN TAGGING
  ---------------------------------------- */
  // Load email log (on-demand, not real-time)
  const loadEmailLog = async () => {
    if (emailLog.length > 0) return; // already loaded
    setEmailLogLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'emailRequests'), orderBy('sentAt', 'desc'), limit(200)));
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      // Also include docs that have createdAt but no sentAt (pending/failed)
      if (list.length === 0) {
        const snap2 = await getDocs(query(collection(db, 'emailRequests'), orderBy('createdAt', 'desc'), limit(200)));
        snap2.forEach(d => { if (!list.find(l => l.id === d.id)) list.push({ id: d.id, ...d.data() }); });
      }
      setEmailLog(list);
    } catch (err) {
      console.error('Failed to load email log:', err);
    }
    setEmailLogLoading(false);
  };

  // Load book database list
  const loadBookDb = async () => {
    const snap = await getDocs(collection(db, 'bookDatabase'));
    const list = [];
    snap.forEach(d => list.push({ id: d.id, ...d.data() }));
    list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    setBookDbList(list);

    // Auto-seed if fewer than 10 books on first load
    if (list.length < 10 && !seedRunRef.current) {
      seedRunRef.current = true;
      console.log('[BookDB Seed] Starting auto-seed, current count:', list.length);
      setSeedToast('Seeding book database — this may take a moment...');
      seedBookDatabase(list.length, (count) => {
        console.log('[BookDB Seed] Complete, imported:', count);
        setSeedToast(`We've pre-loaded ${count} books to get you started — head to Needs Tagging to verify them.`);
        loadBookDb(); // Refresh the list
        setTimeout(() => setSeedToast(null), 10000);
      }).catch(err => {
        console.error('[BookDB Seed] Failed:', err);
        setSeedToast(null);
      });
    }
  };

  // Search Google Books
  const searchGoogleBooks = async (q) => {
    if (!q || q.length < 2) { setBookDbSearchResults([]); return; }
    setBookDbSearching(true);
    try {
      const resp = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(q)}&maxResults=8`);
      const data = await resp.json();
      setBookDbSearchResults((data.items || []).map(item => ({
        googleBooksId: item.id,
        title: item.volumeInfo.title,
        author: (item.volumeInfo.authors || []).join(', '),
        cover: item.volumeInfo.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
        pageCount: item.volumeInfo.pageCount || null,
        publishedDate: item.volumeInfo.publishedDate || null,
        description: (item.volumeInfo.description || '').substring(0, 300),
        isbn: (item.volumeInfo.industryIdentifiers || []).find(i => i.type === 'ISBN_13')?.identifier || null,
        googleBooksLink: item.volumeInfo.infoLink || null,
      })));
    } catch (e) { console.error('Google Books search error:', e); }
    setBookDbSearching(false);
  };

  // Add book to database — with duplicate detection
  const addBookToDb = async (book, skipDupCheck = false) => {
    // Check for duplicates first
    if (!skipDupCheck) {
      const dup = findDuplicate(book, bookDbList, dismissedDupPairs);
      if (dup) {
        setDupWarning({ newBook: book, match: dup.match, similarity: dup.similarity });
        return;
      }
    }
    try {
      await setDoc(doc(db, 'bookDatabase', book.googleBooksId), {
        title: book.title, author: book.author, googleBooksId: book.googleBooksId,
        isbn: book.isbn, cover: book.cover, description: book.description,
        pageCount: book.pageCount, publishedDate: book.publishedDate,
        googleBooksLink: book.googleBooksLink,
        adminTags: {}, resolvedTags: {}, submissionCount: 0,
        dateAdded: new Date().toISOString(), addedBy: currentUser.email,
      });
      alert(`Added "${book.title}" to database!`);
      loadBookDb();
    } catch (e) { alert('Error: ' + e.message); }
  };

  // Merge two book records in Firestore
  const handleMergeBooks = async (primary, secondary, preferCover = 'primary') => {
    try {
      const merged = mergeBookRecords(primary, secondary, preferCover);
      const primaryId = primary.id || primary.googleBooksId;
      const secondaryId = secondary.id || secondary.googleBooksId;
      await setDoc(doc(db, 'bookDatabase', primaryId), merged);
      await deleteDoc(doc(db, 'bookDatabase', secondaryId));
      loadBookDb();
      return true;
    } catch (e) {
      console.error('Merge error:', e);
      alert('Merge failed: ' + e.message);
      return false;
    }
  };

  // Delete a duplicate book (keep the other)
  const handleDeleteDuplicate = async (bookId) => {
    try {
      await deleteDoc(doc(db, 'bookDatabase', bookId));
      loadBookDb();
    } catch (e) {
      alert('Delete failed: ' + e.message);
    }
  };

  // Dismiss a duplicate pair permanently
  const handleDismissDuplicate = (id1, id2) => {
    setDismissedDupPairs(prev => {
      const next = new Set(prev);
      next.add(`${id1}|||${id2}`);
      return next;
    });
  };

  // Open tag editor for a book
  const openAdminTagEditor = async (bookId) => {
    setAdminTagBookId(bookId);
    setAdminTagMsg('');
    setAdminTagSections({});
    const bookData = bookDbList.find(b => b.id === bookId);
    setAdminTagBookData(bookData);
    try {
      const subsSnap = await getDocs(collection(db, 'bookDatabase', bookId, 'submissions'));
      const subs = [];
      subsSnap.forEach(d => subs.push({ id: d.id, ...d.data() }));
      setAdminTagSubmissions(subs);
      setAdminTagOverrides(bookData?.adminTags || {});
      setAdminTagForm(bookData?.adminTags || {});
    } catch (e) { console.error('Failed to load tags:', e); }
    setBookDbView('editor');
  };

  const handleSaveAdminTags = async () => {
    if (!adminTagBookId) return;
    setAdminTagSaving(true);
    try {
      const toSave = {};
      for (const section of BOOK_TAXONOMY) {
        if (adminTagForm[section.key] && Object.keys(adminTagForm[section.key]).length > 0) {
          toSave[section.key] = adminTagForm[section.key];
        }
      }
      // Save to adminTags on the book document
      await updateDoc(doc(db, 'bookDatabase', adminTagBookId), {
        adminTags: toSave,
      });
      // Recalculate resolved tags
      const resolved = resolveBookTags(toSave, adminTagSubmissions);

      // Check if high-priority fields are now filled → auto-clear needsTagging
      const hasSteam = Object.keys(resolved.steamLevel || {}).some(k => resolved.steamLevel[k]?.value === 'present');
      const hasGenre = Object.keys(resolved.genres || {}).some(k => resolved.genres[k]?.value === 'present');
      const tropeCount = Object.keys(resolved.tropes || {}).filter(k => resolved.tropes[k]?.value === 'present').length;
      const hasRelationship = Object.keys(resolved.relationship || {}).some(k => resolved.relationship[k]?.value === 'present');
      const needsTagging = !(hasSteam && hasGenre && tropeCount >= 3 && hasRelationship);

      await updateDoc(doc(db, 'bookDatabase', adminTagBookId), { resolvedTags: resolved, needsTagging });
      setAdminTagOverrides(toSave);
      setAdminTagMsg('Admin tags saved!');
      loadBookDb();
    } catch (e) {
      setAdminTagMsg('Error saving: ' + e.message);
    }
    setAdminTagSaving(false);
  };

  const handleClearAdminOverride = async (sectionKey, item) => {
    if (!adminTagBookId) return;
    const updated = { ...adminTagForm };
    if (updated[sectionKey]) {
      delete updated[sectionKey][item];
      if (Object.keys(updated[sectionKey]).length === 0) delete updated[sectionKey];
    }
    setAdminTagForm(updated);
    try {
      await updateDoc(doc(db, 'bookDatabase', adminTagBookId), { adminTags: updated });
      const resolved = resolveBookTags(updated, adminTagSubmissions);
      await updateDoc(doc(db, 'bookDatabase', adminTagBookId), { resolvedTags: resolved });
      setAdminTagOverrides(updated);
    } catch (e) { console.error('Failed to clear override:', e); }
  };

  // Screenshot import — extract tags from image using Claude vision
  const handleScreenshotImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScreenshotImporting(true);
    setScreenshotPreview(null);

    try {
      // Read file as base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const mediaType = file.type || 'image/png';
      const imageSrc = URL.createObjectURL(file);

      // Build taxonomy reference for the prompt
      const taxonomyRef = BOOK_TAXONOMY.map(sec => {
        const allItems = sec.groups.flatMap(g => g.items);
        return `${sec.key} (${sec.title}): ${allItems.join(', ')}`;
      }).join('\n');

      const systemPrompt = `You are a book tag extraction assistant. You read screenshots of book pages (e.g. from Romance.io, StoryGraph, Goodreads) and extract all visible tags, tropes, and metadata. Return ONLY valid JSON — no markdown fences, no explanation.`;

      const prompt = `Extract all book tags from this screenshot. Map them to these categories and return as JSON: genre, relationship, tropes, story_elements, content_warnings, heroine_traits, hero_traits, time_period, story_structure, pov, length. Use only values from our taxonomy. Return null for any category not visible.

Here is our full taxonomy — you MUST use these exact values:
${taxonomyRef}

Return a JSON object where keys are the taxonomy section keys (steamLevel, genres, relationship, tropes, kinks, storyElements, contentWarnings, heroine, hero, location, timePeriod, storyStructure, pov, length) and values are arrays of matched item strings from our taxonomy, or null if that category is not visible in the screenshot.

Example: {"genres": ["High fantasy", "Fae"], "tropes": ["Enemies to lovers", "Slow burn"], "steamLevel": ["Explicit open door"], "relationship": ["M-F romance"], "storyStructure": null, ...}`;

      const result = await callClaudeWithImage({
        imageBase64: base64,
        mediaType,
        prompt,
        systemPrompt,
        maxTokens: 2048,
      });

      // Parse the JSON response
      const cleaned = result.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
      const detected = JSON.parse(cleaned);

      // Convert to adminTagForm format: { sectionKey: { itemName: 'present' } }
      const detectedTags = {};
      let totalCount = 0;
      for (const section of BOOK_TAXONOMY) {
        const values = detected[section.key];
        if (values && Array.isArray(values) && values.length > 0) {
          const validItems = section.groups.flatMap(g => g.items);
          detectedTags[section.key] = {};
          for (const val of values) {
            // Find exact match or close match
            const exact = validItems.find(item => item.toLowerCase() === val.toLowerCase());
            if (exact) {
              detectedTags[section.key][exact] = 'present';
              totalCount++;
            }
          }
          if (Object.keys(detectedTags[section.key]).length === 0) {
            delete detectedTags[section.key];
          }
        }
      }

      setScreenshotPreview({ detectedTags, imageSrc, totalCount });
    } catch (err) {
      console.error('[Screenshot Import] Error:', err);
      alert('Failed to extract tags from screenshot: ' + err.message);
    }
    setScreenshotImporting(false);
    // Reset file input so the same file can be re-selected
    if (screenshotFileRef.current) screenshotFileRef.current.value = '';
  };

  const applyScreenshotTags = () => {
    if (!screenshotPreview?.detectedTags) return;
    // Merge detected tags into current admin tag form (don't overwrite existing)
    setAdminTagForm(prev => {
      const merged = { ...prev };
      for (const [secKey, items] of Object.entries(screenshotPreview.detectedTags)) {
        merged[secKey] = { ...(merged[secKey] || {}), ...items };
      }
      return merged;
    });
    setScreenshotPreview(null);
    setAdminTagMsg('Screenshot tags applied — review and save when ready.');
  };

  // CSV Import
  const handleCsvImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (results) => {
        setCsvImportData(results.data);
        setCsvImportResult(null);
      },
      error: () => alert('Failed to parse CSV'),
    });
  };

  const commitCsvImport = async () => {
    if (!csvImportData) return;
    setCsvImporting(true);
    let imported = 0, updated = 0, errors = 0;
    for (const row of csvImportData) {
      if (!row.title) { errors++; continue; }
      try {
        // Search Google Books for this title+author to get an ID
        const q = encodeURIComponent(`intitle:${row.title}${row.author ? `+inauthor:${row.author}` : ''}`);
        const resp = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${q}&maxResults=1`);
        const data = await resp.json();
        if (!data.items?.length) { errors++; continue; }
        const item = data.items[0];
        const gbId = item.id;
        const vol = item.volumeInfo;

        // Build admin tags from CSV columns
        const adminTags = {};
        const tagMap = {
          steam_level: 'steamLevel', genre: 'genres', relationship: 'relationship',
          tropes: 'tropes', content_warnings: 'contentWarnings', time_period: 'timePeriod',
          story_structure: 'storyStructure', pov: 'pov', length: 'length',
        };
        for (const [csvCol, taxKey] of Object.entries(tagMap)) {
          if (row[csvCol]) {
            const items = row[csvCol].split('|').map(s => s.trim()).filter(Boolean);
            adminTags[taxKey] = {};
            items.forEach(item => { adminTags[taxKey][item] = 'present'; });
          }
        }

        const existing = await getDoc(doc(db, 'bookDatabase', gbId));
        if (existing.exists()) {
          await updateDoc(doc(db, 'bookDatabase', gbId), { adminTags });
          const resolved = resolveBookTags(adminTags, []);
          await updateDoc(doc(db, 'bookDatabase', gbId), { resolvedTags: resolved });
          updated++;
        } else {
          const resolved = resolveBookTags(adminTags, []);
          await setDoc(doc(db, 'bookDatabase', gbId), {
            title: vol.title, author: (vol.authors || []).join(', '), googleBooksId: gbId,
            isbn: (vol.industryIdentifiers || []).find(i => i.type === 'ISBN_13')?.identifier || null,
            cover: vol.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
            description: (vol.description || '').substring(0, 300),
            pageCount: vol.pageCount || null, publishedDate: vol.publishedDate || null,
            googleBooksLink: vol.infoLink || null,
            adminTags, resolvedTags: resolved, submissionCount: 0,
            dateAdded: new Date().toISOString(), addedBy: currentUser.email,
          });
          imported++;
        }
      } catch (e) { errors++; }
    }
    setCsvImportResult({ imported, updated, errors });
    setCsvImporting(false);
    loadBookDb();
  };

  /* ----------------------------------------
     REVIEW HANDLERS
  ---------------------------------------- */
const handleApproveReview = async (reviewId, reviewData, pointsToAward, newReaderStatus) => {
  const points = pointsToAward !== undefined ? pointsToAward : 50;

  const statusMap = {
    'Street Team': 'Street Team',
    'Second ARC': 'Book 2 ARC interest',
    'Closed': 'Closed',
  };
  const finalStatus = statusMap[newReaderStatus] || 'Street Team';

  try {
    // Write to author-scoped bookReviews (canonical path)
    const authorId = reviewData.authorId || 'uzMXPEok4AVIw8luvjAeNLlWy743';
    await updateDoc(doc(db, 'authors', authorId, 'bookReviews', reviewId), {
      status: 'approved',
      approvedAt: serverTimestamp(),
      pointsAwarded: points
    });

    const readerRef = readers.find(r =>
      r.emailCorrespondence === reviewData.readerEmail ||
      r.id === reviewData.readerId
    );

    if (readerRef) {
      const readerUpdate = {
        totalPoints: increment(points),
        totalReviews: increment(1),
        status: finalStatus,
        reviewRating: reviewData.rating || null,
        lastModified: serverTimestamp()
      };
      await updateDoc(doc(db, 'readers', readerRef.id), readerUpdate);

      // Also update per-book reader subcollection so Author Portal reflects the change
      if (reviewData.bookId) {
        try {
          await updateDoc(doc(db, 'authors', authorId, 'books', reviewData.bookId, 'readers', readerRef.id), readerUpdate);
        } catch (_) { /* per-book doc may not exist yet */ }
      }
    }

    setCustomPoints(prev => {
      const updated = { ...prev };
      delete updated[reviewId];
      return updated;
    });

    alert(`✅ Review approved! ${points} points awarded. ${readerRef?.name || 'Reader'} moved to "${finalStatus}".`);
  } catch (error) {
    
    alert('❌ Error approving review: ' + error.message);
  }
};

  const handleRejectReview = async (reviewId, reviewData) => {
    if (!window.confirm('Reject and delete this review? This cannot be undone.')) return;
    try {
      const authorId = reviewData?.authorId || 'uzMXPEok4AVIw8luvjAeNLlWy743';
      await deleteDoc(doc(db, 'authors', authorId, 'bookReviews', reviewId));
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

/* ----------------------------------------
   DELETE REVIEW
---------------------------------------- */
const handleDeleteReview = async (reviewId, reviewData) => {
  if (!window.confirm('Are you sure you want to delete this review? This cannot be undone.')) {
    return;
  }

  try {
    // If review was approved, deduct the points from the reader
    if (reviewData.status === 'approved' && reviewData.pointsAwarded > 0) {
      const reader = readers.find(r => 
        r.emailCorrespondence === reviewData.readerEmail || 
        r.id === reviewData.readerId
      );
      
      if (reader) {
        await updateDoc(doc(db, 'readers', reader.id), {
          totalPoints: increment(-reviewData.pointsAwarded),
          totalReviews: increment(-1),
          lastModified: serverTimestamp()
        });
      }
    }

    // Delete the review
    const authorId = reviewData.authorId || 'uzMXPEok4AVIw8luvjAeNLlWy743';
    await deleteDoc(doc(db, 'authors', authorId, 'bookReviews', reviewId));

    alert('✅ Review deleted successfully!');
  } catch (error) {
    
    alert('❌ Error deleting review: ' + error.message);
  }
};

/* ----------------------------------------
   UPDATE REVIEW STAR RATING
---------------------------------------- */
const handleUpdateReviewRating = async (reviewId, newRating) => {
  try {
    const rev = reviews.find(r => r.id === reviewId);
    if (rev?.authorId) {
      await updateDoc(doc(db, 'authors', rev.authorId, 'bookReviews', reviewId), { rating: newRating });
    } else {
      // Fallback to legacy path for old reviews
      await updateDoc(doc(db, 'bookReviews', reviewId), { rating: newRating });
    }
    setEditingRatings(prev => { const u = { ...prev }; delete u[reviewId]; return u; });
  } catch (error) {
    alert('❌ Error updating rating: ' + error.message);
  }
};

/* ----------------------------------------
   BACKFILL RATINGS FROM APPROVED REVIEWS
---------------------------------------- */
const handleBackfillRatings = async () => {
  if (!window.confirm('This will copy star ratings from all approved reviews onto the reader records so filters work. Continue?')) return;

  const approvedReviews = reviews.filter(r => r.status === 'approved' && r.rating);
  if (approvedReviews.length === 0) {
    alert('No approved reviews with ratings found.');
    return;
  }

  let updated = 0;
  for (const rev of approvedReviews) {
    const reader = readers.find(r =>
      r.emailCorrespondence === rev.readerEmail || r.id === rev.readerId
    );
    if (reader) {
      try {
        await updateDoc(doc(db, 'readers', reader.id), {
          reviewRating: rev.rating,
          lastModified: serverTimestamp()
        });
        updated++;
      } catch (e) {
        
      }
    }
  }
  alert(`✅ Done! Updated ${updated} reader record${updated !== 1 ? 's' : ''} with star ratings.`);
};

/* ----------------------------------------
   MANUAL POINTS - ADD
---------------------------------------- */
const handleAddPoints = async (readerId, amount, reason) => {
  if (!amount || amount <= 0) {
    alert('Please enter a valid points amount');
    return;
  }

  if (!reason || reason.trim() === '') {
    alert('Please enter a reason for adding points');
    return;
  }

  try {
    const readerRef = doc(db, 'readers', readerId);
    
    // Add points to reader
    await updateDoc(readerRef, {
      totalPoints: increment(amount),
      lastModified: serverTimestamp()
    });

    // Log the points adjustment
    await addDoc(collection(db, 'pointsHistory'), {
      readerId: readerId,
      amount: amount,
      type: 'manual_add',
      reason: reason,
      createdAt: serverTimestamp(),
      createdBy: currentUser.email
    });

    alert(`✅ Added ${amount} points!`);
    setPointsAmount('');
    setPointsReason('');
    setShowPointsModal(false);
  } catch (error) {
    
    alert('❌ Error adding points: ' + error.message);
  }
};

/* ----------------------------------------
   MANUAL POINTS - DEDUCT
---------------------------------------- */
const handleDeductPoints = async (readerId, amount, reason) => {
  if (!amount || amount <= 0) {
    alert('Please enter a valid points amount');
    return;
  }

  if (!reason || reason.trim() === '') {
    alert('Please enter a reason for deducting points');
    return;
  }

  try {
    const readerRef = doc(db, 'readers', readerId);
    const reader = readers.find(r => r.id === readerId);
    
    // Check if reader has enough points
    if (reader && reader.totalPoints < amount) {
      if (!window.confirm(`Reader only has ${reader.totalPoints} points. Deduct ${amount} anyway (will result in negative balance)?`)) {
        return;
      }
    }

    // Deduct points from reader
    await updateDoc(readerRef, {
      totalPoints: increment(-amount),
      lastModified: serverTimestamp()
    });

    // Log the points adjustment
    await addDoc(collection(db, 'pointsHistory'), {
      readerId: readerId,
      amount: -amount,
      type: 'manual_deduct',
      reason: reason,
      createdAt: serverTimestamp(),
      createdBy: currentUser.email
    });

    alert(`✅ Deducted ${amount} points!`);
    setPointsAmount('');
    setPointsReason('');
    setShowPointsModal(false);
  } catch (error) {
    
    alert('❌ Error deducting points: ' + error.message);
  }
};

/* ----------------------------------------
   GIVE MANUAL REWARD
---------------------------------------- */
const handleGiveReward = async (readerId, rewardId) => {
  if (!rewardId) {
    alert('Please select a reward');
    return;
  }

  const reward = rewards.find(r => r.id === rewardId);
  const reader = readers.find(r => r.id === readerId);

  if (!reward || !reader) {
    alert('Reward or reader not found');
    return;
  }

  if (!window.confirm(`Give "${reward.name}" to ${reader.name}? (This bypasses the normal redemption process)`)) {
    return;
  }

  try {
    // Create a redemption record
    await addDoc(collection(db, 'redemptions'), {
      readerId: readerId,
      readerEmail: reader.emailCorrespondence,
      readerName: reader.name,
      rewardId: rewardId,
      rewardName: reward.name,
      pointsSpent: 0,
      status: 'fulfilled',
      redeemedAt: serverTimestamp(),
      fulfilledAt: serverTimestamp(),
      manualGrant: true,
      grantedBy: currentUser.email,
      notes: 'Manually granted by admin'
    });

    // Update reward stock if limited
    if (reward.stock === 'limited' && reward.quantity > 0) {
      await updateDoc(doc(db, 'rewards', rewardId), {
        quantity: increment(-1)
      });
    }

    alert(`✅ Reward "${reward.name}" given to ${reader.name}!`);
    setSelectedRewardId('');
    setShowRewardModal(false);
    setRewardModalReader(null);
  } catch (error) {
    
    alert('❌ Error giving reward: ' + error.message);
  }
};

/* ----------------------------------------
   LOAD POINTS HISTORY
---------------------------------------- */
const loadPointsHistory = async (readerId) => {
  try {
    const historyRef = collection(db, 'pointsHistory');
    const q = query(
      historyRef, 
      where('readerId', '==', readerId),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    
    const snapshot = await getDocs(q);
    const history = [];
    snapshot.forEach(doc => {
      history.push({ id: doc.id, ...doc.data() });
    });
    
    setPointsHistory(history);
  } catch (error) {
    
    setPointsHistory([]);
  }
};

/* ----------------------------------------
   APPROVE ARC REQUEST  // 👈 CORRECT COMMENT
---------------------------------------- */
const handleApproveArcRequest = async (requestId) => {
  try {
    const request = arcRequests.find(r => r.id === requestId);
    if (!request) {
      alert("Request not found");
      return;
    }

    // Check if reader already exists
    const existingReader = readers.find(r => r.emailCorrespondence === request.readerEmail);

    if (!existingReader) {
      // Create new reader — no status change, just register them
      await addDoc(collection(db, "readers"), {
        name: request.readerName,
        emailCorrespondence: request.readerEmail,
        totalPoints: request.readerStats?.totalPoints || 0,
        totalReviews: request.readerStats?.totalReviews || 0,
        signupDate: new Date().toLocaleDateString(),
        booksReceived: [request.bookId],
        source: 'reader_portal',
        hasApprovedArcRequest: true,
        createdAt: serverTimestamp(),
        status: "Noted ARC interest"
      });
    } else {
      // Update existing reader — add book but do NOT change their status
      const readerRef = doc(db, "readers", existingReader.id);
      const currentBooks = existingReader.booksReceived || [];

      if (!currentBooks.includes(request.bookId)) {
        await updateDoc(readerRef, {
          booksReceived: [...currentBooks, request.bookId],
          hasApprovedArcRequest: true,
          lastModified: serverTimestamp()
        });
      } else {
        await updateDoc(readerRef, {
          hasApprovedArcRequest: true,
          lastModified: serverTimestamp()
        });
      }
    }

    // Update request status — syncs to both arcRequests and campaign applications
    await updateArcStatus({
      authorId: request.authorId || null,
      readerId: request.readerId,
      bookId: request.bookId,
      newStatus: 'approved',
      requestId,
    });

    alert("ARC request approved! ✅ Reader status unchanged.");
  } catch (error) {
    alert("Error approving request");
  }
};


/* ----------------------------------------
   BULK APPROVE ALL PENDING ARC REQUESTS
---------------------------------------- */
const handleApproveAllPendingArcRequests = async () => {
  const pending = arcRequests.filter(r => r.status === 'pending');
  if (pending.length === 0) {
    alert("No pending ARC requests to approve.");
    return;
  }
  if (!window.confirm(`Approve all ${pending.length} pending ARC request${pending.length > 1 ? 's' : ''}? Reader statuses will not be changed.`)) return;

  let approved = 0;
  let errors = 0;

  for (const request of pending) {
    try {
      const existingReader = readers.find(r => r.emailCorrespondence === request.readerEmail);

      if (!existingReader) {
        await addDoc(collection(db, "readers"), {
          name: request.readerName,
          emailCorrespondence: request.readerEmail,
          totalPoints: request.readerStats?.totalPoints || 0,
          totalReviews: request.readerStats?.totalReviews || 0,
          signupDate: new Date().toLocaleDateString(),
          booksReceived: [request.bookId],
          source: 'reader_portal',
          hasApprovedArcRequest: true,
          createdAt: serverTimestamp(),
          status: "Noted ARC interest"
        });
      } else {
        const readerRef = doc(db, "readers", existingReader.id);
        const currentBooks = existingReader.booksReceived || [];
        if (!currentBooks.includes(request.bookId)) {
          await updateDoc(readerRef, {
            booksReceived: [...currentBooks, request.bookId],
            hasApprovedArcRequest: true,
            lastModified: serverTimestamp()
          });
        } else {
          await updateDoc(readerRef, {
            hasApprovedArcRequest: true,
            lastModified: serverTimestamp()
          });
        }
      }

      // Sync to both arcRequests and campaign applications
      await updateArcStatus({
        authorId: request.authorId || null,
        readerId: request.readerId,
        bookId: request.bookId,
        newStatus: 'approved',
        requestId: request.id,
      });

      approved++;
    } catch (err) {
      errors++;
    }
  }

  alert(`✅ Done! ${approved} request${approved > 1 ? 's' : ''} approved${errors > 0 ? `, ${errors} failed (check console)` : ''}. Reader statuses unchanged.`);
};

/* ----------------------------------------
   DENY ARC REQUEST
---------------------------------------- */
const handleDenyArcRequest = async (requestId) => {
  if (!window.confirm("Are you sure you want to decline this ARC request?")) return;

  try {
    const request = arcRequests.find(r => r.id === requestId);
    await updateArcStatus({
      authorId: request?.authorId || null,
      readerId: request?.readerId,
      bookId: request?.bookId,
      newStatus: 'declined',
      requestId,
    });
    alert("ARC request declined.");
  } catch (error) {
    alert("Error declining request");
  }
};

  /* ----------------------------------------
     REWARD HANDLERS
  ---------------------------------------- */
  const handleRewardSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReward) {
        const rewardRef = doc(db, 'rewards', editingReward.id);
        await updateDoc(rewardRef, {
          ...rewardFormData,
          lastModified: serverTimestamp()
        });
        alert('Reward updated!');
      } else {
        await addDoc(collection(db, 'rewards'), {
          ...rewardFormData,
          createdAt: serverTimestamp()
        });
        alert('Reward added!');
      }
      resetRewardForm();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const resetRewardForm = () => {
    setRewardFormData({
      name: '',
      description: '',
      pointCost: 0,
      available: true,
      stock: 'unlimited',
      quantity: 0,
    });
    setShowRewardForm(false);
    setEditingReward(null);
  };

  const handleEditReward = (reward) => {
    setEditingReward(reward);
    setRewardFormData(reward);
    setShowRewardForm(true);
  };

  const handleDeleteReward = async (id) => {
    if (window.confirm('Delete this reward?')) {
      try {
        await deleteDoc(doc(db, 'rewards', id));
        alert('Reward deleted!');
      } catch (err) {
        alert('Error: ' + err.message);
      }
    }
  };

  const handleFulfillRedemption = async (redemptionId) => {
    try {
      await updateDoc(doc(db, 'redemptions', redemptionId), {
        status: 'fulfilled',
        fulfilledAt: serverTimestamp()
      });
      alert('Redemption marked as fulfilled!');
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  /* ----------------------------------------
     ANNOUNCEMENT HANDLERS
  ---------------------------------------- */
  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    
    try {
      const announcementsRef = collection(db, 'announcements');
      
      const newAnnouncement = {
        ...announcementForm,
        createdAt: new Date().toISOString(),
        authorName: 'Julieanna Mac'
      };

      // If a book is selected, add the book title
      if (announcementForm.bookId) {
        const selectedBook = books.find(b => b.id === announcementForm.bookId);
        if (selectedBook) {
          newAnnouncement.bookTitle = selectedBook.title;
        }
      }

      await addDoc(announcementsRef, newAnnouncement);
      
      // Reset form
      setAnnouncementForm({
        title: '',
        message: '',
        arcSendDate: '',
        releaseDate: '',
        reviewDeadline: '',
        bookId: ''
      });
      
      alert('Announcement posted successfully! 🎉');
    } catch (err) {
      alert('Error creating announcement: ' + err.message);
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await deleteDoc(doc(db, 'announcements', announcementId));
      alert('Announcement deleted!');
    } catch (err) {
      alert('Error deleting announcement: ' + err.message);
    }
  };

  /* ----------------------------------------
     COMMUNITY FEED HANDLERS
  ---------------------------------------- */
  const handleCreateAnnouncementPost = async (e) => {
    e.preventDefault();
    
    if (!announcementPostForm.content.trim()) {
      alert('Please enter some content');
      return;
    }

    try {
      await addDoc(collection(db, 'posts'), {
        type: 'announcement',
        authorId: 'admin',
        authorName: 'Julieanna Mac',
        content: announcementPostForm.content,
        isPinned: false,
        createdAt: serverTimestamp(),
      });

      setAnnouncementPostForm({ content: '' });
      setShowCreateAnnouncement(false);
      alert('✨ Announcement post created successfully!');
    } catch (error) {
      alert('Error creating post: ' + error.message);
    }
  };

  const handleTogglePin = async (postId, currentPinStatus) => {
    try {
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        isPinned: !currentPinStatus
      });
      
      const action = !currentPinStatus ? 'pinned' : 'unpinned';
      alert(`Post ${action} successfully!`);
    } catch (error) {
      alert('Error updating post: ' + error.message);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post? This will also delete all comments and likes.')) return;

    try {
      // Delete the post
      await deleteDoc(doc(db, 'posts', postId));
      
      // Delete all comments for this post
      const commentsQuery = query(collection(db, 'postComments'), where('postId', '==', postId));
      const commentsSnap = await getDocs(commentsQuery);
      const commentDeletePromises = commentsSnap.docs.map(d => deleteDoc(d.ref));
      await Promise.all(commentDeletePromises);
      
      // Delete all likes for this post
      const likesQuery = query(collection(db, 'postLikes'), where('postId', '==', postId));
      const likesSnap = await getDocs(likesQuery);
      const likeDeletePromises = likesSnap.docs.map(d => deleteDoc(d.ref));
      await Promise.all(likeDeletePromises);
      
      alert('Post deleted successfully!');
    } catch (error) {
      alert('Error deleting post: ' + error.message);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await deleteDoc(doc(db, 'postComments', commentId));
      alert('Comment deleted');
    } catch (error) {
      alert('Error deleting comment: ' + error.message);
    }
  };

  /* ── DM subscription moved to MessagesTab component ── */

  const handleAuthorCommentReply = async (postId) => {
    if (!currentUser || !authorCommentInput[postId]?.trim()) return;
    try {
      await addDoc(collection(db, 'postComments'), {
        postId,
        readerId: 'author',
        readerName: 'Julie McNairn',
        isAuthor: true,
        content: authorCommentInput[postId].trim(),
        createdAt: serverTimestamp(),
      });
      setAuthorCommentInput({ ...authorCommentInput, [postId]: '' });
    } catch (err) { alert('Error posting reply: ' + err.message); }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const now = new Date();
    const postDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - postDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return postDate.toLocaleDateString();
  };

  /* ----------------------------------------
     FILTERED READERS
  ---------------------------------------- */
  const filteredReaders = readers.filter((r) => {
    const term = searchTerm.toLowerCase();
    const match =
      r.name?.toLowerCase().includes(term) ||
      r.emailCorrespondence?.toLowerCase().includes(term) ||
      r.socialUsernames?.toLowerCase().includes(term) ||
      (Array.isArray(r.reviewPlatforms)
        ? r.reviewPlatforms.join(" ").toLowerCase().includes(term)
        : false);

 const matchStatus = filterStatus === "All" || r.status === filterStatus;
    const matchSource = filterSource === "All" || r.source === filterSource;
    const matchArc = 
      filterArc === "All" || 
      (filterArc === "Has ARC" && r.hasApprovedArcRequest) ||
      (filterArc === "No ARC" && !r.hasApprovedArcRequest);
    
    const matchEReader = 
      filterEReader === "All" ||
      (filterEReader === "Has Email" && r.eReaderEmail && r.eReaderEmail.trim() !== "") ||
      (filterEReader === "Missing" && (!r.eReaderEmail || r.eReaderEmail.trim() === "")) ||
      (filterEReader === "Duplicate" && r.eReaderEmail && r.eReaderEmail.toLowerCase() === (r.emailCorrespondence || "").toLowerCase());

    const matchRating =
      filterRating === "All" ||
      (filterRating === "5" && Number(r.reviewRating) === 5) ||
      (filterRating === "4" && Number(r.reviewRating) === 4) ||
      (filterRating === "3" && Number(r.reviewRating) === 3) ||
      (filterRating === "unrated" && !r.reviewRating);

    return match && matchStatus && matchSource && matchArc && matchEReader && matchRating;
  });

  const approvedReviews = reviews.filter((r) => r.status === "approved");
  const approvedReviewsWithRating = approvedReviews.filter((r) => r.rating);

  const stats = {
    totalReaders: readers.length,
    arcsSent: readers.filter((r) =>
      ["ARC sent", "Reviewed", "Street Team", "Book 2 ARC interest"].includes(r.status) ||
      (r.status === "Closed" && r.booksReceived && r.booksReceived.length > 0)
    ).length,
    reviewed: approvedReviews.length,
    avgRating:
      approvedReviewsWithRating.length > 0
        ? (
            approvedReviewsWithRating.reduce((sum, r) => sum + Number(r.rating || 0), 0) /
            approvedReviewsWithRating.length
          ).toFixed(1)
        : "N/A",
  };
  /* ----------------------------------------
     RENDER LOGIC (LOADING / LOGIN)
  ---------------------------------------- */

  if (isLoadingAuth || (currentUser && !claimsChecked)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-teal-400 border-t-transparent" />
          <p className="text-xs font-medium text-slate-100">
            Loading your secure ARC CRM…
          </p>
        </div>
      </div>
    );
  }

  // Admin route guard: a signed-in NON-superadmin must never see the CRM. The
  // redirect effect above sends them to /author or /reader; until the browser
  // navigates, show a neutral interstitial (never the dashboard).
  if (currentUser && !isSuperadminUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-teal-400 border-t-transparent" />
          <p className="text-xs font-medium text-slate-100">
            Taking you to your account…
          </p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4">
        <div className="w-full max-w-md rounded-3xl border border-slate-200/40 bg-gradient-to-b from-slate-50/90 via-white to-slate-100/60 p-8 shadow-xl">

          {/* LOGIN UI */}
          <div className="mb-6 flex flex-col items-center gap-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-500/90 text-white shadow-md">
              <Lock className="h-6 w-6" />
            </div>
            <h1 className="text-lg font-semibold text-slate-900">
              Narratoria ARC Reader CRM
            </h1>
            <p className="text-xs text-slate-500">
              Secure login for your private ARC dashboard
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">
                Email
              </Label>
              <Input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none shadow-sm focus:border-slate-500 focus:ring-2 focus:ring-teal-300"
              />
            </div>

            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">
                Password
              </Label>
              <Input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none shadow-sm focus:border-slate-500 focus:ring-2 focus:ring-teal-300"
              />
            </div>

            {loginError && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {loginError}
              </p>
            )}

            <Button
              type="submit"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-teal-700"
            >
              <Shield className="h-4 w-4" />
              Sign in
            </Button>
          </form>
        </div>
      </div>
    );
  }

  /* ----------------------------------------
     MAIN DASHBOARD
  ---------------------------------------- */

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-slate-50 to-white text-slate-900">

      {/* SIDEBAR */}
      <aside className="hidden w-60 flex-col border-r border-slate-100 bg-gradient-to-b from-white via-slate-50 to-slate-100/60 px-4 py-5 shadow-sm md:flex">

        <div className="mb-8 flex items-center gap-3 px-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-500 text-white shadow-sm">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              Narratoria
            </p>
            <p className="text-[11px] text-slate-700">ARC Reader CRM</p>
          </div>
        </div>

        <nav className="space-y-1 text-xs">
          <Button
            variant="ghost"
            onClick={() => setActiveTab("insights")}
            className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
              activeTab === "insights"
                ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-md"
                : "text-purple-700 hover:bg-purple-50"
            }`}
          >
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> AI Insights
            </span>
            <Badge className="bg-white/20 text-[10px] border-0 px-1.5">NEW</Badge>
          </Button>

          <div className="my-1.5 border-t border-slate-100/60" />

          <Button
            variant="ghost"
            onClick={() => setActiveTab("readers")}
            className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
              activeTab === "readers"
                ? "bg-teal-600 text-white"
                : "text-slate-700 hover:bg-slate-100/70"
            }`}
          >
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4" /> Readers
            </span>
            <span className="rounded-full bg-white/20 px-2 text-[10px]">{readers.length}</span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => setActiveTab("reviews")}
            className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
              activeTab === "reviews"
                ? "bg-teal-600 text-white"
                : "text-slate-700 hover:bg-slate-100/70"
            }`}
          >
            <span className="flex items-center gap-2">
              <Star className="h-4 w-4" /> Reviews
            </span>
            <span className="rounded-full bg-white/20 px-2 text-[10px]">
              {reviews.length}
            </span>
          </Button>

<Button
  variant="ghost"
  onClick={() => setActiveTab("arc-requests")}
  className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
    activeTab === "arc-requests"
      ? "bg-teal-600 text-white shadow-md"
      : "text-slate-600 hover:bg-slate-50 hover:text-teal-700"
  }`}
>
  <span className="flex items-center gap-2">
    <Mail size={16} />
    ARC Requests
  </span>
  {arcRequests.filter(r => r.status === 'pending').length > 0 && (
    <span className="rounded-full bg-white/20 px-2 text-[10px]">
      {arcRequests.filter(r => r.status === 'pending').length}
    </span>
  )}
</Button>

          <Button
            variant="ghost"
            onClick={() => setActiveTab("books")}
            className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
              activeTab === "books"
                ? "bg-teal-600 text-white"
                : "text-slate-700 hover:bg-slate-100/70"
            }`}
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> Books
            </span>
            <span className="rounded-full bg-white/20 px-2 text-[10px]">{books.length}</span>
          </Button>

          <Button
            variant="ghost"
            onClick={() => { setActiveTab("book-database"); loadBookDb(); }}
            className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
              activeTab === "book-database"
                ? "bg-teal-600 text-white"
                : "text-slate-700 hover:bg-slate-100/70"
            }`}
          >
            <span className="flex items-center gap-2">
              <Tag className="h-4 w-4" /> Book Database
            </span>
            <span className="rounded-full bg-white/20 px-2 text-[10px]">{bookDbList.length}</span>
          </Button>

          <Button
  variant="ghost"
  onClick={() => window.open('/?tool=publishing', '_blank')}
  className="h-auto flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-colors text-slate-600 hover:bg-slate-50 hover:text-teal-700"
>
  <BookOpen size={16} />
  <span>Publishing</span>
</Button>

<Button
              variant="ghost"
              onClick={() => setActiveTab("rewards")}
              className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
                activeTab === "rewards"
                  ? "bg-teal-600 text-white"
                  : "text-slate-700 hover:bg-slate-100/70"
              }`}
            >
              <span className="flex items-center gap-2">
                <Gift className="h-4 w-4" />
                Rewards
              </span>
              <span className="rounded-full bg-white/20 px-2 text-[10px]">{rewards.length}</span>
            </Button>

          <Button
            variant="ghost"
            onClick={() => setActiveTab("announcements")}
            className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
              activeTab === "announcements"
                ? "bg-teal-600 text-white"
                : "text-slate-700 hover:bg-slate-100/70"
            }`}
          >
            <span className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Announcements
            </span>
            <span className="rounded-full bg-white/20 px-2 text-[10px]">{announcements.length}</span>
          </Button>

          {/* Community Feed Tab */}
          <Button
            variant="ghost"
            onClick={() => setActiveTab("community")}
            className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
              activeTab === "community"
                ? "bg-teal-600 text-white"
                : "text-slate-700 hover:bg-slate-100/70"
            }`}
          >
            <span className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Community
            </span>
            <span className="rounded-full bg-white/20 px-2 text-[10px]">{posts.length}</span>
          </Button>

          {/* Direct Messages Tab */}
          <Button
            variant="ghost"
            onClick={() => setActiveTab("messages")}
            className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 ${
              activeTab === "messages"
                ? "bg-teal-600 text-white"
                : "text-slate-700 hover:bg-slate-100/70"
            }`}
          >
            <span className="flex items-center gap-2">
              <Inbox className="h-4 w-4" />
              Messages
            </span>
            {dmUnreadTotal > 0 && (
              <span className="rounded-full bg-red-500 px-2 text-[10px] font-bold text-white">{dmUnreadTotal}</span>
            )}
          </Button>

            <Button
  variant="ghost"
  onClick={() => setActiveTab("duplicates")}
  className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 text-left font-medium transition-colors ${
    activeTab === "duplicates"
      ? "bg-teal-600 text-white shadow-md"
      : "text-slate-700 hover:bg-slate-100/70"
  }`}
>

  <span className="flex items-center gap-2">
    <LinkIcon className="h-4 w-4" />
    Merge Duplicates
  </span>
</Button>
<Button
  variant="ghost"
  onClick={() => setActiveTab("orders")}
  className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 text-left font-medium transition-colors ${
    activeTab === "orders"
      ? "bg-teal-600 text-white shadow-md"
      : "text-slate-700 hover:bg-slate-100/70"
  }`}
>
  <span className="flex items-center gap-2">
    <Package className="h-4 w-4" />
    Orders
  </span>
</Button>
<Button
  variant="ghost"
  onClick={() => { setActiveTab("email-log"); loadEmailLog(); }}
  className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 text-left font-medium transition-colors ${
    activeTab === "email-log"
      ? "bg-teal-600 text-white shadow-md"
      : "text-slate-700 hover:bg-slate-100/70"
  }`}
>
  <span className="flex items-center gap-2">
    <Mail className="h-4 w-4" />
    Email Log
  </span>
</Button>
<Button
  variant="ghost"
  onClick={() => setActiveTab("settings")}
  className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 text-left font-medium transition-colors ${
    activeTab === "settings"
      ? "bg-teal-600 text-white shadow-md"
      : "text-slate-700 hover:bg-slate-100/70"
  }`}
>
  <span className="flex items-center gap-2">
    <Settings className="h-4 w-4" />
    Settings
  </span>
</Button>
<Button
  variant="ghost"
  onClick={() => setActiveTab("platform")}
  className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 text-left font-medium transition-colors ${
    activeTab === "platform"
      ? "bg-teal-600 text-white shadow-md"
      : "text-slate-700 hover:bg-slate-100/70"
  }`}
>
  <span className="flex items-center gap-2">
    <Shield className="h-4 w-4" />
    Platform
  </span>
</Button>
<Button
  variant="ghost"
  onClick={() => setActiveTab("licences")}
  className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 text-left font-medium transition-colors ${
    activeTab === "licences"
      ? "bg-teal-600 text-white shadow-md"
      : "text-slate-700 hover:bg-slate-100/70"
  }`}
>
  <span className="flex items-center gap-2">
    <KeyRound className="h-4 w-4" />
    Roadmap Licences
  </span>
  <span className="rounded-full bg-white/20 px-2 text-[10px]">{licences.length}</span>
</Button>
<Button
  variant="ghost"
  onClick={() => setActiveTab("testing")}
  className={`h-auto flex w-full items-center justify-between rounded-lg px-3 py-2 text-left font-medium transition-colors ${
    activeTab === "testing"
      ? "bg-teal-600 text-white shadow-md"
      : "text-slate-700 hover:bg-slate-100/70"
  }`}
>
  <span className="flex items-center gap-2">
    <FlaskConical className="h-4 w-4" />
    Testing
  </span>
  {roadmapFeedback.filter((f) => (f.status || "new") === "new").length > 0 && (
    <span className="rounded-full bg-red-500 px-2 text-[10px] font-bold text-white">
      {roadmapFeedback.filter((f) => (f.status || "new") === "new").length}
    </span>
  )}
</Button>
        </nav>

        <div className="mt-auto border-t border-slate-100 pt-4 text-[10px] text-slate-500">
          <p className="flex items-center gap-2">
            <Shield className="h-3 w-3 text-emerald-500" />
            GDPR aware • Cloud synced
          </p>
          <p className="mt-2">
            Built for authors who want a calm, professional overview of ARC readers.
          </p>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 px-4 py-4 md:px-8 md:py-6">

        {/* HEADER */}
        <header className="mb-5 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-teal-700">
              Dashboard
            </p>
            <h1 className="text-lg font-semibold text-slate-900">ARC Reader Overview</h1>
            <p className="text-[11px] text-slate-500">
              Cloud-based tracker for your ARC readers, reviews & books.
            </p>
          </div>

          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-slate-700 shadow-sm hover:border-teal-400 hover:text-teal-700"
          >
            <LogOut className="h-3.5 w-3.5" />
            Logout
          </Button>
        </header>

        {/* STATS */}
        <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Readers" value={stats.totalReaders} icon={Mail} accent="bg-slate-500/15 text-teal-700" />
          <StatCard label="ARCs Sent" value={stats.arcsSent} icon={BookOpen} accent="bg-sky-500/15 text-sky-700" />
          <StatCard label="Reviewed" value={stats.reviewed} icon={Star} accent="bg-emerald-500/15 text-emerald-700" />
          <StatCard label="Average Rating" value={stats.avgRating} icon={Star} accent="bg-violet-500/15 text-violet-700" />
        </section>

        {/* ----------------------------------------
             AI INSIGHTS TAB
        ---------------------------------------- */}
{activeTab === "insights" && (
  <div className="px-4 py-4 md:px-6">
    <div className="mx-auto max-w-5xl">
      <ReaderInsights readers={readers} reviews={reviews} books={books} />
    </div>
  </div>
)}

        {/* ----------------------------------------
             READERS TAB
        ---------------------------------------- */}
{activeTab === "readers" && (
  <>
    {/* READERS TOOLBAR */}
    <section className="mb-4 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* SEARCH */}
        <div className="flex flex-1 items-center gap-3">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, username or platform…"
              className="w-full rounded-lg border border-slate-200 bg-white px-7 py-1.5 text-[11px] text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            />
          </div>

          {/* FILTER */}
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option value="All">All statuses</option>
              <option value="Noted ARC interest">Noted ARC interest</option>
              <option value="Engaged">Engaged</option>
              <option value="Follow-up needed">Follow-up needed</option>
              <option value="ARC sent">ARC sent</option>
              <option value="Reviewed">Reviewed</option>
              <option value="Closed">Closed</option>
              <option value="Street Team">Street Team</option>
              <option value="Book 2 ARC interest">Book 2 ARC interest</option>
              <option value="New Buyer">New Buyer</option>
</select>

            {/* SOURCE FILTER */}
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option value="All">All sources</option>
              <option value="reader_portal">Portal only</option>
              <option value="manual">Manual only</option>
            </select>

            {/* ARC FILTER */}
            <select
              value={filterArc}
              onChange={(e) => setFilterArc(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option value="All">All ARCs</option>
              <option value="Has ARC">Has approved ARC</option>
              <option value="No ARC">No ARC</option>
            </select>

            {/* EREADER EMAIL FILTER */}
            <select
              value={filterEReader}
              onChange={(e) => setFilterEReader(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option value="All">All Kindle emails</option>
              <option value="Has Email">✅ Has Kindle email</option>
              <option value="Missing">❌ Missing</option>
              <option value="Duplicate">⚠️ Needs check</option>
            </select>

            {/* STAR RATING FILTER */}
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-800 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option value="All">All ratings</option>
              <option value="5">⭐⭐⭐⭐⭐ 5 stars</option>
              <option value="4">⭐⭐⭐⭐ 4 stars</option>
              <option value="3">⭐⭐⭐ 3 stars</option>
              <option value="unrated">No rating yet</option>
            </select>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={openNewReaderDrawer}
            className="flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-teal-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Reader
          </button>

<button
  onClick={() => setShowQuickAddModal(true)}
  className="flex items-center gap-1 rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-[11px] font-medium text-purple-800 hover:bg-purple-100"
>
  <Mail className="h-3.5 w-3.5" />
  Quick Add
</button>

          <button
            onClick={exportToCSV}
            className="flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100"
          >
            <Download className="h-3.5 w-3.5" />
            CSV
          </button>

          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-medium text-blue-800 hover:bg-blue-100"
          >
            <Upload className="h-3.5 w-3.5" />
            Upload
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={(e) => {}}
            className="hidden"
          />
        </div>
      </div>
    </section>

    {/* GROUPED BY BOOK */}
    <section className="space-y-3">
      {isLoadingReaders ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center text-[11px] text-slate-500">
          Loading readers…
        </div>
      ) : (
        <>
          {/* RENDER EACH BOOK */}
          {books.map((book) => {
            // Get readers for this book
            const bookReaders = filteredReaders.filter(r => 
              (r.booksReceived || []).includes(book.id)
            );

            if (bookReaders.length === 0 && searchTerm === '' && filterStatus === 'All') {
              return null; // Don't show empty books unless filtering
            }

            const isCollapsed = collapsedBooks[book.id];
            const reviewedCount = bookReaders.filter(r => r.status === 'Reviewed').length;

            return (
              <div key={book.id} className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
                {/* BOOK HEADER */}
                <button
                  onClick={() => setCollapsedBooks({
                    ...collapsedBooks,
                    [book.id]: !isCollapsed
                  })}
                  className="w-full bg-gradient-to-r from-slate-50 to-slate-100/60 px-4 py-3 text-left hover:from-slate-100 hover:to-slate-200/60 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BookOpen className="h-4 w-4 text-teal-600" />
                      <div>
                        <h3 className="text-[13px] font-bold text-slate-900">{book.title}</h3>
                        {book.series && (
                          <p className="text-[10px] text-slate-500">{book.series}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-[10px] text-slate-500">Readers</p>
                        <p className="text-[13px] font-bold text-slate-900">{bookReaders.length}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-slate-500">Reviewed</p>
                        <p className="text-[13px] font-bold text-emerald-600">{reviewedCount}</p>
                      </div>
                      <div className="text-slate-400">
                        {isCollapsed ? '▼' : '▲'}
                      </div>
                    </div>
                  </div>
                </button>

                {/* READERS TABLE */}
                {!isCollapsed && (
                  <div className="overflow-x-auto">
                    {bookReaders.length === 0 ? (
                      <div className="px-4 py-6 text-center text-[11px] text-slate-500">
                        No readers assigned to this book yet.
                      </div>
                    ) : (
                      <table className="min-w-full text-[11px]">
<thead className="bg-slate-50">
  <tr>
    <th className="px-3 py-2 text-left">Reader</th>
    <th className="px-3 py-2 text-left">Kindle Email</th>
    <th className="px-3 py-2 text-left">Status</th>
    <th className="px-3 py-2 text-left">Rating</th>
    <th className="px-3 py-2 text-center">ARC</th>
    <th className="px-3 py-2 text-left">Points</th>
    <th className="px-3 py-2 text-right">Actions</th>
  </tr>
</thead>

                        <tbody>
                          {bookReaders.map((r) => (
                            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60">
<td 
  className="px-3 py-2 align-top cursor-pointer hover:bg-slate-50/50 transition-colors"
  onClick={() => openReaderDetailPanel(r)}
>
  <div className="flex items-center gap-2">
    <div>
      <p className="font-medium text-teal-700 hover:text-slate-900">{r.name || "Unnamed"}</p>
      <p className="text-[10px] text-slate-500">{r.emailCorrespondence}</p>
      {r.location && <p className="text-[10px] text-slate-400">{r.location}</p>}
    </div>
    <SourceBadge source={r.source} />
  </div>
</td>

<td className="px-3 py-2 align-top">
  <div className="flex items-center gap-1.5">
    <EReaderEmailIcon 
      emailCorrespondence={r.emailCorrespondence}
      eReaderEmail={r.eReaderEmail}
    />
    <span className="text-[10px] text-slate-600 truncate max-w-[120px]">
      {r.eReaderEmail || <span className="text-slate-400 italic">missing</span>}
    </span>
  </div>
</td>

<td className="px-3 py-2 align-top">
  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(r.status)}`}>
    {r.status}
  </span>
</td>

                              <td className="px-3 py-2 align-top">
                                {r.reviewRating ? (
                                  <div className="flex items-center gap-1">
                                    <span className="flex">
                                      {[1,2,3,4,5].map(star => (
                                        <Star
                                          key={star}
                                          className={`h-3 w-3 ${Number(r.reviewRating) >= star ? 'text-teal-400 fill-teal-400' : 'text-slate-200 fill-slate-200'}`}
                                        />
                                      ))}
                                    </span>
                                    <span className="text-[10px] font-semibold text-slate-700">{r.reviewRating}</span>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-slate-400 italic">No rating</span>
                                )}
                              </td>

<td className="px-3 py-2 align-top text-center">
  {r.hasApprovedArcRequest ? (
    <span className="inline-flex items-center justify-center rounded-full bg-emerald-100 p-1" title="ARC Approved">
      <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
    </span>
  ) : (
    <span className="text-slate-300">—</span>
  )}
</td>

                              <td className="px-3 py-2 align-top">
                                {r.totalPoints || 0}
                              </td>

                              <td className="px-3 py-2 align-top text-right">
  <div className="inline-flex gap-1">
    {/* Quick Add Points */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        const amount = prompt('How many points to add?', '10');
        const reason = prompt('Reason for adding points?', 'Quick bonus');
        if (amount && reason) {
          handleAddPoints(r.id, Number(amount), reason);
        }
      }}
      className="rounded-full border border-green-200 bg-green-50 p-1 text-green-600 hover:bg-green-100"
      title="Quick add points"
    >
      <Plus className="h-3.5 w-3.5" />
    </button>

    {/* Quick Deduct Points */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        const amount = prompt('How many points to deduct?', '10');
        const reason = prompt('Reason for deducting points?', 'Penalty');
        if (amount && reason) {
          handleDeductPoints(r.id, Number(amount), reason);
        }
      }}
      className="rounded-full border border-red-200 bg-red-50 p-1 text-red-600 hover:bg-red-100"
      title="Quick deduct points"
    >
      <Minus className="h-3.5 w-3.5" />
    </button>

    {/* Edit Reader */}
    <button
      onClick={() => openEditReaderDrawer(r)}
      className="rounded-full border border-slate-200 bg-white p-1 text-slate-600 hover:text-teal-700"
    >
      <Edit2 className="h-3.5 w-3.5" />
    </button>

    {/* Delete Reader */}
    <button
      onClick={() => handleDeleteReader(r.id)}
      className="rounded-full border border-rose-200 bg-rose-50 p-1 text-rose-600 hover:bg-rose-100"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  </div>
</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* UNASSIGNED READERS */}
          {(() => {
            const unassignedReaders = filteredReaders.filter(r => 
              !r.booksReceived || r.booksReceived.length === 0
            );

            if (unassignedReaders.length === 0) return null;

            const isCollapsed = collapsedBooks['unassigned'];

            return (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                {/* UNASSIGNED HEADER */}
                <button
                  onClick={() => setCollapsedBooks({
                    ...collapsedBooks,
                    'unassigned': !isCollapsed
                  })}
                  className="w-full bg-gradient-to-r from-slate-50 to-slate-100/60 px-4 py-3 text-left hover:from-slate-100 hover:to-slate-200/60 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-slate-500" />
                      <div>
                        <h3 className="text-[13px] font-bold text-slate-900">Unassigned Readers</h3>
                        <p className="text-[10px] text-slate-500">Not assigned to any book yet</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-[10px] text-slate-500">Readers</p>
                        <p className="text-[13px] font-bold text-slate-900">{unassignedReaders.length}</p>
                      </div>
                      <div className="text-slate-400">
                        {isCollapsed ? '▼' : '▲'}
                      </div>
                    </div>
                  </div>
                </button>

                {/* UNASSIGNED TABLE */}
                {!isCollapsed && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-[11px]">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Reader</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Rating</th>
                          <th className="px-3 py-2 text-left">Points</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {unassignedReaders.map((r) => (
                          <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                            <td className="px-3 py-2 align-top">
                              <p className="font-medium">{r.name || "Unnamed"}</p>
                              <p className="text-[10px] text-slate-500">{r.emailCorrespondence}</p>
                              {r.location && <p className="text-[10px] text-slate-400">{r.location}</p>}
                            </td>

                           <td className="px-3 py-2 align-top">
  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(r.status)}`}>
    {r.status}
  </span>
</td>

                            <td className="px-3 py-2 align-top">
                              {r.reviewRating ? (
                                <div className="flex items-center gap-1">
                                  <span className="flex">
                                    {[1,2,3,4,5].map(star => (
                                      <Star
                                        key={star}
                                        className={`h-3 w-3 ${Number(r.reviewRating) >= star ? 'text-teal-400 fill-teal-400' : 'text-slate-200 fill-slate-200'}`}
                                      />
                                    ))}
                                  </span>
                                  <span className="text-[10px] font-semibold text-slate-700">{r.reviewRating}</span>
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-400 italic">No rating</span>
                              )}
                            </td>

                            <td className="px-3 py-2 align-top">
                              {r.totalPoints || 0}
                            </td>

                           <td className="px-3 py-2 align-top text-right">
  <div className="inline-flex gap-1">
    {/* Quick Add Points */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        const amount = prompt('How many points to add?', '10');
        const reason = prompt('Reason for adding points?', 'Quick bonus');
        if (amount && reason) {
          handleAddPoints(r.id, Number(amount), reason);
        }
      }}
      className="rounded-full border border-green-200 bg-green-50 p-1 text-green-600 hover:bg-green-100"
      title="Quick add points"
    >
      <Plus className="h-3.5 w-3.5" />
    </button>

    {/* Quick Deduct Points */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        const amount = prompt('How many points to deduct?', '10');
        const reason = prompt('Reason for deducting points?', 'Penalty');
        if (amount && reason) {
          handleDeductPoints(r.id, Number(amount), reason);
        }
      }}
      className="rounded-full border border-red-200 bg-red-50 p-1 text-red-600 hover:bg-red-100"
      title="Quick deduct points"
    >
      <Minus className="h-3.5 w-3.5" />
    </button>

    {/* Edit Reader */}
    <button
      onClick={() => openEditReaderDrawer(r)}
      className="rounded-full border border-slate-200 bg-white p-1 text-slate-600 hover:text-teal-700"
    >
      <Edit2 className="h-3.5 w-3.5" />
    </button>

    {/* Delete Reader */}
    <button
      onClick={() => handleDeleteReader(r.id)}
      className="rounded-full border border-rose-200 bg-rose-50 p-1 text-rose-600 hover:bg-rose-100"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  </div>
</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* NO RESULTS MESSAGE */}
          {filteredReaders.length === 0 && (
            <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center text-[11px] text-slate-500">
              No readers found matching your search.
            </div>
          )}
        </>
      )}
    </section>

{/* ----------------------------------------
     READER DETAIL PANEL (NEW)
---------------------------------------- */}
{showReaderDetailPanel && viewingReader && (
  <div className="fixed inset-0 z-50 flex items-center justify-end bg-slate-900/30 backdrop-blur-sm">
    <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-slate-100 bg-white shadow-2xl">
      
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
<div className="flex items-center gap-3">
  <h2 className="text-2xl font-bold text-slate-900">{viewingReader.name || 'Unnamed Reader'}</h2>
  <SourceBadge source={viewingReader.source} />
</div>
            <p className="text-sm text-slate-500">{viewingReader.emailCorrespondence}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={switchToEditFromDetail}
              className="flex items-center gap-2 rounded-lg border border-teal-300 bg-slate-50 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-slate-100"
            >
              <Edit2 className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={closeReaderDetailPanel}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-500">Total Reviews</p>
              <Star className="h-5 w-5 text-teal-600" />
            </div>
            <p className="text-3xl font-bold text-slate-900">{readerReviews.length}</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-500">Total Points</p>
              <Gift className="h-5 w-5 text-teal-600" />
            </div>
            <p className="text-3xl font-bold text-slate-900">{viewingReader.totalPoints || 0}</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-slate-500">Books Received</p>
              <BookOpen className="h-5 w-5 text-teal-600" />
            </div>
            <p className="text-3xl font-bold text-slate-900">{(viewingReader.booksReceived || []).length}</p>
          </div>
        </div>

        {/* Basic Information */}
        <div className="rounded-xl border border-slate-100 bg-white p-5">
          <h3 className="mb-4 text-lg font-bold text-slate-900">📋 Basic Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Name</p>
              <p className="text-slate-900">{viewingReader.name || '—'}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Status</p>
              <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-teal-700">
                {viewingReader.status || 'Noted ARC interest'}
              </span>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Email (correspondence)</p>
              <p className="text-slate-900">{viewingReader.emailCorrespondence || '—'}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">eReader Email</p>
              <div className="flex items-center gap-2">
                <p className="text-slate-900">{viewingReader.eReaderEmail || '—'}</p>
                <EReaderEmailIcon
                  emailCorrespondence={viewingReader.emailCorrespondence}
                  eReaderEmail={viewingReader.eReaderEmail}
                />
              </div>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Phone Number</p>
              <p className="text-slate-900">{viewingReader.phoneNumber || '—'}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">First Name</p>
              <p className="text-slate-900">{viewingReader.firstName || '—'}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Last Name</p>
              <p className="text-slate-900">{viewingReader.lastName || '—'}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Location</p>
              <p className="text-slate-900">{viewingReader.location || '—'}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Signup Date</p>
              <p className="text-slate-900">{viewingReader.signupDate || '—'}</p>
            </div>
          </div>
        </div>

        {/* 📦 Shipping Address (NEW) */}
        {viewingReader.shippingAddress && (
          <div className="rounded-xl border border-slate-100 bg-white p-5">
            <h3 className="mb-4 text-lg font-bold text-slate-900">📦 Shipping Address</h3>
            <div className="rounded-lg bg-slate-50 p-4">
              <p className="text-sm text-slate-900 leading-relaxed">
                {viewingReader.shippingAddress.line1 && <>{viewingReader.shippingAddress.line1}<br /></>}
                {viewingReader.shippingAddress.line2 && <>{viewingReader.shippingAddress.line2}<br /></>}
                {viewingReader.shippingAddress.city && <>{viewingReader.shippingAddress.city} </>}
                {viewingReader.shippingAddress.postalCode && <>{viewingReader.shippingAddress.postalCode}<br /></>}
                {viewingReader.shippingAddress.country && <>{viewingReader.shippingAddress.country}</>}
                {!viewingReader.shippingAddress.line1 && <span className="text-slate-400">No address provided</span>}
              </p>
            </div>
          </div>
        )}

        {/* 📱 Social Media & Review Platforms (NEW) */}
        <div className="rounded-xl border border-slate-100 bg-white p-5">
          <h3 className="mb-4 text-lg font-bold text-slate-900">📱 Social Media & Review Platforms</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">📷 Instagram</p>
              {viewingReader.socialMedia?.instagram ? (
                <a 
                  href={`https://instagram.com/${viewingReader.socialMedia.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:text-slate-800 flex items-center gap-1"
                >
                  {viewingReader.socialMedia.instagram}
                  <ExternalLink size={12} />
                </a>
              ) : (
                <p className="text-slate-400">—</p>
              )}
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">📱 TikTok</p>
              {viewingReader.socialMedia?.tiktok ? (
                <a 
                  href={`https://tiktok.com/@${viewingReader.socialMedia.tiktok.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:text-slate-800 flex items-center gap-1"
                >
                  {viewingReader.socialMedia.tiktok}
                  <ExternalLink size={12} />
                </a>
              ) : (
                <p className="text-slate-400">—</p>
              )}
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">📚 Goodreads</p>
              <p className="text-slate-900">{viewingReader.socialMedia?.goodreads || '—'}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">📦 Amazon</p>
              <p className="text-slate-900">{viewingReader.socialMedia?.amazon || '—'}</p>
            </div>
            <div className="col-span-2">
              <p className="mb-1 text-xs font-medium text-slate-500">📝 Other Sites</p>
              <p className="text-slate-900">{viewingReader.socialMedia?.other || '—'}</p>
            </div>
          </div>
        </div>

        {/* 📚 Reading Preferences (NEW) */}
        {viewingReader.preferences && (
          <div className="rounded-xl border border-slate-100 bg-white p-5">
            <h3 className="mb-4 text-lg font-bold text-slate-900">📚 Reading Preferences</h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="mb-2 text-xs font-medium text-slate-500">Favorite Genres</p>
                <div className="flex flex-wrap gap-2">
                  {viewingReader.preferences.favoriteGenres?.length > 0 ? (
                    viewingReader.preferences.favoriteGenres.map(genre => (
                      <span key={genre} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-teal-700">
                        {genre}
                      </span>
                    ))
                  ) : (
                    <p className="text-slate-400">No genres selected</p>
                  )}
                </div>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-slate-500">Favorite Tropes</p>
                <p className="text-slate-900">{viewingReader.preferences.favoriteTropes || '—'}</p>
              </div>
            </div>
          </div>
        )}

{/* 📖 Reading History */}
<div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-lg font-bold text-slate-900">📖 Reading History</h3>
    <Button
      variant="outline"
      size="sm"
      onClick={() => setShowReaderHistoryImport(!showReaderHistoryImport)}
    >
      <Upload className="h-3.5 w-3.5 mr-1" />
      {showReaderHistoryImport ? 'Hide Import' : 'Import History'}
    </Button>
  </div>

  {/* Stats & Shelves */}
  {readerReadingHistory.length > 0 ? (
    <div className="space-y-4">
      {/* Shelf counts */}
      {(() => {
        const read = readerReadingHistory.filter(b => b.readStatus === 'read');
        const reading = readerReadingHistory.filter(b => b.readStatus === 'reading');
        const tbr = readerReadingHistory.filter(b => b.readStatus === 'to-read');
        const favourites = readerReadingHistory.filter(b => b.rating === 5);
        const loved = readerReadingHistory.filter(b => b.rating >= 4);
        return (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            <div className="rounded-lg bg-teal-50 border border-teal-200 p-2 text-center">
              <p className="text-lg font-bold text-teal-700">{readerReadingHistory.length}</p>
              <p className="text-[8px] text-teal-600 uppercase font-semibold">All Books</p>
            </div>
            <div className="rounded-lg bg-green-50 border border-green-200 p-2 text-center">
              <p className="text-lg font-bold text-green-700">{read.length}</p>
              <p className="text-[8px] text-green-600 uppercase font-semibold">Read</p>
            </div>
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-2 text-center">
              <p className="text-lg font-bold text-blue-700">{reading.length}</p>
              <p className="text-[8px] text-blue-600 uppercase font-semibold">Reading</p>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
              <p className="text-lg font-bold text-slate-700">{tbr.length}</p>
              <p className="text-[8px] text-slate-500 uppercase font-semibold">TBR</p>
            </div>
            <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-2 text-center">
              <p className="text-lg font-bold text-yellow-700">{favourites.length}</p>
              <p className="text-[8px] text-yellow-600 uppercase font-semibold">5-Star</p>
            </div>
          </div>
        );
      })()}

      {/* Top rated books */}
      {(() => {
        const topRated = readerReadingHistory
          .filter(b => b.rating >= 4)
          .sort((a, b) => (b.rating || 0) - (a.rating || 0))
          .slice(0, 5);
        if (topRated.length === 0) return null;
        return (
          <div>
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Top Rated Books</p>
            <div className="space-y-1">
              {topRated.map(b => (
                <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{b.title}</p>
                    <p className="text-[10px] text-slate-500">{b.author}</p>
                  </div>
                  <span className="text-slate-500 flex-shrink-0 ml-2">{'⭐'.repeat(b.rating)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* TBR List — high-value for ARC targeting */}
      {(() => {
        const tbr = readerReadingHistory
          .filter(b => b.readStatus === 'to-read')
          .slice(0, 10);
        if (tbr.length === 0) return null;
        return (
          <div>
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">TBR List (Want to Read)</p>
            <p className="text-[10px] text-slate-400 mb-2">Books this reader wants to read — target them for ARC invites on these titles.</p>
            <div className="space-y-1">
              {tbr.map(b => (
                <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-yellow-50/50 p-2 text-xs">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{b.title}</p>
                    <p className="text-[10px] text-slate-500">{b.author}</p>
                  </div>
                  <Badge className="bg-yellow-100 text-yellow-700 text-[9px]">TBR</Badge>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Taste Twins — readers with most overlap */}
      {(() => {
        if (readerReadingHistory.length < 3) return null;
        const myHighlyRated = new Set(
          readerReadingHistory.filter(b => b.rating >= 4).map(b => b.bookId).filter(Boolean)
        );
        if (myHighlyRated.size < 2) return null;

        // Check bookDatabase contributors for overlap
        const overlapCounts = {};
        for (const book of bookDbList) {
          if (myHighlyRated.has(book.id) && book.contributors) {
            for (const cId of book.contributors) {
              if (cId !== viewingReader.id) {
                overlapCounts[cId] = (overlapCounts[cId] || 0) + 1;
              }
            }
          }
        }
        const twins = Object.entries(overlapCounts)
          .filter(([, count]) => count >= 2)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([rid, count]) => {
            const r = readers.find(x => x.id === rid);
            return { id: rid, name: r?.name || r?.emailCorrespondence || rid, overlap: count };
          });
        if (twins.length === 0) return null;
        return (
          <div>
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Taste Twins</p>
            <div className="space-y-1">
              {twins.map(t => (
                <div key={t.id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs">
                  <span className="font-semibold text-slate-700 truncate">{t.name}</span>
                  <Badge variant="outline" className="text-[9px]">{t.overlap} shared favourites</Badge>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Import batches */}
      {readerImportBatches.length > 0 && (
        <div>
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Import History</p>
          <div className="space-y-1">
            {readerImportBatches.map(batch => (
              <div key={batch.batchId} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-2 text-xs">
                <div>
                  <span className="font-semibold text-slate-700">{batch.platform}</span>{' '}
                  <span className="text-slate-500">
                    — {batch.booksImported} books · {batch.importedAt?.toDate?.()?.toLocaleDateString() || '—'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ) : (
    <p className="text-sm text-slate-400 text-center py-3">No reading history imported yet.</p>
  )}

  {/* Import form */}
  {showReaderHistoryImport && (
    <div className="mt-4 pt-4 border-t border-slate-200">
      <ReadingHistoryImport
        isAdmin={true}
        readerId={viewingReader.id}
        existingBooks={bookDbList}
        allReaders={readers}
        adminUserId={currentUser?.uid}
        onComplete={() => loadReaderReadingHistory(viewingReader.id)}
      />
    </div>
  )}
</div>

{/* 📄 Reading Résumé */}
<div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
  <h3 className="mb-3 text-lg font-bold text-slate-900">📄 Reading Résumé</h3>
  <p className="text-xs text-slate-500 mb-3">Preview and download this reader's résumé — useful for evaluating ARC applications.</p>
  <ReaderResume readerData={viewingReader} isAdmin={true} />
</div>

{/* Manual Points & Rewards Management */}
<div className="mb-6 rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-6">
  <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-purple-900">
    <Gift className="h-5 w-5" />
    Manual Points & Rewards
  </h3>

  {/* Points Display */}
  <div className="mb-4 rounded-lg bg-white p-4">
    <p className="text-sm font-medium text-slate-600">Current Balance</p>
    <p className="text-3xl font-bold text-purple-600">
      {viewingReader.totalPoints || 0} points
    </p>
  </div>

  {/* Add Points */}
  <div className="mb-4 rounded-lg border-2 border-green-200 bg-white p-4">
    <p className="mb-3 text-sm font-bold text-green-900">➕ Add Bonus Points</p>
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Points Amount</label>
        <input
          type="number"
          min="1"
          placeholder="e.g., 50"
          value={pointsAmount}
          onChange={(e) => setPointsAmount(Number(e.target.value))}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
        <input
          type="text"
          placeholder="e.g., Great Instagram engagement"
          value={pointsReason}
          onChange={(e) => setPointsReason(e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </div>
      <button
        onClick={() => handleAddPoints(viewingReader.id, pointsAmount, pointsReason)}
        className="w-full rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2 text-sm font-bold text-white hover:from-green-700 hover:to-emerald-700"
      >
        Add {pointsAmount || 0} Points
      </button>
    </div>
  </div>

  {/* Deduct Points */}
  <div className="mb-4 rounded-lg border-2 border-red-200 bg-white p-4">
    <p className="mb-3 text-sm font-bold text-red-900">➖ Deduct Points</p>
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Points Amount</label>
        <input
          type="number"
          min="1"
          placeholder="e.g., 20"
          value={pointsAmount}
          onChange={(e) => setPointsAmount(Number(e.target.value))}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
        <input
          type="text"
          placeholder="e.g., Didn't post review as promised"
          value={pointsReason}
          onChange={(e) => setPointsReason(e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </div>
      <button
        onClick={() => handleDeductPoints(viewingReader.id, pointsAmount, pointsReason)}
        className="w-full rounded-lg bg-gradient-to-r from-red-600 to-rose-600 px-4 py-2 text-sm font-bold text-white hover:from-red-700 hover:to-rose-700"
      >
        Deduct {pointsAmount || 0} Points
      </button>
    </div>
  </div>

  {/* Give Manual Reward */}
  <div className="rounded-lg border-2 border-slate-200 bg-white p-4">
    <p className="mb-3 text-sm font-bold text-slate-900">🎁 Give Manual Reward</p>
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Select Reward</label>
        <select
          value={selectedRewardId}
          onChange={(e) => setSelectedRewardId(e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">Choose a reward...</option>
          {rewards.filter(r => r.available).map(reward => (
            <option key={reward.id} value={reward.id}>
              {reward.name} ({reward.pointCost} pts)
            </option>
          ))}
        </select>
      </div>
      <button
        onClick={() => handleGiveReward(viewingReader.id, selectedRewardId)}
        disabled={!selectedRewardId}
        className="w-full rounded-lg bg-gradient-to-r from-teal-600 to-orange-600 px-4 py-2 text-sm font-bold text-white hover:from-teal-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Give Reward (Free - No Points Deducted)
      </button>
    </div>
  </div>
</div>

{/* Points History */}
<div className="mb-6 rounded-2xl border border-slate-200 bg-white p-6">
  <h3 className="mb-4 text-lg font-bold text-slate-900">📊 Points History</h3>
  
  {pointsHistory.length === 0 ? (
    <p className="text-sm text-slate-500">No points history yet</p>
  ) : (
    <div className="space-y-2">
      {pointsHistory.map((entry) => (
        <div 
          key={entry.id} 
          className={`flex items-start justify-between rounded-lg border p-3 ${
            entry.amount > 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
          }`}
        >
          <div>
            <p className={`text-sm font-bold ${
              entry.amount > 0 ? 'text-green-900' : 'text-red-900'
            }`}>
              {entry.amount > 0 ? '+' : ''}{entry.amount} points
            </p>
            <p className="text-xs text-slate-600">{entry.reason}</p>
            <p className="text-xs text-slate-400">
              {entry.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
            </p>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            entry.type === 'manual_add' ? 'bg-green-200 text-green-800' :
            entry.type === 'manual_deduct' ? 'bg-red-200 text-red-800' :
            'bg-blue-200 text-blue-800'
          }`}>
            {entry.type === 'manual_add' ? 'Manual Add' :
             entry.type === 'manual_deduct' ? 'Manual Deduct' :
             'Review'}
          </span>
        </div>
      ))}
    </div>
  )}
</div>

        {/* ⭐ Review History (ENHANCED) */}
        <div className="rounded-xl border border-slate-100 bg-white p-5">
          <h3 className="mb-4 text-lg font-bold text-slate-900">⭐ Review History</h3>
          
          {readerReviews.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-8">No reviews submitted yet</p>
          ) : (
            <div className="space-y-4">
              {readerReviews.map((review) => (
                <div key={review.id} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-bold text-slate-900">{review.bookTitle}</h4>
                      <p className="text-xs text-slate-500">
                        {review.submittedAt?.toDate().toLocaleDateString()}
                      </p>
                    </div>
                    {review.pointsEarned && (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-teal-700">
                        +{review.pointsEarned} pts
                      </span>
                    )}
                  </div>

                  {/* ⭐ Star Rating (NEW) */}
                  {review.rating && (
                    <div className="mb-3">
                      <span className="text-2xl text-slate-500">
                        {'⭐'.repeat(review.rating)}
                      </span>
                    </div>
                  )}

                  {/* 📖 Quick Summary (NEW) */}
                  {review.quickSummary && (
                    <div className="mb-3 rounded-md bg-white p-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">Quick Summary:</p>
                      <p className="text-sm text-slate-700">{review.quickSummary}</p>
                    </div>
                  )}

                  {/* 💖 What They Loved (NEW) */}
                  {review.lovedMost && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">💖 Loved Most:</p>
                      <p className="text-sm text-slate-700">{review.lovedMost}</p>
                      {review.lovedMostDetails && (
                        <p className="text-xs text-slate-600 mt-1 italic">{review.lovedMostDetails}</p>
                      )}
                    </div>
                  )}

                  {/* ✨ Favorite Character/Moment (NEW) */}
                  {review.favoriteCharacterMoment && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">✨ Favorite Moment:</p>
                      <p className="text-sm text-slate-700">{review.favoriteCharacterMoment}</p>
                    </div>
                  )}

                  {/* 🎯 Target Audience (NEW) */}
                  {review.targetAudience?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-slate-500 mb-2">🎯 Perfect For:</p>
                      <div className="flex flex-wrap gap-2">
                        {review.targetAudience.map((audience, idx) => (
                          <span key={idx} className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
                            {audience}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 💭 Full Review */}
                  {review.fullReview && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">Full Review:</p>
                      <p className="text-sm text-slate-700 leading-relaxed">{review.fullReview}</p>
                    </div>
                  )}

                  {/* 💬 Favorite Quote (NEW) */}
                  {review.favoriteQuote && (
                    <div className="mb-3 rounded-md bg-slate-50 p-3 border-l-4 border-teal-400">
                      <p className="text-sm text-slate-900 italic">"{review.favoriteQuote}"</p>
                    </div>
                  )}

                  {/* 🔒 Private Notes (NEW - HIGHLIGHTED FOR AUTHOR) */}
                  {review.privateNotes && (
                    <div className="mb-3 rounded-md bg-yellow-50 p-3 border-2 border-yellow-300">
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className="h-4 w-4 text-yellow-700" />
                        <p className="text-xs font-bold text-yellow-900">PRIVATE NOTES (Author Only)</p>
                      </div>
                      <p className="text-sm text-yellow-900">{review.privateNotes}</p>
                    </div>
                  )}

                  {/* Review Links */}
                  {review.reviewLinks && (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(review.reviewLinks)
                        .filter(([platform, link]) => link)
                        .map(([platform, link]) => (
                          <a
                            key={platform}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-200"
                          >
                            {platform}
                            <ExternalLink size={10} />
                          </a>
                        ))}
                    </div>
                  )}

                  {/* Additional Comments */}
                  {review.comments && (
                    <div className="mt-3 rounded-md bg-slate-100 p-2">
                      <p className="text-xs text-slate-600 italic">{review.comments}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Platform Stats */}
        <div className="rounded-xl border border-slate-100 bg-white p-5">
          <h3 className="mb-4 text-lg font-bold text-slate-900">Platform Statistics</h3>
          <div className="space-y-2 text-sm">
            {['amazon', 'goodreads', 'instagram', 'tiktok', 'blog'].map(platform => {
              const count = readerReviews.filter(r => r.reviewLinks?.[platform]).length;
              return count > 0 ? (
                <div key={platform} className="flex items-center justify-between">
                  <span className="text-slate-600 capitalize">{platform} Reviews:</span>
                  <span className="font-bold text-slate-900">{count}</span>
                </div>
              ) : null;
            })}
          </div>
        </div>

      </div>
    </div>
  </div>
)}

            {/* ----------------------------------------
                 READER DRAWER
            ---------------------------------------- */}
            {showReaderDrawer && (
              <div className="fixed inset-0 z-40 flex items-center justify-end bg-slate-900/30 backdrop-blur-sm">
                <div className="h-full w-full max-w-md overflow-y-auto border-l border-slate-100 bg-white px-4 py-4 shadow-2xl">

                  {/* Drawer Header */}
                  <div className="mb-3 flex items-center justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.26em] text-teal-700">
                        {editingReader ? "Edit reader" : "New reader"}
                      </p>
                      <h2 className="text-sm font-semibold text-slate-900">
                        ARC reader details
                      </h2>
                    </div>

                    <button
                      onClick={closeReaderDrawer}
                      className="rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:text-teal-700"
                    >
                      Close
                    </button>
                  </div>

                  {/* Drawer Form */}
                  <form onSubmit={handleSaveReader} className="space-y-3">

                    {/* BASIC INFO */}
                    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
                      <p className="mb-2 text-[11px] font-semibold text-slate-700">Basic information</p>

                      <div className="space-y-2">

                        {/* NAME */}
                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">Name *</label>
                          <input
                            type="text"
                            required
                            value={readerForm.name}
                            onChange={(e) => setReaderForm({ ...readerForm, name: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                          />
                        </div>

                        {/* EMAILS */}
                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">
                            Email (correspondence) *
                          </label>
                          <input
                            type="email"
                            required
                            value={readerForm.emailCorrespondence}
                            onChange={(e) =>
                              setReaderForm({ ...readerForm, emailCorrespondence: e.target.value })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">
                            Email (eReader)
                          </label>
                          <input
                            type="email"
                            value={readerForm.eReaderEmail}
                            onChange={(e) =>
                              setReaderForm({ ...readerForm, eReaderEmail: e.target.value })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={readerForm.phoneNumber}
                            onChange={(e) =>
                              setReaderForm({ ...readerForm, phoneNumber: e.target.value })
                            }
                            placeholder="+44 7700 900000"
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                          />
                        </div>

                        {/* MARKETING */}
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={readerForm.acceptsMarketing}
                            onChange={(e) =>
                              setReaderForm({ ...readerForm, acceptsMarketing: e.target.checked })
                            }
                            className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600"
                          />
                          <label className="text-[11px] text-slate-700">
                            Accepts marketing emails
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* ARC PREFERENCES */}
                    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
                      <p className="mb-2 text-[11px] font-semibold text-slate-700">ARC preferences</p>

                      <div className="space-y-2">

                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">Interested in</label>
                          <select
                            value={readerForm.interestedIn}
                            onChange={(e) =>
                              setReaderForm({ ...readerForm, interestedIn: e.target.value })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                          >
                            <option>eBook ARC request</option>
                            <option>Print ARC request</option>
                            <option>Both eBook and Print</option>
                          </select>
                        </div>

                        {/* STATUS */}
 <div>
  <label className="block text-[11px] mb-1 font-medium text-slate-600">Status</label>
  <select
    value={readerForm.status}
    onChange={(e) =>
      setReaderForm({ ...readerForm, status: e.target.value })
    }
    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
  >
    <option>Noted ARC interest</option>
    <option>Engaged</option>
    <option>Follow-up needed</option>
    <option>ARC sent</option>
    <option>Reviewed</option>
    <option>Closed</option>
    <option>Street Team</option>
    <option>Book 2 ARC interest</option>
    <option>New Buyer</option>
  </select>
</div>

                        {/* DATES */}
                        <div className="grid grid-cols-2 gap-2">

                          <div>
                            <label className="block text-[11px] mb-1 font-medium text-slate-600">
                              Signup date
                            </label>
                            <input
                              type="date"
                              value={readerForm.signupDate}
                              onChange={(e) =>
                                setReaderForm({ ...readerForm, signupDate: e.target.value })
                              }
                              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                            />
                          </div>

                          <div>
                            <label className="block text-[11px] mb-1 font-medium text-slate-600">
                              ARC sent (date)
                            </label>
                            <input
                              type="date"
                              value={readerForm.arcSent}
                              onChange={(e) =>
                                setReaderForm({ ...readerForm, arcSent: e.target.value })
                              }
                              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] mb-1 font-medium text-slate-600">
                              Review date
                            </label>
                            <input
                              type="date"
                              value={readerForm.reviewDate}
                              onChange={(e) =>
                                setReaderForm({ ...readerForm, reviewDate: e.target.value })
                              }
                              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                            />
                          </div>

                          <div>
                            <label className="block text-[11px] mb-1 font-medium text-slate-600">
                              Rating
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="5"
                              step="0.1"
                              value={readerForm.rating}
                              onChange={(e) =>
                                setReaderForm({ ...readerForm, rating: e.target.value })
                              }
                              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* BOOKS RECEIVED */}
                    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
                      <p className="mb-2 text-[11px] font-semibold text-slate-700">Books Received (ARCs)</p>

                      <div className="space-y-2">
                        {books.length === 0 ? (
                          <p className="text-[11px] text-slate-500">No books available. Add books first.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {books.map((book) => (
                              <label key={book.id} className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={(readerForm.booksReceived || []).includes(book.id)}
                                  onChange={(e) => {
                                    const currentBooks = readerForm.booksReceived || [];
                                    if (e.target.checked) {
                                      setReaderForm({
                                        ...readerForm,
                                        booksReceived: [...currentBooks, book.id]
                                      });
                                    } else {
                                      setReaderForm({
                                        ...readerForm,
                                        booksReceived: currentBooks.filter(id => id !== book.id)
                                      });
                                    }
                                  }}
                                  className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-slate-500"
                                />
                                <span className="text-[11px] text-slate-700">{book.title}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* SOCIAL + NOTES */}
                    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">

                      <p className="mb-2 text-[11px] font-semibold text-slate-700">Socials & notes</p>

                      <div className="space-y-2">

                        {/* PLATFORMS */}
                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">
                            Review platforms
                          </label>
                          <input
                            type="text"
                            value={
                              Array.isArray(readerForm.reviewPlatforms)
                                ? readerForm.reviewPlatforms.join(", ")
                                : ""
                            }
                            onChange={(e) =>
                              setReaderForm({
                                ...readerForm,
                                reviewPlatforms: e.target.value
                                  .split(",")
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                            placeholder="Amazon, Goodreads…"
                          />
                        </div>

                        {/* PRIMARY PLATFORM */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] mb-1 font-medium text-slate-600">
                              Primary Platform
                            </label>
                            <select
                              value={readerForm.primaryPlatform}
                              onChange={(e) =>
                                setReaderForm({
                                  ...readerForm,
                                  primaryPlatform: e.target.value,
                                })
                              }
                              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                            >
                              <option>Instagram</option>
                              <option>TikTok</option>
                              <option>YouTube</option>
                              <option>Blog</option>
                              <option>Goodreads</option>
                              <option>BookBub</option>
                              <option>Other</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-[11px] mb-1 font-medium text-slate-600">
                              Follower Count
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={readerForm.followerCount}
                              onChange={(e) =>
                                setReaderForm({
                                  ...readerForm,
                                  followerCount: parseInt(e.target.value) || 0,
                                })
                              }
                              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                              placeholder="5000"
                            />
                          </div>
                        </div>

                        {/* USERNAMES */}
                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">
                            Social usernames
                          </label>
                          <input
                            type="text"
                            value={readerForm.socialUsernames}
                            onChange={(e) =>
                              setReaderForm({
                                ...readerForm,
                                socialUsernames: e.target.value,
                              })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                            placeholder="@user on IG…"
                          />
                        </div>

                        {/* FAV BOOKS */}
                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">
                            Favourite books
                          </label>
                          <input
                            type="text"
                            value={readerForm.favoriteBooks}
                            onChange={(e) =>
                              setReaderForm({
                                ...readerForm,
                                favoriteBooks: e.target.value,
                              })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                          />
                        </div>

                        {/* LOCATION */}
                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">
                            Location
                          </label>
                          <input
                            type="text"
                            value={readerForm.location}
                            onChange={(e) =>
                              setReaderForm({
                                ...readerForm,
                                location: e.target.value,
                              })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                          />
                        </div>

                        {/* NOTES */}
                        <div>
                          <label className="block text-[11px] mb-1 font-medium text-slate-600">
                            Notes
                          </label>
                          <textarea
                            rows={3}
                            value={readerForm.notes}
                            onChange={(e) =>
                              setReaderForm({
                                ...readerForm,
                                notes: e.target.value,
                              })
                            }
                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px]"
                          />
                        </div>
                      </div>
                    </div>

                    {/* SAVE BAR */}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <button
                        type="submit"
                        disabled={isSavingReader}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-1.5 text-[11px] font-semibold text-white hover:bg-teal-700 disabled:opacity-70"
                      >
                        {isSavingReader ? "Saving…" : "Save reader"}
                      </button>

                      <button
                        type="button"
                        onClick={closeReaderDrawer}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-slate-700 hover:text-teal-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* QUICK ADD MODAL */}
{showQuickAddModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
    <div className="w-full max-w-2xl rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Quick Add from Email</h3>
          <p className="text-xs text-slate-500">Paste the entire signup email below</p>
        </div>
        <button
          onClick={() => {
            setShowQuickAddModal(false);
            setPastedEmailText("");
          }}
          className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <XCircle size={20} />
        </button>
      </div>

      <div className="mb-4">
        <label className="mb-2 block text-xs font-medium text-slate-600">
          Paste Email Content:
        </label>
        <textarea
          value={pastedEmailText}
          onChange={(e) => setPastedEmailText(e.target.value)}
          rows={12}
          placeholder="Name: Jane Doe
Email (Correspondence): janedoe@gmail.com, accepts marketing: false
Email (eReaders only): janedow@kindle.com
What are you interested in?: eBook ARC request
Where do you currently leave book reviews?: Instagram, TikTok, Amazon, Goodreads
..."
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 font-mono"
        />
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleQuickAddFromEmail}
          disabled={!pastedEmailText.trim() || isParsingEmail}
          className="flex-1 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isParsingEmail ? "Parsing with AI..." : "Add Reader"}
        </button>
        <button
          onClick={() => {
            setShowQuickAddModal(false);
            setPastedEmailText("");
          }}
          disabled={isParsingEmail}
          className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

          </>
        )}

{/* REVIEWS TAB */}
        {activeTab === "reviews" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Review Submissions</h2>
              <button
                onClick={handleBackfillRatings}
                className="flex items-center gap-2 rounded-lg border border-teal-300 bg-slate-50 px-3 py-2 text-xs font-semibold text-teal-700 hover:bg-slate-100 transition-colors"
                title="Copy star ratings from approved reviews onto reader records so the star filter works"
              >
                <Star className="h-3.5 w-3.5" />
                Sync Ratings to Readers
              </button>
            </div>

            {reviews.length === 0 ? (
              <div className="rounded-2xl border border-slate-100 bg-white p-6 text-center text-sm text-slate-500">
                No reviews submitted yet.
              </div>
            ) : (() => {
              // Helper to render a single review card
              const renderReviewCard = (rev) => {
                const submittedLinks = Object.entries(rev.reviewLinks || {})
                  .filter(([, link]) => link && link.trim() !== '');
                const currentRating = editingRatings[rev.id] !== undefined ? editingRatings[rev.id] : (rev.rating || 0);
                const isEditingRating = editingRatings[rev.id] !== undefined;

                // Pre-select status based on reader's chosen path
                const pathToStatus = {
                  'street-team': 'Street Team',
                  'arc-priority': 'Second ARC',
                  'not-for-me': 'Closed',
                };

                return (
                  <div key={rev.id} className="rounded-xl border-2 border-slate-200 bg-white p-5 shadow-sm">
                    {/* Header */}
                    <div className="mb-4 flex items-start justify-between border-b border-slate-100 pb-3">
                      <div>
                        <p className="text-sm font-medium text-slate-600">
                          📅 Submitted: {rev.submittedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                        </p>
                        {rev.readerPath && (
                          <p className="mt-1 text-sm font-semibold text-purple-600">
                            {rev.readerPath === 'street-team' && '🎯 Wants to join Street Team!'}
                            {rev.readerPath === 'arc-priority' && '📚 Wants future ARCs'}
                            {rev.readerPath === 'not-for-me' && 'Not interested in future content'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {rev.status === 'approved' && (
                          <button onClick={() => handleDeleteReview(rev.id, rev)}
                            className="rounded-lg border border-red-300 bg-red-50 p-2 text-red-700 hover:bg-red-100 transition-colors" title="Delete review">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Editable Star Rating */}
                    <div className="mb-4 rounded-lg bg-gradient-to-r from-slate-50 to-orange-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-slate-900">⭐ Rating:</p>
                        {!isEditingRating ? (
                          <button onClick={() => setEditingRatings(prev => ({ ...prev, [rev.id]: rev.rating || 0 }))}
                            className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-teal-700 hover:bg-slate-200 transition-colors">
                            <Edit2 className="h-3 w-3" /> Edit
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button onClick={() => handleUpdateReviewRating(rev.id, editingRatings[rev.id])}
                              className="rounded-lg bg-green-100 px-2 py-1 text-xs font-semibold text-green-700 hover:bg-green-200">Save</button>
                            <button onClick={() => setEditingRatings(prev => { const u = {...prev}; delete u[rev.id]; return u; })}
                              className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200">Cancel</button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(star => (
                          <button key={star} type="button"
                            onClick={() => isEditingRating && setEditingRatings(prev => ({ ...prev, [rev.id]: star }))}
                            className={`focus:outline-none transition-transform ${isEditingRating ? 'hover:scale-110 cursor-pointer' : 'cursor-default'}`}>
                            <Star className={`h-6 w-6 transition-colors ${star <= currentRating ? 'fill-teal-400 text-teal-400' : 'text-slate-300'}`} />
                          </button>
                        ))}
                        <span className="ml-2 text-lg font-bold text-teal-600">{currentRating}/5</span>
                        {isEditingRating && <span className="text-xs text-slate-500 italic ml-1">editing...</span>}
                      </div>
                    </div>

                    {rev.quickSummary && (
                      <div className="mb-4 rounded-lg bg-blue-50 p-4 border-l-4 border-blue-400">
                        <p className="text-xs font-bold text-blue-900 mb-2">📝 Quick Summary</p>
                        <p className="text-sm text-blue-800 leading-relaxed">{rev.quickSummary}</p>
                      </div>
                    )}
                    {rev.lovedMost && (
                      <div className="mb-4 rounded-lg bg-pink-50 p-4 border-l-4 border-pink-400">
                        <p className="text-xs font-bold text-pink-900 mb-2">💖 What They Loved Most</p>
                        <p className="text-sm font-semibold text-pink-800">{rev.lovedMost}</p>
                        {rev.lovedMostDetails && <p className="text-sm text-pink-700 mt-2 leading-relaxed">{rev.lovedMostDetails}</p>}
                      </div>
                    )}
                    {rev.favoriteCharacterMoment && (
                      <div className="mb-4 rounded-lg bg-purple-50 p-4 border-l-4 border-purple-400">
                        <p className="text-xs font-bold text-purple-900 mb-2">⭐ Favorite Character Moment</p>
                        <p className="text-sm text-purple-800 leading-relaxed">{rev.favoriteCharacterMoment}</p>
                      </div>
                    )}
                    {rev.targetAudience && rev.targetAudience.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-bold text-slate-700 mb-2">👥 Perfect For:</p>
                        <div className="flex flex-wrap gap-2">
                          {rev.targetAudience.map((a, i) => (
                            <span key={i} className="rounded-full bg-indigo-100 px-3 py-1 text-sm font-medium text-indigo-700">{a}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {rev.fullReview && (
                      <div className="mb-4 rounded-lg bg-slate-50 p-4">
                        <p className="text-xs font-bold text-slate-900 mb-3">📖 Full Review</p>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{rev.fullReview}</p>
                      </div>
                    )}
                    {rev.favoriteQuote && (
                      <div className="mb-4 rounded-lg border-l-4 border-slate-500 bg-slate-50 p-4">
                        <p className="text-xs font-bold text-slate-900 mb-2">💬 Favorite Quote</p>
                        <p className="text-sm italic text-slate-800">"{rev.favoriteQuote}"</p>
                      </div>
                    )}
                    {submittedLinks.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-bold text-slate-700 mb-3">🔗 Posted Reviews</p>
                        <div className="flex flex-wrap gap-2">
                          {submittedLinks.map(([platform, link]) => (
                            <a key={platform} href={link} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-slate-500 to-orange-500 px-4 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-orange-600 transition-all shadow-sm">
                              {platform}<ExternalLink size={14} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {rev.privateNotes && (
                      <div className="mb-4 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Lock className="h-4 w-4 text-slate-600" />
                          <p className="text-xs font-bold text-slate-700">Private Notes (Only You See This)</p>
                        </div>
                        <p className="text-sm text-slate-600 leading-relaxed">{rev.privateNotes}</p>
                      </div>
                    )}
                    {rev.readerComments && !rev.fullReview && (
                      <div className="mb-4 rounded-lg bg-slate-100 p-3">
                        <p className="text-xs font-medium text-slate-600 mb-1">💬 Comments:</p>
                        <p className="text-sm text-slate-700">{rev.readerComments}</p>
                      </div>
                    )}

                    {/* ACTION SECTION */}
                    <div className="mt-5 border-t border-slate-200 pt-4">
                      {(!rev.status || rev.status === 'pending') && (
                        <div className="space-y-3">
                          <div className="rounded-lg bg-green-50 p-4">
                            <label className="block text-sm font-semibold text-green-900 mb-2">💰 Points to Award</label>
                            <div className="flex items-center gap-3">
                              <input type="number" min="0" step="10" placeholder="50"
                                value={customPoints[rev.id] !== undefined ? customPoints[rev.id] : 50}
                                onChange={(e) => setCustomPoints({...customPoints, [rev.id]: Number(e.target.value)})}
                                className="w-32 rounded-lg border-2 border-green-300 px-3 py-2 text-center text-lg font-bold focus:border-green-500 focus:ring-2 focus:ring-green-200"
                              />
                              <p className="text-xs text-green-600">Suggested: 50 for a review submission</p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <button
                              onClick={() => {
                                const suggestedStatus = pathToStatus[rev.readerPath] || 'Street Team';
                                setApprovalReview({ id: rev.id, data: rev, points: customPoints[rev.id] !== undefined ? customPoints[rev.id] : 50 });
                                setApprovalStatus(suggestedStatus);
                                setShowApprovalModal(true);
                              }}
                              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-3 text-sm font-bold text-white hover:from-green-700 hover:to-emerald-700 transition-all shadow-md">
                              <CheckCircle size={18} /> Accept Review
                            </button>
                            <button onClick={() => handleRejectReview(rev.id)}
                              className="flex items-center justify-center gap-2 rounded-lg border-2 border-red-300 bg-white px-6 py-3 text-sm font-bold text-red-700 hover:bg-red-50 transition-all">
                              <XCircle size={18} /> Reject
                            </button>
                          </div>
                        </div>
                      )}
                      {rev.status === 'approved' && (
                        <div className="flex items-center gap-3 rounded-lg bg-green-50 p-4">
                          <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-bold text-green-900">✅ Review Approved</p>
                            <p className="text-sm text-green-700">+{rev.pointsAwarded || 0} points awarded to reader</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              };

              // Split reviews by status
              const pendingRevs = reviews.filter(r => !r.status || r.status === 'pending');
              const approvedRevs = reviews.filter(r => r.status === 'approved');
              const rejectedRevs = reviews.filter(r => r.status === 'rejected');

              // Helper to get reader name for a review
              const getReaderName = (rev) => {
                const r = readers.find(r => r.emailCorrespondence === rev.readerEmail || r.id === rev.readerId);
                return r?.name || rev.readerName || 'Unknown Reader';
              };

              const SectionHeader = ({ label, count, color, bg, sectionKey }) => (
                <button
                  onClick={() => setReviewSectionOpen(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
                  className={`w-full flex items-center justify-between rounded-2xl border-2 px-5 py-4 font-bold text-left transition-all ${bg}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-bold ${color}`}>{label}</span>
                    <span className={`rounded-full px-3 py-1 text-sm font-bold ${color} bg-white/60`}>{count}</span>
                  </div>
                  <span className={`text-xl ${color}`}>{reviewSectionOpen[sectionKey] ? '▲' : '▼'}</span>
                </button>
              );

              return (
                <div className="space-y-4">
                  {/* PENDING */}
                  <div>
                    <SectionHeader label="⏳ Pending Review" count={pendingRevs.length} color="text-yellow-800" bg="border-yellow-300 bg-yellow-50 hover:bg-yellow-100" sectionKey="pending" />
                    {reviewSectionOpen.pending && pendingRevs.length > 0 && (
                      <div className="mt-3 space-y-4 pl-2">
                        {pendingRevs.map(rev => (
                          <div key={rev.id}>
                            <p className="mb-2 text-sm font-semibold text-slate-600">👤 {getReaderName(rev)} — {rev.bookTitle || 'Unknown Book'}</p>
                            {renderReviewCard(rev)}
                          </div>
                        ))}
                      </div>
                    )}
                    {reviewSectionOpen.pending && pendingRevs.length === 0 && (
                      <p className="mt-3 pl-2 text-sm text-slate-400">No pending reviews.</p>
                    )}
                  </div>

                  {/* ACCEPTED */}
                  <div>
                    <SectionHeader label="✅ Accepted" count={approvedRevs.length} color="text-green-800" bg="border-green-300 bg-green-50 hover:bg-green-100" sectionKey="approved" />
                    {reviewSectionOpen.approved && approvedRevs.length > 0 && (
                      <div className="mt-3 space-y-4 pl-2">
                        {approvedRevs.map(rev => (
                          <div key={rev.id}>
                            <p className="mb-2 text-sm font-semibold text-slate-600">👤 {getReaderName(rev)} — {rev.bookTitle || 'Unknown Book'}</p>
                            {renderReviewCard(rev)}
                          </div>
                        ))}
                      </div>
                    )}
                    {reviewSectionOpen.approved && approvedRevs.length === 0 && (
                      <p className="mt-3 pl-2 text-sm text-slate-400">No accepted reviews yet.</p>
                    )}
                  </div>

                  {/* REJECTED */}
                  <div>
                    <SectionHeader label="❌ Rejected" count={rejectedRevs.length} color="text-red-800" bg="border-red-200 bg-red-50 hover:bg-red-100" sectionKey="rejected" />
                    {reviewSectionOpen.rejected && rejectedRevs.length > 0 && (
                      <div className="mt-3 space-y-4 pl-2">
                        {rejectedRevs.map(rev => (
                          <div key={rev.id}>
                            <p className="mb-2 text-sm font-semibold text-slate-600">👤 {getReaderName(rev)} — {rev.bookTitle || 'Unknown Book'}</p>
                            {renderReviewCard(rev)}
                          </div>
                        ))}
                      </div>
                    )}
                    {reviewSectionOpen.rejected && rejectedRevs.length === 0 && (
                      <p className="mt-3 pl-2 text-sm text-slate-400">No rejected reviews.</p>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

{activeTab === "arc-requests" && (
  <>
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-xl font-bold text-slate-900">ARC Requests</h2>
      <div className="flex items-center gap-2 text-sm">
        <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-teal-700">
          Pending: {arcRequests.filter(r => r.status === 'pending').length}
        </span>
        <span className="rounded-full bg-green-100 px-3 py-1 font-medium text-green-700">
          Approved: {arcRequests.filter(r => r.status === 'approved').length}
        </span>
        {arcRequests.filter(r => r.status === 'pending').length > 0 && (
          <button
            onClick={handleApproveAllPendingArcRequests}
            className="rounded-full bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-1 font-semibold text-white hover:from-teal-600 hover:to-teal-700 transition-all shadow-sm"
          >
            ✅ Approve All ({arcRequests.filter(r => r.status === 'pending').length})
          </button>
        )}
      </div>
    </div>

    {arcRequests.length === 0 ? (
      <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center text-sm text-slate-500">
        No ARC requests yet.
      </div>
    ) : (
      <div className="space-y-3">
        {arcRequests
          .sort((a, b) => {
            // Pending first
            if (a.status === 'pending' && b.status !== 'pending') return -1;
            if (a.status !== 'pending' && b.status === 'pending') return 1;
            // Then by date
            return (b.requestedAt?.seconds || 0) - (a.requestedAt?.seconds || 0);
          })
          .map((request) => {
            const book = books.find(b => b.id === request.bookId);
            const reader = readers.find(r => r.id === request.readerId);
            
            return (
              <div
                key={request.id}
                className={`rounded-2xl border p-4 shadow-sm ${getArcStatusBgClass(request.status)}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-3">
                      <h3 className="text-lg font-bold text-slate-900">
                        {request.readerName}
                      </h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold border ${getArcStatusBadgeClass(request.status)}`}>
                        {getArcStatusLabel(request.status)}
                      </span>
                    </div>

                    <p className="mb-2 text-sm text-slate-600">
                      <strong>Book:</strong> {book?.title || request.bookTitle}
                    </p>

                    <p className="mb-2 text-sm text-slate-600">
                      <strong>Email:</strong> {request.readerEmail}
                    </p>

                    <div className="mb-3 rounded-lg bg-white p-3">
                      <p className="mb-1 text-xs font-semibold text-slate-500">
                        Why they want it:
                      </p>
                      <p className="text-sm text-slate-700">{request.message}</p>
                    </div>

                    <div className="flex gap-4 text-xs text-slate-500">
                      <span>📊 {request.readerStats?.totalReviews || 0} reviews submitted</span>
                      <span>⭐ {request.readerStats?.totalPoints || 0} points earned</span>
                      <span>📅 Requested {request.requestedAt?.toDate().toLocaleDateString()}</span>
                    </div>

                    {reader && (
                      <div className="mt-2 text-xs text-slate-500">
                        <span>
                          📍 {reader.location || 'Location not provided'}
                        </span>
                      </div>
                    )}
                  </div>

                  {request.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproveArcRequest(request.id)}
                        className="rounded-lg bg-green-600 px-4 py-2 text-xs font-semibold text-white hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDenyArcRequest(request.id)}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Deny
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    )}
  </>
)}

        {/* ----------------------------------------
             BOOKS TAB
        ---------------------------------------- */}
        {activeTab === "books" && (() => {
          const STATUS_COLOURS = {
            upcoming:          'bg-blue-100 text-blue-700',
            pre_launch_active: 'bg-green-100 text-green-700',
            launched:          'bg-teal-100 text-teal-700',
            archived:          'bg-slate-100 text-slate-500',
            published:         'bg-green-100 text-green-700',
            'arc-phase':       'bg-green-100 text-green-700',
          };
          const STATUS_LABELS = {
            upcoming: 'Upcoming',
            pre_launch_active: 'Pre-Launch Active',
            launched: 'Launched',
            archived: 'Archived',
            published: 'Published',
            'arc-phase': 'ARC Phase',
          };

          // Build per-book review stats from the reviews already loaded via collectionGroup
          const bookReviewStats = {};
          reviews.forEach(rev => {
            if (!rev.bookId) return;
            if (!bookReviewStats[rev.bookId]) {
              bookReviewStats[rev.bookId] = { total: 0, approved: 0, ratings: [], dist: {1:0,2:0,3:0,4:0,5:0}, reviews: [] };
            }
            const s = bookReviewStats[rev.bookId];
            s.total++;
            if (rev.status === 'approved') s.approved++;
            if (rev.rating && rev.rating >= 1 && rev.rating <= 5) {
              s.ratings.push(rev.rating);
              s.dist[rev.rating]++;
            }
            s.reviews.push(rev);
          });
          // Compute averages
          Object.values(bookReviewStats).forEach(s => {
            s.avgRating = s.ratings.length > 0 ? (s.ratings.reduce((a,b) => a+b, 0) / s.ratings.length) : null;
          });

          const searchLower = booksSearch.toLowerCase();
          const filteredBooks = books.filter(b => {
            if (booksStatusFilter !== 'all' && b.status !== booksStatusFilter) return false;
            if (searchLower) {
              const authorName = (platformAuthors[b.authorId] || '').toLowerCase();
              const title = (b.title || '').toLowerCase();
              const series = (b.series || '').toLowerCase();
              return title.includes(searchLower) || authorName.includes(searchLower) || series.includes(searchLower);
            }
            return true;
          });
          return (
          <div className="p-6">
            {/* Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Platform Books</h2>
              <p className="text-sm text-slate-500">{books.length} books across all authors (read-only)</p>
            </div>

            {/* Search & Filter */}
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by title, author, or series..."
                  value={booksSearch}
                  onChange={(e) => setBooksSearch(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                />
              </div>
              <select
                value={booksStatusFilter}
                onChange={(e) => setBooksStatusFilter(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="upcoming">Upcoming</option>
                <option value="pre_launch_active">Pre-Launch Active</option>
                <option value="launched">Launched</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Results count */}
            {booksSearch || booksStatusFilter !== 'all' ? (
              <p className="mb-4 text-xs text-slate-500">
                Showing {filteredBooks.length} of {books.length} books
              </p>
            ) : null}

            {/* Book Table */}
            {filteredBooks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-12 text-center">
                <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                <p className="text-sm text-slate-500">
                  {books.length === 0 ? 'No author books on the platform yet.' : 'No books match your search.'}
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Book</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Author</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 hidden md:table-cell">Series</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">Status</th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-700">Rating</th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-700">Reviews</th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-700">Reviews Open</th>
                      <th className="px-4 py-3 text-right font-semibold text-slate-700 hidden lg:table-cell">Words</th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700 hidden lg:table-cell">Launch Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredBooks.map((b) => (
                    <React.Fragment key={`${b.authorId}-${b.id}`}>
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {b.coverUrl ? (
                              <img src={b.coverUrl} alt="" className="h-12 w-8 rounded object-cover shadow-sm" />
                            ) : (
                              <div className="flex h-12 w-8 items-center justify-center rounded bg-slate-100">
                                <BookOpen className="h-4 w-4 text-slate-400" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="truncate font-medium text-slate-900">{b.title}</p>
                              {b.seriesNumber && (
                                <p className="text-xs text-slate-400">Book #{b.seriesNumber}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {platformAuthors[b.authorId] || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                          {b.series || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLOURS[b.status] || 'bg-slate-100 text-slate-600'}`}>
                            {STATUS_LABELS[b.status] || b.status}
                          </span>
                          {b.arcAvailable && (
                            <span className="ml-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-teal-700">
                              ARC
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            const stats = bookReviewStats[b.id];
                            if (!stats || stats.avgRating === null) return <span className="text-slate-300">—</span>;
                            return (
                              <div className="flex flex-col items-center">
                                <span className="text-sm font-bold text-slate-900">{stats.avgRating.toFixed(1)}</span>
                                <div className="flex">
                                  {[1,2,3,4,5].map(s => (
                                    <span key={s} className="text-xs" style={{ color: stats.avgRating >= s ? '#f59e0b' : '#e5e7eb' }}>★</span>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {(() => {
                            const stats = bookReviewStats[b.id];
                            if (!stats) return <span className="text-slate-300">0</span>;
                            return (
                              <button
                                onClick={() => setBooksExpandedReview(prev => prev === b.id ? null : b.id)}
                                className="group flex flex-col items-center hover:text-teal-600 transition-colors"
                              >
                                <span className="text-sm font-bold text-slate-900 group-hover:text-teal-600">{stats.total}</span>
                                <span className="text-[10px] text-slate-400 group-hover:text-teal-500">{stats.approved} approved</span>
                              </button>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={async () => {
                              const newVal = b.reviewsEnabled === false ? true : false;
                              try {
                                await updateDoc(doc(db, 'authors', b.authorId, 'books', b.id), { reviewsEnabled: newVal });
                              } catch (err) {
                                alert('Failed to update: ' + err.message);
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                              b.reviewsEnabled !== false
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                          >
                            <span className={`inline-block h-2 w-2 rounded-full ${b.reviewsEnabled !== false ? 'bg-green-500' : 'bg-slate-400'}`} />
                            {b.reviewsEnabled !== false ? 'Open' : 'Closed'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right text-slate-600 hidden lg:table-cell">
                          {b.wordCount ? Number(b.wordCount).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">
                          {b.pubDate || b.goLiveDate || b.releaseDate || '-'}
                        </td>
                      </tr>
                      {/* Expanded review detail row */}
                      {booksExpandedReview === b.id && bookReviewStats[b.id] && (
                        <tr>
                          <td colSpan={9} className="bg-slate-50 px-4 py-4">
                            <div className="space-y-4">
                              {/* Star distribution */}
                              <div className="flex items-center gap-6">
                                <div>
                                  <p className="text-xs font-semibold text-slate-500 mb-2">Rating Distribution</p>
                                  <div className="space-y-1">
                                    {[5,4,3,2,1].map(star => {
                                      const count = bookReviewStats[b.id].dist[star];
                                      const total = bookReviewStats[b.id].ratings.length;
                                      const pct = total > 0 ? (count / total) * 100 : 0;
                                      return (
                                        <div key={star} className="flex items-center gap-2 text-xs">
                                          <span className="w-4 text-right font-medium text-slate-600">{star}★</span>
                                          <div className="h-2.5 w-32 rounded-full bg-slate-200 overflow-hidden">
                                            <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                                          </div>
                                          <span className="w-6 text-right text-slate-500">{count}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="text-center px-6 border-l border-slate-200">
                                  <p className="text-3xl font-bold text-slate-900">{bookReviewStats[b.id].avgRating?.toFixed(1) || '—'}</p>
                                  <div className="flex justify-center my-1">
                                    {[1,2,3,4,5].map(s => (
                                      <span key={s} className="text-base" style={{ color: (bookReviewStats[b.id].avgRating || 0) >= s ? '#f59e0b' : '#e5e7eb' }}>★</span>
                                    ))}
                                  </div>
                                  <p className="text-xs text-slate-400">{bookReviewStats[b.id].ratings.length} rated</p>
                                </div>
                              </div>
                              {/* Individual reviews */}
                              <div>
                                <p className="text-xs font-semibold text-slate-500 mb-2">Individual Reviews ({bookReviewStats[b.id].total})</p>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {bookReviewStats[b.id].reviews
                                    .sort((a, b) => (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0))
                                    .map(rev => (
                                    <div key={rev.id} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-sm font-medium text-slate-900">{rev.readerName || rev.readerEmail || 'Anonymous'}</span>
                                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                            rev.status === 'approved' ? 'bg-green-100 text-green-700' :
                                            rev.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                            'bg-yellow-100 text-yellow-700'
                                          }`}>{rev.status || 'pending'}</span>
                                          {rev.rating > 0 && (
                                            <span className="flex items-center gap-0.5 text-xs">
                                              {[1,2,3,4,5].map(s => (
                                                <span key={s} style={{ color: rev.rating >= s ? '#f59e0b' : '#e5e7eb' }}>★</span>
                                              ))}
                                            </span>
                                          )}
                                        </div>
                                        {rev.quickSummary && <p className="mt-1 text-xs text-slate-600 line-clamp-2">{rev.quickSummary}</p>}
                                        {!rev.quickSummary && rev.fullReview && <p className="mt-1 text-xs text-slate-600 line-clamp-2">{rev.fullReview}</p>}
                                        {rev.favoriteQuote && <p className="mt-1 text-xs italic text-teal-600 line-clamp-1">"{rev.favoriteQuote}"</p>}
                                        <p className="mt-1 text-[10px] text-slate-400">
                                          {rev.submittedAt?.toDate?.().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) || 'Unknown date'}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          );
        })()}

        {/* ----------------------------------------
             BOOK DATABASE TAB (Admin)
        ---------------------------------------- */}
        {activeTab === "book-database" && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Book Database</h2>
                <p className="text-sm text-slate-500">{bookDbList.length} books in database</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant={bookDbView === 'list' ? 'default' : 'outline'} size="sm"
                  onClick={() => { setBookDbView('list'); loadBookDb(); }}>
                  Database
                </Button>
                <Button variant={bookDbView === 'search' ? 'default' : 'outline'} size="sm"
                  onClick={() => setBookDbView('search')}>
                  <Search className="h-4 w-4 mr-1" /> Search & Add
                </Button>
                <Button variant={bookDbView === 'bulk-import' ? 'default' : 'outline'} size="sm"
                  onClick={() => setBookDbView('bulk-import')}>
                  <Upload className="h-4 w-4 mr-1" /> Bulk Import
                </Button>
                {(() => {
                  const dupCount = findAllDuplicateGroups(bookDbList, dismissedDupPairs).length;
                  return dupCount > 0 ? (
                    <Button variant={bookDbView === 'duplicates' ? 'default' : 'outline'} size="sm"
                      onClick={() => setBookDbView('duplicates')}
                      className={bookDbView !== 'duplicates' ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : ''}>
                      Duplicates ({dupCount})
                    </Button>
                  ) : null;
                })()}
                <Button variant={bookDbView === 'import-tools' ? 'default' : 'outline'} size="sm"
                  onClick={() => setBookDbView('import-tools')}>
                  <Sparkles className="h-4 w-4 mr-1" /> Import Tools
                </Button>
                <Button variant={bookDbView === 'reader-imports' ? 'default' : 'outline'} size="sm"
                  onClick={() => setBookDbView('reader-imports')}>
                  Reader Imports
                </Button>
                <label className="cursor-pointer">
                  <input type="file" accept=".csv" ref={csvFileRef} onChange={handleCsvImport} className="hidden" />
                  <Button variant="outline" size="sm" onClick={() => csvFileRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-1" /> CSV Import
                  </Button>
                </label>
              </div>
            </div>

            {/* Seed toast notification */}
            {seedToast && (
              <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50 p-4 flex items-center justify-between">
                <p className="text-sm text-teal-800">{seedToast}</p>
                <button onClick={() => setSeedToast(null)} className="text-teal-600 hover:text-teal-800 ml-3 text-sm font-bold">×</button>
              </div>
            )}

            {/* Duplicate warning dialog */}
            {dupWarning && (
              <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
                <p className="text-sm font-bold text-amber-800 mb-2">Possible Duplicate Detected</p>
                <p className="text-xs text-amber-700 mb-3">
                  "<span className="font-semibold">{dupWarning.newBook.title}</span>" looks like a duplicate of
                  "<span className="font-semibold">{dupWarning.match.title}</span>" by {dupWarning.match.author}
                  ({Math.round(dupWarning.similarity * 100)}% match)
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => { addBookToDb(dupWarning.newBook, true); setDupWarning(null); }}>
                    Add Anyway
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    handleDismissDuplicate(
                      dupWarning.newBook.googleBooksId,
                      dupWarning.match.id || dupWarning.match.googleBooksId
                    );
                    setDupWarning(null);
                  }}>
                    Not a Duplicate
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setDupWarning(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* DUPLICATES VIEW */}
            {bookDbView === 'duplicates' && (
              <div>
                <p className="text-sm text-slate-500 mb-4">Books with similar titles by the same author. Review and merge or dismiss.</p>

                {/* Active merge UI */}
                {dupMergeState && (
                  <div className="mb-6 rounded-2xl border-2 border-teal-200 bg-white p-5 shadow-lg">
                    <p className="text-sm font-bold text-slate-800 mb-3">Merge Books</p>
                    <p className="text-xs text-slate-500 mb-4">Choose which cover to keep. All tags will be combined from both records.</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {[dupMergeState.primary, dupMergeState.secondary].map((book, i) => {
                        const label = i === 0 ? 'primary' : 'secondary';
                        const isChosen = dupMergeState.preferCover === label;
                        return (
                          <div key={book.id} className={`rounded-xl border-2 p-3 cursor-pointer transition-all ${
                            isChosen ? 'border-teal-400 bg-teal-50/50' : 'border-slate-200 hover:border-slate-300'
                          }`} onClick={() => setDupMergeState(prev => ({ ...prev, preferCover: label }))}>
                            <div className="flex items-center gap-1 mb-2">
                              <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                isChosen ? 'border-teal-500 bg-teal-500' : 'border-slate-300'
                              }`}>
                                {isChosen && <CheckCircle className="h-3 w-3 text-white" />}
                              </div>
                              <span className="text-[10px] font-semibold text-slate-500 uppercase">Use this cover</span>
                            </div>
                            <div className="flex gap-3">
                              {book.cover ? (
                                <img src={book.cover} alt="" className="h-20 w-14 rounded-lg object-cover flex-shrink-0" />
                              ) : (
                                <div className="h-20 w-14 rounded-lg bg-slate-100 flex-shrink-0 flex items-center justify-center text-slate-300 text-xs">?</div>
                              )}
                              <div className="min-w-0">
                                <h4 className="font-semibold text-slate-900 text-sm truncate">{book.title}</h4>
                                <p className="text-xs text-slate-500">{book.author}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">{book.publishedDate} {book.pageCount && `· ${book.pageCount}p`}</p>
                                <p className="text-[10px] text-slate-400">{book.submissionCount || 0} submissions · {
                                  BOOK_TAXONOMY.reduce((acc, sec) => acc + Object.keys(book.resolvedTags?.[sec.key] || {}).filter(k => book.resolvedTags[sec.key][k]?.value === 'present').length, 0)
                                } tags</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex gap-2">
                      <Button size="sm" onClick={async () => {
                        const ok = await handleMergeBooks(dupMergeState.primary, dupMergeState.secondary, dupMergeState.preferCover);
                        if (ok) setDupMergeState(null);
                      }}>
                        Merge — Keep "{dupMergeState.primary.title}"
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setDupMergeState(null)}>Cancel</Button>
                    </div>
                  </div>
                )}

                {/* Duplicate groups */}
                {findAllDuplicateGroups(bookDbList, dismissedDupPairs).map((group, gIdx) => (
                  <div key={gIdx} className="mb-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                      <p className="text-xs font-semibold text-slate-600">
                        {group.author} — {group.books.length} editions
                      </p>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {group.books.map(book => (
                        <div key={book.id} className="flex items-center gap-3 px-4 py-3">
                          {book.cover ? (
                            <img src={book.cover} alt="" className="h-14 w-10 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="h-14 w-10 rounded bg-slate-100 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-900 text-sm truncate">{book.title}</h4>
                            <p className="text-xs text-slate-500">{book.author}</p>
                            <p className="text-[10px] text-slate-400">{book.publishedDate} {book.pageCount && `· ${book.pageCount}p`} · {book.submissionCount || 0} submissions</p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button variant="outline" size="sm" className="text-[10px] px-2 py-1 h-auto"
                              onClick={() => {
                                // Merge: use this as primary, first other as secondary
                                const other = group.books.find(b => b.id !== book.id);
                                if (other) setDupMergeState({ primary: book, secondary: other, preferCover: 'primary' });
                              }}>
                              Keep this
                            </Button>
                            <Button variant="outline" size="sm" className="text-[10px] px-2 py-1 h-auto text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteDuplicate(book.id)}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 flex gap-2">
                      <Button variant="outline" size="sm" className="text-[10px]"
                        onClick={() => {
                          // Dismiss all pairs in this group
                          for (let i = 0; i < group.books.length; i++) {
                            for (let j = i + 1; j < group.books.length; j++) {
                              handleDismissDuplicate(group.books[i].id, group.books[j].id);
                            }
                          }
                        }}>
                        Not duplicates — dismiss
                      </Button>
                    </div>
                  </div>
                ))}

                {findAllDuplicateGroups(bookDbList, dismissedDupPairs).length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center">
                    <CheckCircle className="h-8 w-8 text-teal-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No duplicates detected. Looking good!</p>
                  </div>
                )}
              </div>
            )}

            {/* BULK IMPORT VIEW */}
            {bookDbView === 'bulk-import' && (
              <BulkBookImport
                existingBookIds={bookDbList.map(b => b.googleBooksId || b.id)}
                existingBooks={bookDbList}
                onClose={() => { setBookDbView('list'); loadBookDb(); }}
                onImportComplete={() => loadBookDb()}
              />
            )}

            {/* IMPORT TOOLS VIEW */}
            {bookDbView === 'import-tools' && (
              <BookImportTools
                existingBooks={bookDbList}
                onClose={() => { setBookDbView('list'); loadBookDb(); }}
                onImportComplete={() => loadBookDb()}
              />
            )}

            {/* READER IMPORTS VIEW */}
            {bookDbView === 'reader-imports' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Reader Imports</h3>
                  <p className="text-sm text-slate-500 mb-4">Books contributed by readers via CSV imports. Import on behalf of a reader below.</p>
                </div>

                {/* Bulk Admin Import */}
                <div className="rounded-xl border border-teal-200 bg-teal-50/30 p-4">
                  <h4 className="text-sm font-bold text-teal-800 mb-2">Import for a Reader</h4>
                  <p className="text-xs text-slate-500 mb-3">
                    Upload a reader's Goodreads, StoryGraph, LibraryThing, Kindle, BookBub, Literal.club, or Oku export.
                  </p>
                  <ReadingHistoryImport
                    isAdmin={true}
                    existingBooks={bookDbList}
                    allReaders={readers}
                    adminUserId={currentUser?.uid}
                    onComplete={() => loadBookDb()}
                  />
                </div>

                {/* Stats */}
                {(() => {
                  const readerImported = bookDbList.filter(b => b.addedBy === 'reader-import' || b.addedBy === 'admin-import');

                  return (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="rounded-xl bg-teal-50 border border-teal-200 p-4 text-center">
                          <p className="text-2xl font-bold text-teal-700">{readerImported.length}</p>
                          <p className="text-[10px] text-teal-600 uppercase font-semibold">Books from imports</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-center">
                          <p className="text-2xl font-bold text-slate-700">
                            {bookDbList.reduce((acc, b) => acc + (b.submissionCount || 0), 0)}
                          </p>
                          <p className="text-[10px] text-slate-500 uppercase font-semibold">Total tag submissions</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-center">
                          <p className="text-2xl font-bold text-slate-700">
                            {bookDbList.filter(b => {
                              const rt = b.resolvedTags || {};
                              return Object.values(rt).some(sec =>
                                Object.values(sec).some(t => t.source === 'community')
                              );
                            }).length}
                          </p>
                          <p className="text-[10px] text-slate-500 uppercase font-semibold">Community verified</p>
                        </div>
                      </div>

                      {readerImported.length > 0 ? (
                        <div>
                          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Recent Imports</p>
                          <div className="space-y-1">
                            {readerImported.slice(0, 20).map(book => (
                              <div key={book.id} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white p-2 text-sm">
                                {book.cover && <img src={book.cover} alt="" className="h-10 w-7 rounded object-cover flex-shrink-0" />}
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-slate-800 truncate text-xs">{book.title}</p>
                                  <p className="text-[10px] text-slate-500">{book.author}</p>
                                </div>
                                <Badge variant="outline" className="text-[9px]">{book.submissionCount || 0} submissions</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-slate-200 p-6 text-center">
                          <p className="text-sm text-slate-400">No imports yet. Use the form above or readers can import from the Reader Portal.</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* CSV Import Preview */}
            {csvImportData && !csvImportResult && (
              <div className="mb-6 rounded-2xl border-2 border-teal-200 bg-teal-50/50 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-2">CSV Import Preview</h3>
                <p className="text-sm text-slate-600 mb-3">{csvImportData.length} rows found. Columns: {Object.keys(csvImportData[0] || {}).join(', ')}</p>
                <div className="max-h-48 overflow-auto rounded-xl border border-slate-200 bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        {Object.keys(csvImportData[0] || {}).map(col => (
                          <th key={col} className="px-3 py-2 text-left font-semibold text-slate-600">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvImportData.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="px-3 py-1.5 text-slate-700 max-w-[200px] truncate">{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Expected columns: title, author, steam_level, genre, relationship, tropes, content_warnings, time_period, story_structure, pov, length (pipe-separated values)
                </p>
                <div className="mt-4 flex gap-3">
                  <Button onClick={commitCsvImport} disabled={csvImporting}>
                    {csvImporting ? 'Importing...' : `Import ${csvImportData.length} Books`}
                  </Button>
                  <Button variant="outline" onClick={() => { setCsvImportData(null); setCsvImportResult(null); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {csvImportResult && (
              <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4">
                <p className="text-sm font-semibold text-green-800">
                  Import complete: {csvImportResult.imported} added, {csvImportResult.updated} updated, {csvImportResult.errors} errors
                </p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => { setCsvImportData(null); setCsvImportResult(null); }}>
                  Dismiss
                </Button>
              </div>
            )}

            {/* SEARCH & ADD VIEW */}
            {bookDbView === 'search' && (
              <div>
                <div className="mb-4 flex gap-2">
                  <Input placeholder="Search Google Books (title, author, ISBN)..."
                    value={bookDbSearch} onChange={e => setBookDbSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && searchGoogleBooks(bookDbSearch)}
                    className="flex-1" />
                  <Button onClick={() => searchGoogleBooks(bookDbSearch)} disabled={bookDbSearching}>
                    {bookDbSearching ? 'Searching...' : 'Search'}
                  </Button>
                </div>

                {bookDbSearchResults.length > 0 && (
                  <div className="space-y-3">
                    {bookDbSearchResults.map(book => {
                      const alreadyInDb = bookDbList.some(b => b.googleBooksId === book.googleBooksId);
                      return (
                        <div key={book.googleBooksId} className="flex gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          {book.cover && <img src={book.cover} alt="" className="h-24 w-16 rounded-lg object-cover flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-slate-900 truncate">{book.title}</h4>
                            <p className="text-sm text-slate-600">{book.author}</p>
                            <p className="text-xs text-slate-400 mt-1">{book.publishedDate} {book.pageCount && `· ${book.pageCount} pages`} {book.isbn && `· ISBN: ${book.isbn}`}</p>
                            {book.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{book.description}</p>}
                          </div>
                          <div className="flex-shrink-0">
                            {alreadyInDb ? (
                              <Badge variant="secondary">Already in DB</Badge>
                            ) : (
                              <Button size="sm" onClick={() => addBookToDb(book)}>
                                <Plus className="h-4 w-4 mr-1" /> Add
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* DATABASE LIST VIEW */}
            {bookDbView === 'list' && (
              <div>
                {/* Filter tabs + search */}
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                    <button onClick={() => setBookDbListFilter('all')}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                        bookDbListFilter === 'all' ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}>
                      All ({bookDbList.length})
                    </button>
                    <button onClick={() => setBookDbListFilter('needsTagging')}
                      className={`px-3 py-1.5 text-xs font-medium border-l border-slate-200 transition-colors ${
                        bookDbListFilter === 'needsTagging' ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}>
                      Needs Tagging ({bookDbList.filter(b => b.needsTagging).length})
                    </button>
                  </div>
                  <Input placeholder="Filter books..." value={bookDbFilter}
                    onChange={e => setBookDbFilter(e.target.value)} className="max-w-sm" />
                </div>
                <div className="space-y-2">
                  {bookDbList
                    .filter(b => {
                      if (bookDbListFilter === 'needsTagging' && !b.needsTagging) return false;
                      if (bookDbFilter && !b.title?.toLowerCase().includes(bookDbFilter.toLowerCase()) && !b.author?.toLowerCase().includes(bookDbFilter.toLowerCase())) return false;
                      return true;
                    })
                    .map(book => {
                      const tagCount = BOOK_TAXONOMY.reduce((acc, sec) => {
                        const secData = book.resolvedTags?.[sec.key] || {};
                        return acc + Object.keys(secData).filter(k => secData[k]?.value === 'present').length;
                      }, 0);

                      // Check which high-priority fields are filled
                      const hasSteam = Object.keys(book.resolvedTags?.steamLevel || {}).some(k => book.resolvedTags.steamLevel[k]?.value === 'present');
                      const hasGenre = Object.keys(book.resolvedTags?.genres || {}).some(k => book.resolvedTags.genres[k]?.value === 'present');
                      const tropeCount = Object.keys(book.resolvedTags?.tropes || {}).filter(k => book.resolvedTags.tropes[k]?.value === 'present').length;
                      const hasRelationship = Object.keys(book.resolvedTags?.relationship || {}).some(k => book.resolvedTags.relationship[k]?.value === 'present');
                      const autoTagCount = Object.values(book.autoTags || {}).reduce((acc, sec) => acc + Object.keys(sec).length, 0);

                      return (
                        <div key={book.id} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer"
                          onClick={() => openAdminTagEditor(book.id)}>
                          {book.cover && <img src={book.cover} alt="" className="h-16 w-11 rounded-lg object-cover flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-900 truncate">{book.title}</h4>
                            <p className="text-sm text-slate-500">{book.author}</p>
                            {book.needsTagging && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {autoTagCount > 0 && <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-600">{autoTagCount} auto-detected</Badge>}
                                {!hasSteam && <span className="text-[9px] text-red-400">steam</span>}
                                {!hasGenre && <span className="text-[9px] text-red-400">genre</span>}
                                {tropeCount < 3 && <span className="text-[9px] text-red-400">tropes ({tropeCount}/3)</span>}
                                {!hasRelationship && <span className="text-[9px] text-red-400">relationship</span>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-right">
                              <p className="text-sm font-medium text-slate-700">{tagCount} tags</p>
                              <p className="text-xs text-slate-400">{book.submissionCount || 0} submissions</p>
                              {book.needsTagging && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">Needs tagging</Badge>}
                            </div>
                            <ChevronDown size={16} className="text-slate-400" />
                          </div>
                        </div>
                      );
                    })}
                  {bookDbList.length === 0 && (
                    <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center">
                      <Tag className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No books in database yet. Use Search & Add or Bulk Import to get started.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* EDITOR VIEW */}
            {bookDbView === 'editor' && adminTagBookData && (
              <div>
                <Button variant="outline" size="sm" className="mb-4" onClick={() => { setBookDbView('list'); setAdminTagBookId(null); }}>
                  &larr; Back to Database
                </Button>

                <div className="rounded-2xl border-2 border-teal-200 bg-white p-6 shadow-lg">
                  <div className="flex items-start gap-4 mb-6">
                    {adminTagBookData.cover && <img src={adminTagBookData.cover} alt="" className="h-24 w-16 rounded-lg object-cover flex-shrink-0" />}
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{adminTagBookData.title}</h3>
                      <p className="text-sm text-slate-600">{adminTagBookData.author}</p>
                      <p className="text-xs text-slate-400 mt-1">{adminTagBookData.publishedDate} {adminTagBookData.pageCount && `· ${adminTagBookData.pageCount} pages`}</p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="secondary">{adminTagSubmissions.length} reader submission(s)</Badge>
                        {adminTagBookData.googleBooksLink && (
                          <a href={adminTagBookData.googleBooksLink} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 hover:underline flex items-center gap-1">
                            <ExternalLink size={12} /> Google Books
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Current Resolved Tags */}
                  {(() => {
                    const resolved = resolveBookTags(adminTagForm, adminTagSubmissions);
                    const presentTags = [];
                    for (const section of BOOK_TAXONOMY) {
                      const secData = resolved[section.key] || {};
                      for (const [item, info] of Object.entries(secData)) {
                        if (info.value === 'present') presentTags.push({ ...info, item, sectionKey: section.key, section: section.title });
                      }
                    }
                    return presentTags.length > 0 ? (
                      <div className="mb-6 rounded-xl border border-slate-100 bg-slate-50 p-4">
                        <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Current Resolved Tags</p>
                        <div className="flex flex-wrap gap-1.5">
                          {presentTags.map(t => (
                            <span key={`${t.section}-${t.item}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                              t.source === 'admin' ? 'bg-slate-800 text-white' :
                              t.source === 'community' ? 'bg-green-100 text-green-700' :
                              'bg-slate-100 text-slate-500'
                            }`}>
                              {t.source === 'admin' && <span className="text-[9px]">🔒</span>}
                              {t.source === 'community' && <span>✓</span>}
                              {t.source === 'unverified' && <span>~</span>}
                              {t.item}
                              {t.source === 'admin' && (
                                <button onClick={() => handleClearAdminOverride(t.sectionKey, t.item)}
                                  className="ml-1 text-[9px] opacity-60 hover:opacity-100" title="Clear admin override">✕</button>
                              )}
                            </span>
                          ))}
                        </div>
                        <p className="mt-2 text-[10px] text-slate-400">
                          🔒 Admin verified &nbsp; ✓ Community verified (3+ agree) &nbsp; ~ Unverified ({'<'}3 readers)
                        </p>
                      </div>
                    ) : (
                      <div className="mb-6 rounded-xl border border-dashed border-slate-200 p-4 text-center text-sm text-slate-400">No tags yet — set admin overrides below</div>
                    );
                  })()}

                  {/* Reader Submissions */}
                  {adminTagSubmissions.length > 0 && (
                    <div className="mb-6 rounded-xl border border-slate-100 bg-slate-50 p-4">
                      <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">Reader Submissions</p>
                      <div className="space-y-1 text-xs">
                        {adminTagSubmissions.map(sub => {
                          const tagCount = BOOK_TAXONOMY.reduce((acc, sec) => {
                            const secData = sub[sec.key] || {};
                            return acc + Object.keys(secData).length;
                          }, 0);
                          return (
                            <div key={sub.id} className="flex items-center justify-between rounded px-2 py-1 hover:bg-slate-100">
                              <span className="text-slate-700">{sub.readerName || sub.id}</span>
                              <span className="text-slate-400">{tagCount} tags &middot; {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : 'unknown'}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Import from Screenshot */}
                  <div className="mb-6">
                    <input type="file" accept="image/*" ref={screenshotFileRef} onChange={handleScreenshotImport} className="hidden" />
                    <Button variant="outline" size="sm" onClick={() => screenshotFileRef.current?.click()} disabled={screenshotImporting}>
                      {screenshotImporting ? (
                        <><span className="animate-spin inline-block h-3.5 w-3.5 border-2 border-teal-600 border-t-transparent rounded-full mr-2" /> Extracting tags...</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-1" /> Import from Screenshot</>
                      )}
                    </Button>
                    <p className="text-[10px] text-slate-400 mt-1">Upload a screenshot from Romance.io, StoryGraph, etc. — AI will extract and map tags.</p>

                    {/* Screenshot preview */}
                    {screenshotPreview && (
                      <div className="mt-3 rounded-xl border-2 border-teal-200 bg-teal-50/50 p-4">
                        <div className="flex items-start gap-4 mb-3">
                          {screenshotPreview.imageSrc && (
                            <img src={screenshotPreview.imageSrc} alt="Screenshot" className="h-24 rounded-lg border border-slate-200 object-contain flex-shrink-0" />
                          )}
                          <div>
                            <p className="text-sm font-bold text-slate-800">Detected {screenshotPreview.totalCount} tags from screenshot</p>
                            <p className="text-xs text-slate-500 mt-0.5">Review below, then apply to pre-populate the tag editor.</p>
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          {BOOK_TAXONOMY.filter(sec => screenshotPreview.detectedTags[sec.key]).map(section => {
                            const items = screenshotPreview.detectedTags[section.key];
                            return (
                              <div key={section.key}>
                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{section.title}</p>
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(items).map(([item, val]) => (
                                    val === 'present' && (
                                      <span key={item} className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-medium">
                                        ~ {item}
                                        <button onClick={() => {
                                          setScreenshotPreview(prev => {
                                            const updated = { ...prev, detectedTags: { ...prev.detectedTags } };
                                            const sec = { ...updated.detectedTags[section.key] };
                                            delete sec[item];
                                            if (Object.keys(sec).length === 0) delete updated.detectedTags[section.key];
                                            else updated.detectedTags[section.key] = sec;
                                            updated.totalCount = prev.totalCount - 1;
                                            return updated;
                                          });
                                        }} className="ml-1 text-amber-500 hover:text-red-600">×</button>
                                      </span>
                                    )
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {Object.keys(screenshotPreview.detectedTags).length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-2">No matching tags detected. Try a clearer screenshot.</p>
                        ) : (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={applyScreenshotTags}>
                              Apply {screenshotPreview.totalCount} Tags
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setScreenshotPreview(null)}>
                              Discard
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Admin Override Form */}
                  <div className="space-y-2">
                    <p className="text-sm font-bold text-slate-800">Admin Override Tags</p>
                    <p className="text-xs text-slate-500">Set admin-verified values. These override all reader submissions for that field.</p>

                    {BOOK_TAXONOMY.map(section => {
                      const isOpen = adminTagSections[section.key];
                      const sectionData = adminTagForm[section.key] || {};
                      const count = Object.keys(sectionData).length;
                      return (
                        <div key={section.key} className="rounded-xl border border-slate-200 overflow-hidden">
                          <button onClick={() => setAdminTagSections(p => ({ ...p, [section.key]: !p[section.key] }))}
                            className="flex w-full items-center justify-between px-4 py-2 text-left hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-800">{section.title}</span>
                              {count > 0 && <span className="rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">{count} set</span>}
                            </div>
                            {isOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                          </button>
                          {isOpen && (
                            <div className="border-t border-slate-100 px-4 py-3 space-y-3">
                              {section.groups.map(group => (
                                <div key={group.label}>
                                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400">{group.label}</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {group.items.map(item => {
                                      const val = sectionData[item];
                                      return (
                                        <button key={item} onClick={() => {
                                          setAdminTagForm(p => {
                                            const sec = { ...(p[section.key] || {}) };
                                            const next = cycleTagToggle(sec[item]);
                                            if (next) sec[item] = next; else delete sec[item];
                                            return { ...p, [section.key]: sec };
                                          });
                                        }}
                                          className={`flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium transition-all ${
                                            val === 'present' ? 'border-green-400 bg-green-50 text-green-700'
                                          : val === 'absent' ? 'border-red-300 bg-red-50 text-red-600'
                                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                          {val === 'present' && <CheckCircle size={12} />}
                                          {val === 'absent' && <XCircle size={12} />}
                                          {item}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {adminTagMsg && (
                      <div className={`rounded-xl border p-3 text-center text-sm font-medium ${
                        adminTagMsg.includes('saved') ? 'border-green-200 bg-green-50 text-green-700' : 'border-rose-200 bg-rose-50 text-rose-700'
                      }`}>{adminTagMsg}</div>
                    )}
                    <Button onClick={handleSaveAdminTags} disabled={adminTagSaving} className="w-full">
                      {adminTagSaving ? 'Saving...' : 'Save Admin Tags'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ----------------------------------------
             REWARDS TAB
        ---------------------------------------- */}
        {activeTab === "rewards" && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Rewards Management</h2>
              <button
                onClick={() => setShowRewardForm(true)}
                className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-teal-700"
              >
                <Plus size={16} />
                Add Reward
              </button>
            </div>

            {/* Rewards Grid */}
            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {rewards.length === 0 ? (
                <div className="col-span-full rounded-2xl border border-slate-100 bg-white p-6 text-center text-sm text-slate-500">
                  No rewards yet. Add your first reward!
                </div>
              ) : (
                rewards.map((reward) => (
                  <div
                    key={reward.id}
                    className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
                  >
                    <div className="mb-3 flex items-start justify-between">
                      <div>
                        <h3 className="text-base font-bold text-slate-900">{reward.name}</h3>
                        <p className="text-xs text-slate-600">{reward.description}</p>
                      </div>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          reward.available
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {reward.available ? 'Available' : 'Sold Out'}
                      </span>
                    </div>

                    <div className="mb-3">
                      <p className="text-lg font-bold text-teal-600">{reward.pointCost} points</p>
                      {reward.stock === 'limited' && (
                        <p className="text-xs text-slate-500">Stock: {reward.quantity}</p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditReward(reward)}
                        className="rounded-full border border-slate-200 bg-white p-1.5 text-slate-600 hover:border-teal-400"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteReward(reward.id)}
                        className="rounded-full border border-rose-200 bg-rose-50 p-1.5 text-rose-600 hover:bg-rose-100"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Manual Reward Assignment */}
<div className="mb-6 rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-6">
  <h3 className="mb-4 text-xl font-bold text-purple-900">
    🎁 Manually Assign Reward
  </h3>
  <p className="mb-4 text-sm text-purple-700">
    Give a reward to a reader without requiring points redemption
  </p>

  <div className="grid gap-4 md:grid-cols-2">
    {/* Select Reader */}
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        Select Reader
      </label>
      <select
        value={rewardModalReader?.id || ''}
        onChange={(e) => {
          const reader = readers.find(r => r.id === e.target.value);
          setRewardModalReader(reader);
        }}
        className="w-full rounded-lg border-2 border-purple-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
      >
        <option value="">Choose a reader...</option>
        {readers.map(reader => (
          <option key={reader.id} value={reader.id}>
            {reader.name} - {reader.emailCorrespondence}
          </option>
        ))}
      </select>
    </div>

    {/* Select Reward */}
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        Select Reward
      </label>
      <select
        value={selectedRewardId}
        onChange={(e) => setSelectedRewardId(e.target.value)}
        className="w-full rounded-lg border-2 border-purple-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
      >
        <option value="">Choose a reward...</option>
        {rewards.filter(r => r.available).map(reward => (
          <option key={reward.id} value={reward.id}>
            {reward.name} ({reward.pointCost} pts normally)
          </option>
        ))}
      </select>
    </div>
  </div>

  {/* Selected Reader Info */}
  {rewardModalReader && (
    <div className="mt-4 rounded-lg bg-white p-4 border border-purple-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-slate-900">{rewardModalReader.name}</p>
          <p className="text-sm text-slate-600">{rewardModalReader.emailCorrespondence}</p>
          <p className="text-sm font-medium text-purple-600">
            Current Points: {rewardModalReader.totalPoints || 0}
          </p>
        </div>
        {selectedRewardId && (
          <div className="text-right">
            <p className="text-xs text-slate-500">Reward Value</p>
            <p className="text-2xl font-bold text-purple-600">
              {rewards.find(r => r.id === selectedRewardId)?.pointCost || 0} pts
            </p>
            <p className="text-xs font-semibold text-green-600">FREE (Manual)</p>
          </div>
        )}
      </div>
    </div>
  )}

  {/* Give Reward Button */}
  <div className="mt-4">
    <button
      onClick={() => {
        if (rewardModalReader && selectedRewardId) {
          handleGiveReward(rewardModalReader.id, selectedRewardId);
          setRewardModalReader(null);
        }
      }}
      disabled={!rewardModalReader || !selectedRewardId}
      className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-3 text-base font-bold text-white hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
    >
      {!rewardModalReader ? '← Select a reader first' :
       !selectedRewardId ? '← Select a reward' :
       `🎁 Give "${rewards.find(r => r.id === selectedRewardId)?.name}" to ${rewardModalReader.name}`}
    </button>
    {rewardModalReader && selectedRewardId && (
      <p className="mt-2 text-center text-sm text-purple-700">
        ✨ This reward will be given FREE (no points deducted)
      </p>
    )}
  </div>
</div>

            {/* Redemption Requests */}
            <div>
              <h3 className="mb-3 text-xl font-bold">Redemption Requests</h3>
              {redemptions.filter(r => r.status === 'pending').length === 0 ? (
                <div className="rounded-2xl border border-slate-100 bg-white p-6 text-center text-sm text-slate-500">
                  No pending redemptions
                </div>
              ) : (
                <div className="space-y-3">
                  {redemptions
                    .filter(r => r.status === 'pending')
                    .map((redemption) => {
                      const reader = readers.find(r => 
  r.id === redemption.readerId || 
  r.emailCorrespondence === redemption.readerEmail ||
  r.authUid === redemption.readerAuthUid
);
                      const reward = rewards.find(r => r.id === redemption.rewardId);
                      return (
                        <div
                          key={redemption.id}
                          className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-slate-900">
                                {reader?.name || 'Unknown Reader'}
                              </p>
                              <p className="text-sm text-slate-600">
                                Reward: {reward?.name || 'Unknown Reward'}
                              </p>
                              <p className="text-xs text-slate-500">
                                Points used: {redemption.pointsSpent}
                              </p>
                            </div>
                            <button
                              onClick={() => handleFulfillRedemption(redemption.id)}
                              className="rounded-lg bg-green-600 px-4 py-2 text-xs font-semibold text-white hover:bg-green-700"
                            >
                              Mark Fulfilled
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Reward Form Modal */}
            {showRewardForm && (
              <div className="fixed inset-0 z-40 flex items-center justify-end bg-slate-900/30 backdrop-blur-sm">
                <div className="h-full w-full max-w-md overflow-y-auto border-l border-slate-100 bg-white px-4 py-4 shadow-2xl md:px-6">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-900">
                      {editingReward ? 'Edit Reward' : 'Add New Reward'}
                    </h2>
                    <button
                      onClick={resetRewardForm}
                      className="rounded-full border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600 hover:border-teal-400"
                    >
                      Close
                    </button>
                  </div>

                  <form onSubmit={handleRewardSubmit} className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-600">
                        Reward Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={rewardFormData.name}
                        onChange={(e) =>
                          setRewardFormData({ ...rewardFormData, name: e.target.value })
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-600">
                        Description
                      </label>
                      <textarea
                        rows={3}
                        value={rewardFormData.description}
                        onChange={(e) =>
                          setRewardFormData({ ...rewardFormData, description: e.target.value })
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-600">
                        Point Cost *
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={rewardFormData.pointCost}
                        onChange={(e) =>
                          setRewardFormData({ ...rewardFormData, pointCost: Number(e.target.value) })
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-medium text-slate-600">
                        Stock Type
                      </label>
                      <select
                        value={rewardFormData.stock}
                        onChange={(e) =>
                          setRewardFormData({ ...rewardFormData, stock: e.target.value })
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                      >
                        <option value="unlimited">Unlimited</option>
                        <option value="limited">Limited Quantity</option>
                      </select>
                    </div>

                    {rewardFormData.stock === 'limited' && (
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-600">
                          Quantity Available
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={rewardFormData.quantity}
                          onChange={(e) =>
                            setRewardFormData({ ...rewardFormData, quantity: Number(e.target.value) })
                          }
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="available"
                        checked={rewardFormData.available}
                        onChange={(e) =>
                          setRewardFormData({ ...rewardFormData, available: e.target.checked })
                        }
                        className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-400"
                      />
                      <label htmlFor="available" className="text-xs text-slate-600">
                        Available for Redemption
                      </label>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        type="submit"
                        className="rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white hover:bg-teal-700"
                      >
                        {editingReward ? 'Update Reward' : 'Add Reward'}
                      </button>
                      <button
                        type="button"
                        onClick={resetRewardForm}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 hover:border-teal-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </>
        )}

        {/* ----------------------------------------
     ANNOUNCEMENTS TAB
---------------------------------------- */}
{activeTab === "announcements" && (
  <div>
    <h2 className="mb-5 text-xl font-bold text-slate-900">📣 Announcements Dashboard</h2>
    
    {/* Create New Announcement Form */}
    <div className="mb-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-slate-900">
        ✨ Create New Announcement
      </h3>
      
      <form onSubmit={handleCreateAnnouncement} className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Announcement Title *
          </label>
          <input
            type="text"
            value={announcementForm.title}
            onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
            placeholder="e.g., ARC Copies Ready for Review!"
            required
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none shadow-sm focus:border-slate-500 focus:ring-2 focus:ring-teal-300"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Message *
          </label>
          <textarea
            value={announcementForm.message}
            onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
            placeholder="Write your announcement message here..."
            required
            rows={6}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none shadow-sm focus:border-slate-500 focus:ring-2 focus:ring-teal-300"
          />
        </div>

        {/* Related Book */}
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">
            Related Book (Optional)
          </label>
          <select
            value={announcementForm.bookId}
            onChange={(e) => setAnnouncementForm({...announcementForm, bookId: e.target.value})}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none shadow-sm focus:border-slate-500 focus:ring-2 focus:ring-teal-300"
          >
            <option value="">-- Select a book --</option>
            {books.map(book => (
              <option key={book.id} value={book.id}>{book.title}</option>
            ))}
          </select>
        </div>

        {/* Date Fields Grid */}
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              📅 ARC Send Date
            </label>
            <input
              type="date"
              value={announcementForm.arcSendDate}
              onChange={(e) => setAnnouncementForm({...announcementForm, arcSendDate: e.target.value})}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none shadow-sm focus:border-slate-500 focus:ring-2 focus:ring-teal-300"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              ⭐ Review Deadline
            </label>
            <input
              type="date"
              value={announcementForm.reviewDeadline}
              onChange={(e) => setAnnouncementForm({...announcementForm, reviewDeadline: e.target.value})}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none shadow-sm focus:border-slate-500 focus:ring-2 focus:ring-teal-300"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              🚀 Release Date
            </label>
            <input
              type="date"
              value={announcementForm.releaseDate}
              onChange={(e) => setAnnouncementForm({...announcementForm, releaseDate: e.target.value})}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 outline-none shadow-sm focus:border-slate-500 focus:ring-2 focus:ring-teal-300"
            />
          </div>
        </div>

        <button
          type="submit"
          className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-teal-700"
        >
          <Mail className="h-4 w-4" />
          Post Announcement
        </button>
      </form>
    </div>

    {/* Announcements List */}
    <div>
      <h3 className="mb-3 text-base font-semibold text-slate-900">
        📋 All Announcements ({announcements.length})
      </h3>
      
      {announcements.length === 0 ? (
        <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center">
          <div className="mb-3 text-4xl">📣</div>
          <p className="text-sm text-slate-500">No announcements yet. Create your first one above!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map(announcement => (
            <div key={announcement.id} className="rounded-2xl border-l-4 border-slate-500 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="mb-1 text-base font-semibold text-slate-900">
                    {announcement.title}
                  </h4>
                  <div className="mb-2 text-xs text-slate-500">
                    Posted {new Date(announcement.createdAt).toLocaleString()}
                  </div>
                </div>
                
                <button
                  onClick={() => handleDeleteAnnouncement(announcement.id)}
                  className="flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              </div>

              <p className="mb-3 whitespace-pre-wrap text-sm text-slate-700">
                {announcement.message}
              </p>

              {/* Key Dates */}
              {(announcement.arcSendDate || announcement.releaseDate || announcement.reviewDeadline) && (
                <div className="mb-3 flex flex-wrap gap-3 rounded-lg bg-slate-50 p-3">
                  {announcement.arcSendDate && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500">📅 ARC SEND</div>
                      <div className="text-sm font-medium text-slate-900">
                        {new Date(announcement.arcSendDate).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                  {announcement.reviewDeadline && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500">⭐ REVIEW DEADLINE</div>
                      <div className="text-sm font-medium text-slate-900">
                        {new Date(announcement.reviewDeadline).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                  {announcement.releaseDate && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500">🚀 RELEASE DATE</div>
                      <div className="text-sm font-medium text-slate-900">
                        {new Date(announcement.releaseDate).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {announcement.bookTitle && (
                <div className="flex items-center gap-2 text-xs italic text-slate-600">
                  <BookOpen className="h-3 w-3" />
                  <span>Related to: <strong>{announcement.bookTitle}</strong></span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
)}

{/* ----------------------------------------
    COMMUNITY FEED TAB
    ---------------------------------------- */}
{activeTab === "community" && (
  <div>
    <div className="mb-6 flex items-center justify-between">
      <h2 className="text-xl font-bold text-slate-900">💬 Community Feed</h2>
      <button
        onClick={() => setShowCreateAnnouncement(true)}
        className="rounded-lg bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:from-teal-600 hover:to-teal-700"
      >
        <Plus className="inline h-4 w-4 mr-2" />
        Create Announcement Post
      </button>
    </div>

    {/* Stats Cards */}
    <div className="mb-6 grid gap-4 md:grid-cols-3">
      <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-slate-100 p-3">
            <MessageCircle className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <p className="text-xs text-slate-600">Total Posts</p>
            <p className="text-2xl font-bold text-slate-900">{posts.length}</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-red-100 p-3">
            <Heart className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-slate-600">Total Likes</p>
            <p className="text-2xl font-bold text-slate-900">
              {Object.values(postLikes).reduce((sum, item) => sum + item.count, 0)}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-blue-100 p-3">
            <MessageCircle className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-slate-600">Total Comments</p>
            <p className="text-2xl font-bold text-slate-900">
              {Object.values(postComments).reduce((sum, arr) => sum + arr.length, 0)}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Posts Feed */}
    {posts.length === 0 ? (
      <div className="rounded-2xl border border-slate-100 bg-white p-12 text-center">
        <MessageCircle className="mx-auto mb-3 h-12 w-12 text-slate-300" />
        <p className="text-slate-500">No posts yet. Create an announcement to get started!</p>
      </div>
    ) : (
      <div className="space-y-4">
        {posts.map((post) => {
          const isAnnouncement = post.type === 'announcement';
          const likes = postLikes[post.id] || { count: 0 };
          const comments = postComments[post.id] || [];

          return (
            <div key={post.id} className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
              {/* Post Header */}
              <div className="p-4 flex items-center justify-between border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-sm">
                    {post.authorName?.[0]?.toUpperCase() || 'A'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{post.authorName}</span>
                      {isAnnouncement && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-2 py-0.5 text-xs font-semibold text-white">
                          ✨ Author
                        </span>
                      )}
                      {post.isPinned && (
                        <Pin className="h-4 w-4 text-teal-600" fill="currentColor" />
                      )}
                    </div>
                    <span className="text-xs text-slate-500">{formatTimeAgo(post.createdAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTogglePin(post.id, post.isPinned)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      post.isPinned
                        ? 'bg-slate-100 text-teal-700 hover:bg-slate-200'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {post.isPinned ? '📌 Unpin' : 'Pin'}
                  </button>
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="text-slate-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Post Content */}
              <div className="p-4">
                <p className="text-slate-800 whitespace-pre-wrap">{post.content}</p>
              </div>

              {/* Post Stats */}
              <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4 text-red-500" />
                  <span>{likes.count} {likes.count === 1 ? 'like' : 'likes'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-blue-500" />
                  <span>{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
                </div>
              </div>

              {/* Comments Section + Author Reply */}
              <div className="border-t border-slate-100 bg-slate-50/50 p-4">
                {comments.length > 0 && (
                  <>
                    <h4 className="mb-3 text-xs font-semibold text-slate-600 uppercase">Comments</h4>
                    <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="flex gap-3">
                          <div className={`h-7 w-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${comment.isAuthor ? 'bg-gradient-to-br from-slate-500 to-teal-600' : 'bg-gradient-to-br from-slate-300 to-slate-400'}`}>
                            {comment.isAuthor ? 'J' : (comment.readerName?.[0]?.toUpperCase() || 'R')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="font-semibold text-xs text-slate-900 flex items-center gap-1.5">
                                  {comment.readerName}
                                  {comment.isAuthor && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold text-slate-800">AUTHOR</span>}
                                </span>
                                <button onClick={() => handleDeleteComment(comment.id)} className="text-slate-400 hover:text-red-600 transition-colors">
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                              <p className="text-sm text-slate-700">{comment.content}</p>
                            </div>
                            <span className="text-xs text-slate-400 mt-1 ml-3">{formatTimeAgo(comment.createdAt)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {/* Author reply input */}
                <div className="flex gap-2">
                  <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-slate-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold">J</div>
                  <input
                    type="text"
                    placeholder="Reply as Julie McNairn…"
                    value={authorCommentInput[post.id] || ''}
                    onChange={e => setAuthorCommentInput({ ...authorCommentInput, [post.id]: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter' && authorCommentInput[post.id]?.trim()) handleAuthorCommentReply(post.id); }}
                    className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                  <button
                    onClick={() => handleAuthorCommentReply(post.id)}
                    disabled={!authorCommentInput[post.id]?.trim()}
                    className="rounded-lg bg-slate-500 px-3 py-2 text-white hover:bg-teal-600 disabled:opacity-40 transition-colors"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    )}

    {/* Create Announcement Post Modal */}
    {showCreateAnnouncement && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
        <div className="w-full max-w-lg rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
          <h3 className="mb-4 text-xl font-bold text-slate-900">✨ Create Announcement Post</h3>
          
          <form onSubmit={handleCreateAnnouncementPost} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-600">
                What would you like to announce?
              </label>
              <textarea
                rows={8}
                required
                value={announcementPostForm.content}
                onChange={(e) => setAnnouncementPostForm({ ...announcementPostForm, content: e.target.value })}
                placeholder="Share updates, book news, ARC availability, or anything you want your community to know! ✨"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              />
              <p className="mt-2 text-xs text-slate-500">
                💡 This will appear with your "Author" badge and can be pinned to the top of the feed
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={!announcementPostForm.content.trim()}
                className="flex-1 rounded-lg bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-2.5 font-semibold text-white hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Post Announcement
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateAnnouncement(false);
                  setAnnouncementPostForm({ content: '' });
                }}
                className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2.5 font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
  </div>
)}

{/* ----------------------------------------
    MESSAGES TAB (Platform support messages from readers)
    ---------------------------------------- */}
{activeTab === "messages" && (
  <MessagesTab onUnreadCount={handleDmUnreadCount} />
)}

{/* ----------------------------------------
    MERGE DUPLICATES TAB
    ---------------------------------------- */}
{activeTab === "duplicates" && (
  <div className="px-4 py-4 md:px-6">
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Merge Duplicate Readers</h2>
        <p className="mt-1 text-sm text-slate-600">
          Find and merge readers with duplicate email addresses to keep your data clean.
        </p>
      </div>
      <DuplicateMerger />
    </div>
  </div>
)}

{/* ----------------------------------------
    ORDERS TAB
    ---------------------------------------- */}
{activeTab === "orders" && (
  <div className="px-4 py-4 md:px-6">
    <div className="mx-auto max-w-7xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Orders Management</h2>
        <p className="mt-1 text-sm text-slate-600">
          Track and manage signed book orders from your readers
        </p>
      </div>
      <OrdersManagement />
    </div>
  </div>
)}

{/* ----------------------------------------
    EMAIL LOG TAB
    ---------------------------------------- */}
{activeTab === "email-log" && (
  <div className="px-4 py-4 md:px-6">
    <div className="mx-auto max-w-6xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Email Log</h2>
        <p className="text-sm text-slate-500">ARC emails sent via the platform — {emailLog.length} records</p>
      </div>

      {emailLogLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
        </div>
      ) : emailLog.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-12 text-center">
          <Mail className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">No email records found.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Recipient</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Subject</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700 hidden md:table-cell">Book</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700 hidden lg:table-cell">Attachment</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {emailLog.map(e => {
                const ts = e.sentAt?.toDate?.() || e.createdAt?.toDate?.() || null;
                return (
                  <tr key={e.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 truncate max-w-[200px]">{e.recipientName || '—'}</p>
                      <p className="text-xs text-slate-400 truncate max-w-[200px]">{e.recipientEmail || '—'}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-[200px] truncate">{e.subject || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell max-w-[150px] truncate">{e.bookTitle || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        e.status === 'sent' ? 'bg-green-100 text-green-700' :
                        e.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {e.status || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                      {e.hasAttachment ? (
                        <span className="text-xs text-teal-600 font-medium">{e.attachmentName || '✓ Yes'}</span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                      {ts ? ts.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      {ts && <span className="ml-1 text-slate-400">{ts.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
                      {e.status === 'failed' && e.error && (
                        <p className="mt-1 text-red-500 whitespace-normal">Error: {e.error}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <Button variant="outline" size="sm" onClick={() => { setEmailLog([]); loadEmailLog(); }}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>
    </div>
  </div>
)}

{/* ----------------------------------------
    SETTINGS TAB
    ---------------------------------------- */}
{activeTab === "settings" && (
  <div className="px-4 py-4 md:px-6">
    <div className="mx-auto max-w-6xl">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center">
        <Package className="mx-auto mb-3 h-10 w-10 text-slate-400" />
        <h3 className="text-lg font-semibold text-slate-900">Shop Settings Moved</h3>
        <p className="mt-2 text-sm text-slate-600">
          Product pricing and shop settings are now managed per-book in the Author Dashboard → Orders tab.
        </p>
      </div>
    </div>
  </div>
)}

{activeTab === "platform" && (
  <div className="px-4 py-4 md:px-6">
    <div className="mx-auto max-w-6xl">
      <BootstrapSuperadmin />
      <SuperAdminDashboard embedded={true} />
    </div>
  </div>
)}

{activeTab === "licences" && (
  <div className="px-4 py-4 md:px-6">
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Roadmap Licences</h2>
        <p className="text-[11px] text-slate-500">
          Trial &amp; paid licences for the Publishing Roadmap, Plot Planner and bundle. Read-only.
        </p>
      </div>
      <RoadmapLicencesPanel licences={licences} />
    </div>
  </div>
)}

{activeTab === "testing" && (
  <div className="px-4 py-4 md:px-6">
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Testing</h2>
        <p className="text-[11px] text-slate-500">
          Beta feedback from the Publishing Roadmap, and the tester roster.
        </p>
      </div>
      <RoadmapTestingTab feedback={roadmapFeedback} testers={roadmapTesters} marketingFeedback={marketingFeedback} signups={signupNotifications} genreSuggestions={genreSuggestions} />
    </div>
  </div>
)}
      </main>

    {/* REVIEW APPROVAL MODAL */}
    {showApprovalModal && approvalReview && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
          <h3 className="mb-1 text-xl font-bold text-slate-900">✅ Accept Review</h3>
          <p className="mb-5 text-sm text-slate-500">
            Choose what happens to this reader's status after you accept their review.
            The path they selected has been pre-selected for you.
          </p>

          {/* Points */}
          <div className="mb-4 rounded-xl bg-green-50 p-4">
            <label className="mb-2 block text-sm font-semibold text-green-900">💰 Points to Award</label>
            <div className="flex items-center gap-3">
              <input type="number" min="0" step="10"
                value={approvalReview.points}
                onChange={(e) => setApprovalReview(prev => ({ ...prev, points: Number(e.target.value) }))}
                className="w-28 rounded-lg border-2 border-green-300 px-3 py-2 text-center text-lg font-bold focus:border-green-500 focus:ring-2 focus:ring-green-200"
              />
              <p className="text-sm text-green-700">points for this review</p>
            </div>
          </div>

          {/* Status Selection */}
          <div className="mb-5">
            <p className="mb-3 text-sm font-semibold text-slate-700">📋 Move Reader To:</p>
            <div className="space-y-2">
              {[
                { value: 'Street Team', label: '🎉 Street Team', desc: 'They want to help create social buzz & launch energy', color: 'border-teal-400 bg-slate-50' },
                { value: 'Second ARC', label: '📚 Second ARC', desc: 'They want priority access to future ARCs', color: 'border-indigo-400 bg-indigo-50' },
                { value: 'Closed', label: '🔒 Closed', desc: 'No further engagement needed', color: 'border-slate-400 bg-slate-50' },
              ].map(opt => (
                <label key={opt.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 p-3 transition-all ${
                    approvalStatus === opt.value ? opt.color + ' shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}>
                  <input type="radio" name="approvalStatus" value={opt.value}
                    checked={approvalStatus === opt.value}
                    onChange={(e) => setApprovalStatus(e.target.value)}
                    className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-slate-500"
                  />
                  <div>
                    <p className="text-sm font-bold text-slate-900">{opt.label}</p>
                    <p className="text-xs text-slate-500">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={async () => {
                await handleApproveReview(approvalReview.id, approvalReview.data, approvalReview.points, approvalStatus);
                setShowApprovalModal(false);
                setApprovalReview(null);
              }}
              className="flex-1 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 py-3 text-sm font-bold text-white hover:from-green-700 hover:to-emerald-700 transition-all shadow-md">
              ✅ Confirm & Accept
            </button>
            <button onClick={() => { setShowApprovalModal(false); setApprovalReview(null); }}
              className="rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

/* ----------------------------------------
   FINALLY EXPORT THE COMPONENT
---------------------------------------- */
export default Root;

/* ----------------------------------------
   ROOT ROUTER — handles ?tool=publishing
   direct URL access. Lives outside App so
   hooks inside App are never conditional.
---------------------------------------- */
function Root() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('tool') === 'publishing') {
    return <PublishingRoadmap />;
  }
  return <App />;
}
